import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";

export type Lang = "cs" | "uk" | "sk" | "en" | "de";

export const LANGUAGES: { code: Lang; label: string; flag: string }[] = [
  { code: "cs", label: "Čeština", flag: "🇨🇿" },
  { code: "uk", label: "Українська", flag: "🇺🇦" },
  { code: "sk", label: "Slovenčina", flag: "🇸🇰" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
];

const translations: Record<Lang, Record<string, string>> = {
  cs: {
    // TopBar
    "topbar.login": "Přihlásit se",
    "topbar.register": "Zaregistruj se zdarma",
    "topbar.comingSoon": "Další připravujeme",
    "topbar.facades": "Fasádnické práce",

    // Landing
    "landing.title": "Kolik stojí vaše stavba?",
    "landing.subtitle": "Vyberte typ stavební práce a zjistěte orientační cenu za minutu.",
    "landing.service.fasady": "Fasádnické práce",
    "landing.service.fasady.desc": "Orientační cena fasády, zateplení a omítek",
    "service.fasadnicke-prace": "Fasádnické práce",
    "landing.service.strechy": "Střechy",
    "landing.service.okna": "Okna a dveře",
    "landing.startChat": "Zjistit cenu",
    "landing.comingSoon": "Připravujeme",

    // Index
    "index.headline1": "Kolik stojí vaše stavba?",
    "index.headline2": "Spočítejte si cenu stavebních prací",
    "index.headline3": "Zjistěte orientační cenu online",
    "index.headline4": "Naceňte si stavební práce",
    "index.headline5": "Orientační cena za minutu",
    "index.disclaimer": "Odesláním zprávy vyjadřuješ souhlas s našimi",
    "index.terms": "smluvními podmínkami",
    "index.privacyConfirm": "a potvrzuješ, že máš přečtené naše",
    "index.privacy": "zásady ochrany soukromí",
    "index.cookiesSee": "Viz",
    "index.cookies": "předvolby cookies",
    "cookie.message": "Tento web používá cookies pro zajištění správného fungování a analýzu návštěvnosti.",
    "cookie.accept": "Přijmout vše",
    "cookie.reject": "Odmítnout volitelné",
    "cookie.settings": "Nastavení",
    "index.sensitiveWarning": "Nesdílej citlivé osobní údaje (rodné číslo, hesla,..).",
    "index.nextQuestion": "Napište další dotaz...",
    "index.mayMakeErrors": "cenik.ai může dělat chyby. Ověřte si důležité informace.",
    "index.leadSaved": "Kontaktní údaje odeslány – brzy se ozveme! 🏠",

    // Sidebar
    "sidebar.newChat": "Nový chat",
    "sidebar.searchChats": "Vyhledat chat",
    "sidebar.projects": "Projekty",
    "sidebar.howToUse": "Jak to funguje",
    "sidebar.howToUseQuery": "Jak to funguje?",
    "sidebar.howToUseResponse": "Vítejte v **cenik.ai** 👋\n\nFunguje to jednoduše:\n\n1. **Napíšete mi, co potřebujete** – typ stavební práce, rozměry, materiál.\n2. **AI vám spočítá orientační cenu** – na základě aktuálních tržních dat.\n3. **Můžete se doptávat** – na materiály, technologie, nebo porovnat varianty.\n4. **Pokud chcete nezávaznou nabídku**, zanecháte kontakt a spojíme vás s ověřenými dodavateli.\n\n💡 **Tip:** Můžete přejít rovnou na **kalkulačku** a vyplnit si vše sám.\n\nZačněte tím, že popíšete, co potřebujete nacenit 🏠",
    "sidebar.yourChats": "Historie chatů",
    "sidebar.noChatsYet": "Zatím žádné chaty",
    "sidebar.loginForHistory": "Pro zobrazení historie chatů se přihlaste.",
    "sidebar.loginLink": "přihlaste",
    "sidebar.loginForFeature": "Pro použití této funkce se přihlaste.",
    "sidebar.loginForInquiries": "Pro zobrazení poptávek a nabídek se přihlaste.",
    "sidebar.becomeSupplier": "Chci vlastní chatbot",
    "sidebar.supplierDashboard": "Panel dodavatele",
    "sidebar.supplierCta": "Chcete dostávat zdarma poptávky do Vašeho e-mailu?",
    "sidebar.login": "Přihlásit se",

    // ChatInput
    "chat.placeholder1": "Co potřebujete nacenit?",
    "chat.placeholder2": "Jaké jsou rozměry nebo plocha?",
    "chat.placeholder3": "Jaký materiál preferujete?",
    "chat.placeholder4": "Kde se stavba nachází?",
    "chat.placeholder5": "Máte otázky ke stavebním pracím?",
    "chat.placeholderMobile1": "Co potřebujete nacenit?",
    "chat.placeholderMobile2": "Jaké jsou rozměry?",
    "chat.placeholderMobile3": "Jaký materiál?",
    "chat.placeholderMobile4": "Kde je stavba?",
    "chat.placeholderMobile5": "Otázky ke stavbě?",
    "chat.takePhoto": "Vyfotit",
    "chat.addPhotos": "Přidat fotografie",
    "chat.uploadFiles": "Nahrát soubory",
    "chat.fileTooLarge": "Soubor \"{name}\" je větší než 10 MB.",
    "chat.assignToProject": "Zařadit do projektu",
    "chat.unassign": "Odebrat z projektu",
    "chat.deleteChat": "Smazat chat",
    "chat.loginToSave": "Přihlásit se a uložit",

    // Suggestions
    "suggestion.facadePrice": "Spočítej cenu fasády",
    "suggestion.insulationCost": "Kolik stojí stavební práce?",
    "suggestion.bestPolystyrene": "Jaký materiál je nejlepší?",
    "suggestion.contactInsulation": "Zjistit orientační cenu",

    // Login
    "login.title": "Přihlas se nebo se zaregistruj",
    "login.email": "Email",
    "login.emailPlaceholder": "vas@email.cz",
    "login.password": "Heslo",
    "login.passwordPlaceholder": "••••••••",
    "login.submit": "Přihlásit se",
    "login.or": "nebo",
    "login.google": "Pokračovat přes Google",
    "login.noAccount": "Nemáte účet?",
    "login.register": "Zaregistrujte se",
    "login.forgotPassword": "Zapomněli jste heslo?",
    "login.subtitle": "Zadejte e-mail a heslo pro přihlášení.",
    "login.chatbotBoxTitle": "Máte firmu?",
    "login.chatbotBoxDesc": "Vytvořte si vlastní AI chatbot pro vaše zákazníky — zdarma.",
    "login.becomeSupplier": "Chci vlastní chatbot",
    "forgotPassword.title": "Obnovení hesla",
    "forgotPassword.description": "Zadejte svůj e-mail a my vám pošleme odkaz pro obnovení hesla.",
    "forgotPassword.submit": "Odeslat odkaz",
    "forgotPassword.sent": "Odkaz odeslán!",
    "forgotPassword.sentDesc": "Zkontrolujte svou e-mailovou schránku a klikněte na odkaz pro obnovení hesla.",
    "forgotPassword.backToLogin": "Zpět na přihlášení",
    "resetPassword.title": "Nové heslo",
    "resetPassword.password": "Nové heslo",
    "resetPassword.passwordPlaceholder": "Minimálně 8 znaků",
    "resetPassword.submit": "Nastavit heslo",
    "resetPassword.success": "Heslo bylo změněno",
    "login.info": "Přihlaste se pomocí e-mailu a hesla nebo přes Google. Po přihlášení budete mít přístup k historii chatů a uloženým projektům.",
    "register.info": "Vytvořte si bezplatný účet pro ukládání chatů, správu projektů a staveb. Stačí e-mail a heslo nebo Google účet.",
    "forgotPassword.info": "Zadejte e-mail, na který jste se registrovali. Pošleme vám odkaz pro nastavení nového hesla.",
    "resetPassword.info": "Zadejte nové heslo (minimálně 8 znaků). Po uložení budete přesměrováni na přihlášení.",
    "supplier.loginInfo": "Registrujte se jako dodavatel a dostávejte zdarma poptávky z vašeho regionu. Stačí zadat e-mail – žádné heslo, žádné poplatky.",
    "supplier.registerInfo": "Vytvořte si dodavatelský účet pro správu ceníku a přijímání poptávek. Zadejte název firmy, e-mail a heslo.",
    "auth.continue": "Pokračovat",
    "auth.invalidCredentials": "Nesprávný e-mail nebo heslo",
    "auth.alreadyRegistered": "Tento e-mail je již registrován. Přihlaste se.",
    "auth.switchToRegister": "Nemáte účet? Zaregistrujte se",
    "auth.switchToLogin": "Už máte účet? Přihlaste se",
    "auth.forgotPassword": "Zapomněli jste heslo?",
    "auth.backToLogin": "← Zpět na přihlášení",

    // Register
    "register.title": "Vytvořit účet zdarma",
    "register.subtitle": "Vytvořte si nový účet — stačí e-mail a heslo.",
    "register.email": "Email",
    "register.emailPlaceholder": "vas@email.cz",
    "register.password": "Heslo",
    "register.passwordPlaceholder": "Minimálně 8 znaků",
    "register.submit": "Zaregistrovat se",
    "register.or": "nebo",
    "register.google": "Pokračovat přes Google",
    "register.hasAccount": "Už máte účet?",
    "register.login": "Přihlaste se",

    // Dashboard
    "dashboard.pricing": "Ceník",
    "dashboard.emptyTitle": "S čím ti můžu pomoci?",
    "dashboard.emptySubtitle": "Napiš svou poptávku nebo otázku",
    "dashboard.inputPlaceholder": "Co poptáváš dnes?",

    // Pricing
    "pricing.title": "Jednoduchý ceník",
    "pricing.subtitle": "Vyberte plán, který vám vyhovuje",
    "pricing.free": "Free",
    "pricing.pro": "Pro",
    "pricing.agency": "Agency",
    "pricing.forever": "navždy",
    "pricing.perMonth": "/měsíc",
    "pricing.startFree": "Začít zdarma",
    "pricing.choosePro": "Vybrat Pro",
    "pricing.contactUs": "Kontaktovat nás",
    "pricing.feature.requests5": "5 poptávek/měsíc",
    "pricing.feature.basicAI": "Základní AI odpovědi",
    "pricing.feature.emailSupport": "Email podpora",
    "pricing.feature.unlimitedRequests": "Neomezené poptávky",
    "pricing.feature.advancedAI": "Pokročilý AI agent",
    "pricing.feature.prioritySupport": "Prioritní podpora",
    "pricing.feature.pdfExport": "Export do PDF",
    "pricing.feature.allPro": "Vše z Pro",
    "pricing.feature.teamAccounts": "Týmové účty (5 uživatelů)",
    "pricing.feature.apiAccess": "API přístup",
    "pricing.feature.customBranding": "Vlastní branding",
    "pricing.feature.dedicatedManager": "Dedikovaný manažer",
    "pricing.login": "Přihlásit se",

    // Supplier
    "supplier.headline": "Chcete zdarma dostávat poptávky, o které máte zájem?",
    "supplier.description": "Stačí zadat e-mail – začneme vám ihned posílat relevantní poptávky. Žádné heslo, žádné poplatky.",
    "supplier.chatbotHeadline": "Chci vlastní chatbot",
    "supplier.chatbotDescription": "Zaregistrujte se a vytvořte si AI chatbot, který bude odpovídat vašim zákazníkům.",
    "supplier.benefit1": "Poptávky z regionů, kde působíte",
    "supplier.benefit2": "Filtrujte podle typu materiálu a objemu",
    "supplier.benefit3": "Reagujte jen na zakázky, které vám dávají smysl",
    "supplier.emailPlaceholder": "vas@firma.cz",
    "supplier.submitEmail": "Odebírat poptávky zdarma",
    "supplier.noSpam": "Žádný spam. Odhlásit se můžete kdykoliv.",
    "supplier.consent": "Odesláním souhlasíte se zasíláním poptávek na váš e-mail a s podmínkami služby. Z odběru se můžete kdykoliv odhlásit.",
    "supplier.registerHint": "Chcete si vybrat, jaké poptávky dostávat, a nacenovat zakázky? Zaregistrujte se zdarma.",
    "supplier.preferencesTitle": "Jaké práce vás zajímají?",
    "supplier.preferencesSubtitle": "Vyberte typy zakázek, které chcete dostávat. Můžete změnit kdykoliv.",
    "supplier.savePreferences": "Dokončit registraci",
    "supplier.magicLinkSent": "E-mail zaregistrován!",
    "supplier.checkInbox": "Budeme vám zasílat relevantní poptávky od zákazníků přímo na váš e-mail.",
    "supplier.detailsTitle": "Řekněte nám o sobě",
    "supplier.detailsSubtitle": "Tyto údaje jsou nepovinné, ale pomohou nám lépe cílit poptávky.",
    "supplier.namePlaceholder": "Vaše jméno a příjmení",
    "supplier.phonePlaceholder": "Telefon, např. +420 777 123 456",
    "supplier.saveDetails": "Uložit údaje",
    "supplier.skipStep": "Přeskočit",
    "supplier.loginTitle": "Přihlášení dodavatele",
    "supplier.loginSubtitle": "Dostávejte zdarma poptávky přímo do e-mailu.",
    "supplier.noAccount": "Nemáte účet dodavatele?",
    "supplier.register": "Zaregistrujte se",
    "supplier.backToApp": "Zpět na cenik.ai",
    "supplier.registerTitle": "Registrace dodavatele",
    "supplier.registerSubtitle": "Vytvořte si účet a začněte nacenovat",
    "supplier.companyName": "Název firmy / osoby",
    "supplier.companyPlaceholder": "Vaše firma s.r.o. nebo jméno",
    "supplier.registerBtn": "Zaregistrovat se",
    "supplier.hasAccount": "Už máte účet?",
    "supplier.loginLink": "Přihlaste se",
    "supplier.passwordMin": "Heslo musí mít alespoň 8 znaků",
    "supplier.checkEmail": "Ověřte svůj e-mail pro aktivaci účtu",
    "supplier.icoPlaceholder": "IČO, např. 12345678 (nepovinné)",
    "supplier.addressPlaceholder": "Sídlo firmy, např. Praha",
    "supplier.fillCompany": "Vyplňte název firmy",
    "supplier.becomePartnerTitle": "Staňte se partnerem",
    "supplier.becomePartnerSubtitle": "Vyplňte základní údaje o firmě.",
    "supplier.continueToPreferences": "Vytvořit dodavatele",
    "supplier.registrationDone": "Registrace dokončena! Vítejte v panelu dodavatele.",
    "supplier.settingsTitle": "Nastavení",
    "supplier.tabCompany": "Firma",
    "supplier.tabChatbot": "Chatbot",
    "supplier.tabPricing": "Ceníky",
    "supplier.incompleteProfile": "Vyplňte informace o sobě v nastavení, aby váš chatbot fungoval správně.",
    "supplier.goToSettings": "Přejít do nastavení",
    "supplier.passwordPlaceholder": "Heslo (min. 8 znaků)",
    "supplier.dashboard": "Admin dodavatele",
    "supplier.logout": "Odhlásit",
    "supplier.productsTitle": "Moje produkty a ceny",
    "supplier.productsSubtitle": "Přidejte produkty a nastavte cenu za m²",
    "supplier.addProduct": "Přidat produkt",
    "supplier.editProduct": "Upravit produkt",
    "supplier.newProduct": "Nový produkt",
    "supplier.productName": "Název produktu",
    "supplier.productNamePlaceholder": "např. Zateplení EPS 100",
    "supplier.pricePerM2": "Cena za m² (Kč)",
    "supplier.productDescription": "Popis",
    "supplier.descriptionPlaceholder": "Krátký popis produktu...",
    "supplier.cancel": "Zrušit",
    "supplier.save": "Uložit",
    "supplier.noProducts": "Zatím nemáte žádné produkty",
    "supplier.addFirstProduct": "Klikněte na 'Přidat produkt' pro začátek",
    "supplier.productAdded": "Produkt přidán",
    "supplier.productUpdated": "Produkt aktualizován",
    "supplier.productDeleted": "Produkt smazán",
    "supplier.fillRequired": "Vyplňte název a cenu",
    "supplier.inquiriesTab": "Poptávky",
    "supplier.productsTab": "Produkty",
    "supplier.noInquiries": "Zatím žádné poptávky",
    "supplier.noInquiriesHint": "Nové poptávky se zobrazí automaticky",
    "supplier.alreadyPaid": "Nabídka již byla odeslána",
    "supplier.paymentError": "Chyba při platbě, zkuste to znovu",
    "supplier.loginRequired": "Pro platbu se musíte přihlásit",
    "supplier.submitQuoteTitle": "Vaše cenová nabídka",
    "supplier.submitQuoteHint": "Zadejte orientační cenu bez DPH. Po odeslání a zaplacení 15 Kč bude nabídka doručena zákazníkovi.",
    "supplier.quoteTotal": "Nabízená cena (Kč bez DPH)",
    "supplier.quoteNotes": "Poznámka",
    "supplier.quoteNotesPlaceholder": "Co je zahrnuto v ceně, termín realizace...",
    "supplier.submitQuoteBtn": "Odeslat nabídku a zaplatit 15 Kč",
    "supplier.quoteSaved": "Nabídka uložena!",
    "supplier.quoteAlreadySent": "Nabídka již odeslána",
    "supplier.fillQuotePrice": "Vyplňte nabízenou cenu",
    "supplier.quoteSent": "Odesláno",
    "supplier.quoteSentConfirm": "Nabídka byla odeslána zákazníkovi",
    "supplier.quoteSentSuccess": "Nabídka úspěšně odeslána!",
    "supplier.waitingCustomerApproval": "Čekáme na schválení zákazníkem. Jakmile projeví zájem, zobrazí se vám jeho kontaktní údaje.",
    "supplier.customerApproved": "Zákazník schválil sdílení kontaktu",
    "supplier.contactVisibleNow": "Kontaktní údaje zákazníka jsou nyní viditelné výše.",
    "supplier.share": "Sdílet",
    "supplier.linkCopied": "Odkaz zkopírován",
    "supplier.shareFailed": "Sdílení se nezdařilo",
    "supplier.aboutQuery": "Řekněte mi více o vaší firmě.",
    "supplier.aboutCompany": "O firmě",
    "supplier.slugSaved": "Slug uložen",
    "supplier.slugTakenOrInvalid": "Slug je již obsazený nebo neplatný",
    "supplier.connectionError": "Chyba připojení",
    "supplier.paymentNotVerified": "Platba nebyla ověřena. Zkuste obnovit stránku.",
    "supplier.paymentVerificationError": "Chyba při ověřování platby.",
    "supplier.savingError": "Chyba při ukládání",
    "supplier.dataSaved": "Údaje uloženy",
    "supplier.edit": "upravit",
    "supplier.onboardingTitle": "Jak tady fungují poptávky pro vaši firmu?",
    "supplier.onboardingStep1": "Nastavíte ceník a oblasti, kde chcete zakázky.",
    "supplier.onboardingStep2": "Podle toho vám budeme zobrazovat nové poptávky.",
    "supplier.onboardingStep3": "U vybraných poptávek odešlete nacenění a zákazníkovi se zobrazí detail vaší nabídky.",
    "supplier.aiChatCalculator": "Vaše AI chat kalkulačka",
    "supplier.freeTestVersion": "Zdarma · testovací verze",
    "supplier.checking": "Kontroluji...",
    "supplier.slugAvailableLabel": "✓ Volný",
    "supplier.slugTakenLabel": "✗ Obsazený",
    "supplier.savingLabel": "Ukládám...",
    "supplier.saveLabel": "Uložit",
    "supplier.cancel": "Zrušit",
    "supplier.copyLink": "Kopírovat odkaz",
    "supplier.customDomainTag": "· vlastní doména",
    "supplier.customDomainLabel": "Vlastní doména: {domain}",
    "supplier.connectCustomDomain": "Připojit vlastní doménu",
    "supplier.enterDomainLabel": "Zadejte svou doménu (např. {example}):",
    "supplier.connectButton": "Připojit",
    "supplier.verifiedLabel": "Ověřeno ✓",
    "supplier.awaitingDns": "Čeká na DNS",
    "supplier.dnsStep1Title": "1. Nastavte DNS záznamy u vašeho registrátora",
    "supplier.dnsStep1Instruction": "Přihlaste se do správy domény (Forpsi, Active24, WEDOS, Cloudflare…) a přidejte tyto záznamy:",
    "supplier.mainDomainLabel": "Pro hlavní doménu (např. {example}):",
    "supplier.dnsTypeHeader": "Typ",
    "supplier.dnsNameHeader": "Název",
    "supplier.dnsValueHeader": "Hodnota",
    "supplier.subdomainLabel": "Pro subdoménu (např. {example}):",
    "supplier.subdomainHint": "U subdomény nahraďte {sub} vaším názvem (např. {ex1}, {ex2}). CNAME záznam je doporučený pro subdomény.",
    "supplier.dnsStep2Title": "2. Přidejte ověřovací záznam",
    "supplier.dnsStep2Instruction": "Tento záznam slouží k ověření, že doména patří vám:",
    "supplier.dnsSettingsTitle": "Kde najdu nastavení DNS?",
    "supplier.dnsForpsi": "Administrace → Domény → vaše doména → DNS záznamy",
    "supplier.dnsActive24": "Detail domény → DNS správa → Přidat záznam",
    "supplier.dnsWedos": "DNS správa → Přidat záznam (typ A nebo TXT)",
    "supplier.dnsCloudflare": "DNS → Records → Add record",
    "supplier.dnsPropagationHint": "Změna DNS se projeví do 5 minut – 24 hodin. Po nastavení klikněte 'Ověřit DNS'.",
    "supplier.verifyDns": "Ověřit DNS",
    "supplier.disconnectDomain": "Odpojit doménu",
    "supplier.chatbotSettingsLabel": "Nastavení chatbota a ceníků",
    "supplier.noPriceItemsWarning": "Bez nastaveného ceníku se vaše firma nezobrazuje v nabídkách.",
    "supplier.setPriceList": "Nastavit ceník →",
    "supplier.readiness.setupCta": "Nastavení chatbota a ceníků",
    "supplier.readiness.companyInfo": "Informace o firmě",
    "supplier.readiness.pricing": "Ceník",
    "supplier.readiness.done": "Hotovo",
    "supplier.readiness.missing": "Chybí",
    "supplier.readiness.warningText": "Aby se váš chatbot veřejně zobrazil, musíte vyplnit informace o firmě a nastavit ceník.",
    "supplier.readiness.readyText": "Váš chatbot je připraven k veřejnému zobrazení.",
    "supplier.readiness.fallbackTitle": "Chatbot zatím není dostupný",
    "supplier.readiness.fallbackDescription": "Tento chatbot se právě připravuje. Zkuste to prosím později.",
    "supplier.ownChatbot": "Vlastní chatbot",
    "supplier.marketplaceInquiries": "Poptávky z cenik.ai",
    "supplier.filterNew": "Nové",
    "supplier.filterQuoteSent": "Nabídka odeslána",
    "supplier.filterHidden": "Skryté",
    "supplier.chatbotBadge": "Chatbot",
    "supplier.marketplaceBadge": "cenik.ai",
    "supplier.requestMaterial": "Poptávka materiálu",
    "supplier.quoteAction": "Nacenit",
    "supplier.restore": "Obnovit",
    "supplier.hideAction": "Skrýt",
    "supplier.marketplaceUpsellTitle": "Poptávky z cenik.ai marketplace",
    "supplier.marketplaceUpsellDescription": "Získejte přístup k poptávkám z celého marketplace s Premium plánem",
    "supplier.premiumLabel": "Premium",
    "supplier.approved": "Schváleno",
    "customer.allowContact": "Chci, aby mě dodavatel kontaktoval",
    "customer.approved": "Kontakt sdílen",
    "customer.contactShared": "Dodavatel nyní vidí vaše kontaktní údaje",
    "customer.supplierContact": "Kontakt na dodavatele:",

    // Filters
    "filter.title": "Filtr poptávek",
    "filter.workType": "Typ práce",
    "filter.allTypes": "Všechny typy",
    "filter.location": "Místo stavby",
    "filter.locationPlaceholder": "např. Praha, Brno...",
    "filter.priceFrom": "Cena od (Kč)",
    "filter.priceTo": "Cena do (Kč)",
    "filter.deadline": "Termín provedení",
    "filter.deadlinePlaceholder": "např. květen 2026, ihned...",
    "filter.lock": "Uzamknout filtr",
    "filter.apply": "Zobrazit výsledky",
    "filter.save": "Uložit a dostávat oznámení",
    "filter.unlock": "Odemknout filtr",
    "filter.locked": "Uzamčeno",
    "filter.wholeCountry": "Celá Česká republika",
    "filter.reset": "Resetovat",
    "filter.lockedHint": "Filtr je uzamčen – nové poptávky odpovídající vašim kritériím vám budou zasílány do notifikací i na e-mail. Pro změnu filtrů je nejprve odemkněte.",
    "filter.noResults": "Žádné poptávky neodpovídají filtru",
    "filter.noResultsHint": "Zkuste upravit nebo resetovat filtr",
    "filter.searchCity": "Hledat město nebo obec...",
    "filter.radius": "Okruh",
    "filter.addWholeCZ": "+ Celá Česká republika",
    "filter.wholeCZ": "Celá Česká republika",
    "filter.noLocationsSet": "Žádné lokace nenastaveny",
    "filter.searching": "Hledám",
    "filter.locationAreas": "Oblasti působnosti",
    "filter.locationSaved": "Oblasti uloženy",

    // Register extra
    "register.checkEmail": "Ověřte svůj e-mail pro aktivaci účtu",
    "register.emailSent": "E-mail odeslán!",
    "register.checkEmailDesc": "Zkontrolujte svou schránku a klikněte na potvrzovací odkaz. Poté se můžete přihlásit.",
    "register.goToLogin": "Přejít na přihlášení",

    // Profile
    "profile.title": "Nastavení profilu",
    "profile.personalInfo": "Osobní údaje",
    "profile.fullName": "Jméno a příjmení",
    "profile.fullNamePlaceholder": "Jan Novák",
    "profile.phone": "Telefon",
    "profile.phonePlaceholder": "+420 123 456 789",
    "profile.save": "Uložit",
    "profile.saved": "Profil uložen",
    "profile.supplierPanel": "Panel dodavatele",
    "profile.sitesTitle": "Projekty",
    "profile.sitesSubtitle": "Spravujte své projekty a stavby",
    "profile.siteNamePlaceholder": "Název projektu (např. Rodinný dům)",
    "profile.siteAddressPlaceholder": "Adresa projektu",
    "profile.addSite": "Uložit projekt",
    "profile.siteAdded": "Projekt přidán",
    "profile.siteDeleted": "Projekt odstraněn",
    "profile.siteSaved": "Projekt uložen",

    // Sidebar extra
    "sidebar.settings": "Nastavení",
    "sidebar.logout": "Odhlásit se",
    "sidebar.aboutUs": "O nás",
    "sidebar.assignTo": "Přiřadit k projektu",
    "sidebar.unassign": "Odebrat z projektu",
    "sidebar.deleteChat": "Smazat chat",
    "sidebar.chatDeleted": "Chat smazán",
    "sidebar.chatAssigned": "Chat přiřazen",
    "sidebar.noProjectsYet": "Zatím žádné projekty",
    "sidebar.noSearchResults": "Žádné výsledky",
    "sidebar.history": "Historie",
    "sidebar.inquiriesAndQuotes": "Poptávky a nabídky",
    "sidebar.noInquiriesYet": "Zatím žádné poptávky",
    "sidebar.quote": "nabídka",
    "sidebar.quotes": "nabídky",
    "sidebar.backToChat": "Zpět na chat",
    "sidebar.aboutUsResponse": "## O cenik.ai 👋\n\nJsme tu, abychom **změnili způsob, jakým funguje naceňování a poptávky ve stavebnictví**.\n\nVěříme, že získat férovou cenu za fasádu, zateplení nebo jinou stavební práci by mělo být jednoduché – jak pro ty, **kdo poptávají**, tak pro ty, **kdo práci realizují**.\n\n🏗️ **Pro zákazníky** – rychlý odhad ceny bez čekání na nabídky. Jasné srovnání materiálů a technologií.\n\n🤝 **Pro řemeslníky a firmy** – kvalitní poptávky přímo do e-mailu. Žádné poplatky, žádné prostředníky.\n\nZa projektem stojí **Adam Černohous, IČO: 76535398**, se sídlem Třebovská 65, Ústí nad Orlicí 56203.\n\n📧 Kontakt: **info@cenik.ai**",

    // Legal
    "legal.termsQuery": "Smluvn\u00ed podm\u00ednky",
    "legal.termsResponse": [
      "## Smluvn\u00ed podm\u00ednky slu\u017eby cenik.ai",
      "",
      "**Posledn\u00ed aktualizace: 1. 3. 2026**",
      "",
      "### 1. \u00davodn\u00ed ustanoven\u00ed",
      "",
      "Tyto smluvn\u00ed podm\u00ednky (d\u00e1le jen \u201ePodm\u00ednky\u201c) upravuj\u00ed pravidla u\u017e\u00edv\u00e1n\u00ed webov\u00e9 slu\u017eby **cenik.ai** (d\u00e1le jen \u201eSlu\u017eba\u201c), kterou provozuje **Adam \u010cernohous, I\u010cO: 76535398**, se s\u00eddlem T\u0159ebovsk\u00e1 65, \u00dast\u00ed nad Orlic\u00ed 56203 (d\u00e1le jen \u201eProvozovatel\u201c).",
      "",
      "Pou\u017e\u00edv\u00e1n\u00edm Slu\u017eby vyja\u0159ujete souhlas s t\u011bmito Podm\u00ednkami.",
      "",
      "### 2. Popis Slu\u017eby",
      "",
      "Slu\u017eba poskytuje **orienta\u010dn\u00ed cenov\u00e9 kalkulace** stavebn\u00edch prac\u00ed, zejm\u00e9na fas\u00e1d a zateplen\u00ed, prost\u0159ednictv\u00edm chatbota zalo\u017een\u00e9ho na um\u011bl\u00e9 inteligenci. V\u00fdstupy Slu\u017eby maj\u00ed **informativn\u00ed charakter** a nep\u0159edstavuj\u00ed z\u00e1vaznou nab\u00eddku ani odborn\u00fd posudek.",
      "",
      "### 3. Zaznamen\u00e1v\u00e1n\u00ed a zpracov\u00e1n\u00ed chatov\u00fdch zpr\u00e1v",
      "",
      "U\u017eivatel bere na v\u011bdom\u00ed a souhlas\u00ed s t\u00edm, \u017ee:",
      "- **Ve\u0161ker\u00e9 chatov\u00e9 zpr\u00e1vy** (v\u010detn\u011b zpr\u00e1v neregistrovan\u00fdch u\u017eivatel\u016f) jsou zaznamen\u00e1v\u00e1ny a ukl\u00e1d\u00e1ny.",
      "- Zaznamenan\u00e9 zpr\u00e1vy jsou vyu\u017e\u00edv\u00e1ny pro **poskytov\u00e1n\u00ed slu\u017eby**, **zlep\u0161ov\u00e1n\u00ed kvality AI odpov\u011bd\u00ed**, **monitorov\u00e1n\u00ed trhu** a **vytv\u00e1\u0159en\u00ed cenov\u00fdch popt\u00e1vek**.",
      "- Chatov\u00e9 zpr\u00e1vy mohou b\u00fdt **pou\u017eity pro tr\u00e9nov\u00e1n\u00ed a vylep\u0161ov\u00e1n\u00ed AI modelu**.",
      "- U\u017eivatel m\u00e1 pr\u00e1vo po\u017e\u00e1dat o **v\u00fdmaz sv\u00fdch chatov\u00fdch zpr\u00e1v** kontaktov\u00e1n\u00edm info@cenik.ai.",
      "- **U\u017eivatel by nem\u011bl v chatu sd\u00edlet citliv\u00e9 osobn\u00ed \u00fadaje** (rodn\u00e9 \u010d\u00edslo, \u010d\u00edsla bankovn\u00edch \u00fa\u010dt\u016f, hesla apod.).",
      "",
      "### 4. Podm\u00ednky u\u017e\u00edv\u00e1n\u00ed",
      "",
      "- Slu\u017eba je ur\u010dena osob\u00e1m star\u0161\u00edm **16 let**.",
      "- U\u017eivatel se zavazuje uv\u00e1d\u011bt **pravdiv\u00e9 \u00fadaje** a nepou\u017e\u00edvat Slu\u017ebu k nez\u00e1konn\u00fdm \u00fa\u010del\u016fm.",
      "- Provozovatel si vyhrazuje pr\u00e1vo omezit nebo ukon\u010dit p\u0159\u00edstup ke Slu\u017eb\u011b bez ud\u00e1n\u00ed d\u016fvodu.",
      "- Registrovan\u00ed u\u017eivatel\u00e9 jsou odpov\u011bdn\u00ed za zabezpe\u010den\u00ed sv\u00e9ho \u00fa\u010dtu.",
      "",
      "### 5. Odpov\u011bdnost",
      "",
      "- Provozovatel **neodpov\u00edd\u00e1 za p\u0159esnost** cenov\u00fdch odhad\u016f ani za \u0161kody vznikl\u00e9 v d\u016fsledku rozhodnut\u00ed u\u010din\u011bn\u00fdch na z\u00e1klad\u011b v\u00fdstup\u016f Slu\u017eby.",
      "- AI m\u016f\u017ee generovat **nep\u0159esn\u00e9 nebo ne\u00fapln\u00e9 informace**. U\u017eivatel je povinen si d\u016fle\u017eit\u00e9 \u00fadaje ov\u011b\u0159it u kvalifikovan\u00e9ho odborn\u00edka.",
      "- Provozovatel neodpov\u00edd\u00e1 za v\u00fdpadky Slu\u017eby ani za ztr\u00e1tu dat.",
      "",
      "### 6. Du\u0161evn\u00ed vlastnictv\u00ed",
      "",
      "Ve\u0161ker\u00fd obsah Slu\u017eby (texty, grafika, software) je chr\u00e1n\u011bn autorsk\u00fdm pr\u00e1vem. U\u017eivatel nesm\u00ed obsah kop\u00edrovat, distribuovat ani komer\u010dn\u011b vyu\u017e\u00edvat bez p\u00edsemn\u00e9ho souhlasu Provozovatele.",
      "",
      "### 7. Zpracov\u00e1n\u00ed osobn\u00edch \u00fadaj\u016f",
      "",
      "Zpracov\u00e1n\u00ed osobn\u00edch \u00fadaj\u016f se \u0159\u00edd\u00ed samostatn\u00fdmi **Z\u00e1sadami ochrany soukrom\u00ed**.",
      "",
      "### 8. Zm\u011bny Podm\u00ednek",
      "",
      "Provozovatel si vyhrazuje pr\u00e1vo tyto Podm\u00ednky kdykoli zm\u011bnit. O podstatn\u00fdch zm\u011bn\u00e1ch bude u\u017eivatel informov\u00e1n prost\u0159ednictv\u00edm Slu\u017eby.",
      "",
      "### 9. Rozhodn\u00e9 pr\u00e1vo",
      "",
      "Tyto Podm\u00ednky se \u0159\u00edd\u00ed pr\u00e1vn\u00edm \u0159\u00e1dem **\u010cesk\u00e9 republiky**. P\u0159\u00edpadn\u00e9 spory budou \u0159e\u0161eny p\u0159\u00edslu\u0161n\u00fdmi soudy \u010cR.",
      "",
      "### 10. Kontakt",
      "",
      "\ud83d\udce7 **info@cenik.ai**"
    ].join("\n"),

    "legal.privacyQuery": "Z\u00e1sady ochrany soukrom\u00ed",
    "legal.privacyResponse": [
      "## Z\u00e1sady ochrany soukrom\u00ed \u2013 cenik.ai",
      "",
      "**Posledn\u00ed aktualizace: 8. 3. 2026**",
      "",
      "### 1. Spr\u00e1vce osobn\u00edch \u00fadaj\u016f",
      "",
      "Spr\u00e1vcem osobn\u00edch \u00fadaj\u016f je **Adam \u010cernohous, I\u010cO: 76535398**, se s\u00eddlem T\u0159ebovsk\u00e1 65, \u00dast\u00ed nad Orlic\u00ed 56203, e-mail: **info@cenik.ai**.",
      "",
      "### 2. Jak\u00e9 \u00fadaje zpracov\u00e1v\u00e1me",
      "",
      "**a) Registrovan\u00ed u\u017eivatel\u00e9:**",
      "- E-mailov\u00e1 adresa",
      "- Jm\u00e9no a p\u0159\u00edjmen\u00ed (nepovinn\u00e9)",
      "- Telefonn\u00ed \u010d\u00edslo (nepovinn\u00e9)",
      "- Historie chat\u016f a projekt\u016f",
      "",
      "**b) Neregistrovan\u00ed u\u017eivatel\u00e9:**",
      "- Obsah chatov\u00fdch zpr\u00e1v (ukl\u00e1d\u00e1ny po dobu 90 dn\u00ed)",
      "- Technick\u00e9 \u00fadaje (IP adresa, typ prohl\u00ed\u017ee\u010de)",
      "",
      "**c) Kontaktn\u00ed \u00fadaje pro popt\u00e1vky:**",
      "- Jm\u00e9no, e-mail, telefon (pokud u\u017eivatel dobrovoln\u011b poskytne v chatu)",
      "",
      "### 3. \u00da\u010dely zpracov\u00e1n\u00ed",
      "",
      "- **Provoz u\u017eivatelsk\u00e9ho \u00fa\u010dtu** \u2013 Pln\u011bn\u00ed smlouvy (\u010dl. 6/1b GDPR)",
      "- **Poskytov\u00e1n\u00ed AI kalkulac\u00ed** \u2013 Opr\u00e1vn\u011bn\u00fd z\u00e1jem (\u010dl. 6/1f GDPR)",
      "- **Zaznamen\u00e1v\u00e1n\u00ed a ukl\u00e1d\u00e1n\u00ed chatov\u00fdch zpr\u00e1v** \u2013 Opr\u00e1vn\u011bn\u00fd z\u00e1jem (\u010dl. 6/1f GDPR) pro poskytov\u00e1n\u00ed a zlep\u0161ov\u00e1n\u00ed slu\u017eby",
      "- **Anonymizovan\u00e9 vyu\u017eit\u00ed chat\u016f pro tr\u00e9nov\u00e1n\u00ed AI** \u2013 Opr\u00e1vn\u011bn\u00fd z\u00e1jem (\u010dl. 6/1f GDPR)",
      "- **Zprost\u0159edkov\u00e1n\u00ed popt\u00e1vek dodavatel\u016fm** \u2013 Souhlas (\u010dl. 6/1a GDPR)",
      "- **Zlep\u0161ov\u00e1n\u00ed Slu\u017eby** \u2013 Opr\u00e1vn\u011bn\u00fd z\u00e1jem (\u010dl. 6/1f GDPR)",
      "- **Pln\u011bn\u00ed pr\u00e1vn\u00edch povinnost\u00ed** \u2013 Pr\u00e1vn\u00ed povinnost (\u010dl. 6/1c GDPR)",
      "",
      "### 4. Automatizovan\u00e9 zpracov\u00e1n\u00ed a AI",
      "",
      "- Chatov\u00e9 zpr\u00e1vy jsou zpracov\u00e1v\u00e1ny **syst\u00e9mem um\u011bl\u00e9 inteligence** za \u00fa\u010delem generov\u00e1n\u00ed odpov\u011bd\u00ed a cenov\u00fdch kalkulac\u00ed.",
      "- \u00dadaje z chat\u016f mohou b\u00fdt pou\u017eity k **vylep\u0161ov\u00e1n\u00ed AI modelu**, **monitorov\u00e1n\u00ed trhu** a lad\u011bn\u00ed p\u0159esnosti odpov\u011bd\u00ed.",
      "- Na z\u00e1klad\u011b chatov\u00fdch zpr\u00e1v **nedoch\u00e1z\u00ed k automatizovan\u00e9mu rozhodov\u00e1n\u00ed** s pr\u00e1vn\u00edmi \u00fa\u010dinky (\u010dl. 22 GDPR).",
      "- M\u00e1te pr\u00e1vo **nam\u00edtnout** proti zpracov\u00e1n\u00ed va\u0161ich chat\u016f pro \u00fa\u010dely tr\u00e9nov\u00e1n\u00ed AI kontaktov\u00e1n\u00edm info@cenik.ai.",
      "- **Doporu\u010dujeme nesd\u00edlet v chatu citliv\u00e9 osobn\u00ed \u00fadaje** (rodn\u00e9 \u010d\u00edslo, \u010d\u00edsla bankovn\u00edch \u00fa\u010dt\u016f, hesla apod.).",
      "",
      "### 5. P\u0159ed\u00e1v\u00e1n\u00ed \u00fadaj\u016f t\u0159et\u00edm stran\u00e1m",
      "",
      "\u00dadaje mohou b\u00fdt p\u0159ed\u00e1ny:",
      "- **Poskytovatel\u016fm AI slu\u017eeb** (OpenAI, Google) za \u00fa\u010delem generov\u00e1n\u00ed odpov\u011bd\u00ed",
      "- **Dodavatel\u016fm stavebn\u00edch slu\u017eeb** (pouze kontaktn\u00ed \u00fadaje, se souhlasem u\u017eivatele)",
      "- **Poskytovatel\u016fm infrastruktury** (hosting, datab\u00e1ze)",
      "",
      "\u00dadaje nejsou p\u0159ed\u00e1v\u00e1ny mimo EU/EHP bez odpov\u00eddaj\u00edc\u00edch z\u00e1ruk dle GDPR.",
      "",
      "### 6. Doba uchov\u00e1v\u00e1n\u00ed",
      "",
      "- **\u00da\u010detn\u00ed \u00fadaje:** po dobu stanovenou z\u00e1konem (min. 5 let)",
      "- **U\u017eivatelsk\u00e9 \u00fa\u010dty:** po dobu trv\u00e1n\u00ed \u00fa\u010dtu + 30 dn\u00ed po smaz\u00e1n\u00ed",
      "- **Chatov\u00e9 zpr\u00e1vy:** po dobu nezbytn\u011b nutnou pro poskytov\u00e1n\u00ed a zlep\u0161ov\u00e1n\u00ed Slu\u017eby",
      "- **Popt\u00e1vky:** po dobu nezbytn\u011b nutnou pro poskytov\u00e1n\u00ed Slu\u017eby",
      "- **Technick\u00e9 logy:** max. 90 dn\u00ed",
      "",
      "### 7. Va\u0161e pr\u00e1va",
      "",
      "M\u00e1te pr\u00e1vo na:",
      "- **P\u0159\u00edstup** k osobn\u00edm \u00fadaj\u016fm",
      "- **Opravu** nep\u0159esn\u00fdch \u00fadaj\u016f",
      "- **V\u00fdmaz** \u00fadaj\u016f (pr\u00e1vo b\u00fdt zapomenut)",
      "- **Omezen\u00ed** zpracov\u00e1n\u00ed",
      "- **P\u0159enositelnost** \u00fadaj\u016f",
      "- **N\u00e1mitku** proti zpracov\u00e1n\u00ed",
      "- **Odvol\u00e1n\u00ed souhlasu** (bez dopadu na z\u00e1konnost p\u0159edchoz\u00edho zpracov\u00e1n\u00ed)",
      "",
      "Pro uplatn\u011bn\u00ed pr\u00e1v kontaktujte: **info@cenik.ai**",
      "",
      "### 8. Pod\u00e1n\u00ed st\u00ed\u017enosti",
      "",
      "M\u00e1te pr\u00e1vo podat st\u00ed\u017enost u **\u00da\u0159adu pro ochranu osobn\u00edch \u00fadaj\u016f** (\u00daOO\u00da), Pplk. Sochora 27, 170 00 Praha 7, www.uoou.cz.",
      "",
      "### 9. Zabezpe\u010den\u00ed",
      "",
      "Osobn\u00ed \u00fadaje chr\u00e1n\u00edme pomoc\u00ed \u0161ifrov\u00e1n\u00ed (TLS/SSL), \u0159\u00edzen\u00ed p\u0159\u00edstupu a pravideln\u00fdch bezpe\u010dnostn\u00edch audit\u016f.",
      "",
      "### 10. Kontakt",
      "",
      "\ud83d\udce7 **info@cenik.ai**"
    ].join("\n"),

    "legal.cookiesQuery": "P\u0159edvolby cookies",
    "legal.cookiesResponse": [
      "## Z\u00e1sady pou\u017e\u00edv\u00e1n\u00ed cookies \u2013 cenik.ai",
      "",
      "**Posledn\u00ed aktualizace: 1. 3. 2026**",
      "",
      "### Co jsou cookies?",
      "",
      "Cookies jsou mal\u00e9 textov\u00e9 soubory, kter\u00e9 se ukl\u00e1daj\u00ed ve va\u0161em prohl\u00ed\u017ee\u010di p\u0159i n\u00e1v\u0161t\u011bv\u011b webov\u00fdch str\u00e1nek. Pom\u00e1haj\u00ed zajistit spr\u00e1vn\u00e9 fungov\u00e1n\u00ed slu\u017eby.",
      "",
      "### Jak\u00e9 cookies pou\u017e\u00edv\u00e1me",
      "",
      "**1. Nezbytn\u00e9 cookies** (v\u017edy aktivn\u00ed)",
      "- P\u0159ihl\u00e1\u0161en\u00ed a autentizace u\u017eivatele",
      "- Zabezpe\u010den\u00ed relace",
      "- Jazykov\u00e9 preference",
      "- Tyto cookies nelze vypnout, jsou nezbytn\u00e9 pro fungov\u00e1n\u00ed Slu\u017eby.",
      "",
      "**2. Analytick\u00e9 cookies** (voliteln\u00e9)",
      "- Anal\u00fdza n\u00e1v\u0161t\u011bvnosti a pou\u017e\u00edv\u00e1n\u00ed Slu\u017eby",
      "- Zlep\u0161ov\u00e1n\u00ed u\u017eivatelsk\u00e9ho z\u00e1\u017eitku",
      "- Anonymizovan\u00e9 statistiky",
      "",
      "**3. Funk\u010dn\u00ed cookies** (voliteln\u00e9)",
      "- Zapamatov\u00e1n\u00ed nastaven\u00ed (motiv, jazyk)",
      "- Personalizace obsahu",
      "",
      "### Cookies t\u0159et\u00edch stran",
      "",
      "Slu\u017eba m\u016f\u017ee vyu\u017e\u00edvat cookies od:",
      "- **Poskytovatele hostingu** \u2013 pro zaji\u0161t\u011bn\u00ed provozu",
      "- **Analytick\u00fdch n\u00e1stroj\u016f** \u2013 pro m\u011b\u0159en\u00ed n\u00e1v\u0161t\u011bvnosti",
      "",
      "### Jak spravovat cookies",
      "",
      "Cookies m\u016f\u017eete spravovat v nastaven\u00ed sv\u00e9ho prohl\u00ed\u017ee\u010de:",
      "- **Chrome:** Nastaven\u00ed \u2192 Soukrom\u00ed a zabezpe\u010den\u00ed \u2192 Cookies",
      "- **Firefox:** Nastaven\u00ed \u2192 Soukrom\u00ed a zabezpe\u010den\u00ed",
      "- **Safari:** P\u0159edvolby \u2192 Soukrom\u00ed",
      "- **Edge:** Nastaven\u00ed \u2192 Cookies a opr\u00e1vn\u011bn\u00ed web\u016f",
      "",
      "\u26a0\ufe0f Vypnut\u00ed nezbytn\u00fdch cookies m\u016f\u017ee omezit funk\u010dnost Slu\u017eby.",
      "",
      "### Doba platnosti cookies",
      "",
      "- **Nezbytn\u00e9 (relace):** Do zav\u0159en\u00ed prohl\u00ed\u017ee\u010de",
      "- **Nezbytn\u00e9 (p\u0159ihl\u00e1\u0161en\u00ed):** 30 dn\u00ed",
      "- **Analytick\u00e9:** 12 m\u011bs\u00edc\u016f",
      "- **Funk\u010dn\u00ed:** 12 m\u011bs\u00edc\u016f",
      "",
      "### Pr\u00e1vn\u00ed z\u00e1klad",
      "",
      "Pou\u017e\u00edv\u00e1n\u00ed cookies se \u0159\u00edd\u00ed **z\u00e1konem \u010d. 127/2005 Sb.** (z\u00e1kon o elektronick\u00fdch komunikac\u00edch) a **na\u0159\u00edzen\u00edm GDPR**. Nezbytn\u00e9 cookies ukl\u00e1d\u00e1me na z\u00e1klad\u011b opr\u00e1vn\u011bn\u00e9ho z\u00e1jmu, analytick\u00e9 a funk\u010dn\u00ed cookies pouze s va\u0161\u00edm souhlasem.",
      "",
      "### Kontakt",
      "",
      "Dotazy ohledn\u011b cookies sm\u011b\u0159ujte na: \ud83d\udce7 **info@cenik.ai**"
    ].join("\n"),

    // Dashboard extra
    "dashboard.newProject": "Nový projekt",

    // Notifications
    "notifications.title": "Upozornění",
    "notifications.empty": "Žádná upozornění",
    "notifications.newInquiry": "Nová poptávka",
    "notifications.loginPrompt": "Zde se budou zobrazovat vaše nabídky od dodavatelů. Přihlaste se pro zobrazení.",

    // Customer dashboard
    "customerDashboard.title": "Moje poptávky",
    "customerDashboard.newInquiry": "Nová poptávka",
    "customerDashboard.onboardingTitle": "Jak funguje cenik.ai pro vás?",
    "customerDashboard.onboardingStep1": "Zeptejte se v chatu na odhad ceny zateplení nebo fasády.",
    "customerDashboard.onboardingStep2": "Chat z vašich odpovědí vytvoří poptávku.",
    "customerDashboard.onboardingStep3": "Poptávku odešleme firmám z vašeho okolí a jejich nabídky uvidíte právě tady.",
    "customerDashboard.emptyTitle": "Zatím nemáte žádné poptávky",
    "customerDashboard.emptyDesc": "Otevřete chat na hlavní stránce a nechte si spočítat první poptávku.",
    "customerDashboard.statusOpen": "Otevřená",
    "customerDashboard.statusInProgress": "Probíhá komunikace",
    "customerDashboard.statusClosed": "Uzavřená",
    "customerDashboard.statusCompleted": "Realizováno",
    "customerDashboard.quote": "nabídka",
    "customerDashboard.quotes": "nabídky",
    "customerDashboard.quotesMany": "nabídek",

    // Supplier chat
    "supplierChat.newChat": "Nový chat",
    "supplierChat.inquiry": "Poptávka",
    "supplierChat.message": "Zpráva",
    "supplierChat.upsellTitle": "Chcete vlastní chatbot?",
    "supplierChat.upsellDesc": "Získejte AI asistenta pro vaši firmu s vlastním ceníkem a brandingem.",
    "supplierChat.upsellLink": "Zjistit více →",
    "supplierChat.aboutUs": "O nás",
    "supplierChat.msgSavedNotifyFailed": "Zpráva se uložila, ale upozornění firmě se nepodařilo odeslat.",
    "supplierChat.msgDeliveryFailed": "Zprávu se nepodařilo doručit firmě. Zkuste to prosím znovu.",
    "supplierChat.inquiryDeliveryFailed": "Poptávku se nepodařilo doručit firmě. Zkuste to prosím znovu.",
    "supplierChat.error": "Chyba: {error}",
    "supplierChat.chatNotFound": "AI chat nenalezen",
    "supplierChat.domainNotLinked": "Tato doména zatím není propojena s žádným ceníkem.",
    "supplierChat.slugNotFound": "neexistuje.",
    "supplierChat.backToHome": "← Zpět na hlavní stránku",
    "supplierChat.askAboutCompany": "Řekněte mi více o vaší firmě.",
    "supplierChat.inputPlaceholder": "Napište dotaz nebo parametry…",
    "supplierChat.aboutCompany": "O firmě {name}",
    "supplierChat.specialization": "Specializace",
    "supplierChat.serviceArea": "Oblast působnosti",
    "supplierChat.aiAssistantHelp": "Co vám AI asistent pomůže zjistit?",
    "supplierChat.aiHelp1": "Orientační cenu stavebních prací",
    "supplierChat.aiHelp2": "Dostupnost a typy materiálů",
    "supplierChat.aiHelp3": "Odpovědi na odborné dotazy",
    "supplierChat.aiHelp4": "Předběžnou kalkulaci na základě vašich parametrů",
    "supplierChat.faq": "Časté dotazy",
    "supplierChat.contact": "Kontakt",

    // Sidebar extras
    "sidebar.currentChat": "Aktuální chat",
    "sidebar.messageCount": "zpráv",

    // Supplier settings
    "supplierSettings.companyName": "Název firmy *",
    "supplierSettings.ico": "IČO",
    "supplierSettings.contactEmail": "Kontaktní e-mail",
    "supplierSettings.contactPhone": "Kontaktní telefon",
    "supplierSettings.region": "Region",
    "supplierSettings.regionPlaceholder": "Např. Praha, Jihomoravský kraj",
    "supplierSettings.description": "Popis firmy",
    "supplierSettings.descriptionPlaceholder": "Krátký popis vaší firmy...",
    "supplierSettings.saving": "Ukládám...",
    "supplierSettings.saveCompany": "Uložit údaje firmy",
    "supplierSettings.savedCompany": "Údaje firmy uloženy",
    "supplierSettings.saveError": "Chyba při ukládání",
    "supplierSettings.saveFailed": "Nepodařilo se uložit",
    "supplierSettings.chatbotDesc": "Přizpůsobte, jak se váš chatbot představí zákazníkům. Ve free verzi bot nesděluje kontaktní údaje.",
    "supplierSettings.botName": "Jméno chatbota",
    "supplierSettings.botNameHint": "Pokud nevyplníte, bot se bude jmenovat podle vaší firmy.",
    "supplierSettings.botIntro": "Jak se bot představí",
    "supplierSettings.botIntroPlaceholder": "Jsem AI asistent společnosti XY, pomohu vám s dotazy na fasády a zateplení.",
    "supplierSettings.aboutCompany": "O firmě",
    "supplierSettings.aboutCompanyPlaceholder": "Stručné info o firmě, specializaci, zkušenostech, oblasti působení...",
    "supplierSettings.faq": "FAQ – časté otázky a odpovědi",
    "supplierSettings.faqPlaceholder": "Otázka: Jaké materiály používáte?\nOdpověď: EPS, minerální vata, ETICS systémy...\n\nOtázka: Jaká je vaše cena za m²?\nOdpověď: Cena závisí na rozsahu prací, obvykle...",
    "supplierSettings.saveBot": "Uložit nastavení",
    "supplierSettings.savedBot": "Nastavení chatbota uloženo.",
    "supplierSettings.saveBotFailed": "Nepodařilo se uložit nastavení.",
    "supplierSettings.tabServices": "Služby & ceníky",
    "supplierSettings.tabPublicPage": "Veřejná stránka",
    "supplierSettings.publicPageDesc": "Nastavte, jak se vaše veřejná AI stránka zobrazí ve vyhledávačích a na sociálních sítích.",
    "supplierSettings.tagline": "Krátký slogan",
    "supplierSettings.taglinePlaceholder": "Např. Fasádnické práce na klíč v Praze",
    "supplierSettings.taglineHint": "Zobrazí se pod názvem firmy na veřejné stránce.",
    "supplierSettings.businessType": "Obor / typ služby",
    "supplierSettings.businessTypePlaceholder": "Fasádnické práce",
    "supplierSettings.serviceArea": "Oblast působnosti",
    "supplierSettings.serviceAreaPlaceholder": "Praha a Středočeský kraj",
    "supplierSettings.seoTitle": "SEO titulek stránky",
    "supplierSettings.seoTitleHint": "Zobrazí se v záložce prohlížeče a ve výsledcích vyhledávání.",
    "supplierSettings.seoDesc": "SEO popis stránky",
    "supplierSettings.seoDescHint": "Zobrazí se pod titulkem ve výsledcích vyhledávání.",
    "supplierSettings.ogImage": "OG image URL (volitelné)",
    "supplierSettings.ogImageHint": "Obrázek zobrazený při sdílení na sociálních sítích. Ideálně 1200×630 px.",
    "supplierSettings.searchPreview": "Náhled ve vyhledávači",
    "supplierSettings.savePublicPage": "Uložit nastavení stránky",
    "supplierSettings.savedPublicPage": "Nastavení veřejné stránky uloženo.",
    "supplierSettings.servicesHeading": "Jaké služby nabízíte?",
    "supplierSettings.servicesDesc": "Vyberte služby, které vaše firma poskytuje. Pro každou pak můžete přidat popis a ceník.",
    "supplierSettings.addCustomService": "Přidat vlastní službu…",
    "supplierSettings.addButton": "Přidat",
    "supplierSettings.serviceAlreadyAdded": "Tato služba je již přidána",
    "supplierSettings.serviceAdded": 'Služba „{name}" přidána',
    "supplierSettings.selectServiceFirst": "Vyberte alespoň jednu službu výše a poté přidejte popis a ceník.",
    "supplierSettings.customerMessages": "Zprávy od zákazníků",
    "supplierSettings.translate": "Přeložit",
    "supplierSettings.showOriginal": "Zobrazit originál",

    // Admin
    "admin.title": "Admin Panel",
    "admin.loginPrompt": "Přihlaste se administrátorským účtem",
    "admin.loginButton": "Přihlásit se",
    "admin.email": "E-mail",
    "admin.password": "Heslo",
    "admin.googleLogin": "Přihlásit se přes Google",
    "admin.orPassword": "nebo heslem",
    "admin.noPermission": "Tento účet nemá administrátorská oprávnění",
    "admin.loginFailed": "Přihlášení se nezdařilo",
    "admin.googleLoginFailed": "Google přihlášení selhalo",
    "admin.sessionExpired": "Relace vypršela, přihlaste se znovu",
    "admin.registrationFailed": "Registrace se nezdařila",
    "admin.accountCreated": "Admin účet vytvořen a přihlášen",
    "admin.loginExpired": "Přihlášení vypršelo, přihlaste se znovu",
    "admin.tab.chats": "Chaty",
    "admin.tab.inquiries": "Poptávky",
    "admin.tab.users": "Uživatelé",
    "admin.tab.prices": "Ceny",
    "admin.tab.services": "Služby",
    "admin.tab.subscriptions": "Předplatné",
    "admin.tab.feedback": "Feedback",
    "admin.tab.knowledge": "Znalosti",
    "admin.toast.saved": "Uloženo",
    "admin.toast.deleted": "Smazáno",
    "admin.toast.saveFailed": "Nepodařilo se uložit",
    "admin.toast.deleteFailed": "Nepodařilo se smazat",
    "admin.toast.loadFailed": "Nepodařilo se načíst data",
    "admin.toast.statusUpdated": "Stav aktualizován",
    "admin.toast.activated": "Aktivováno",
    "admin.toast.deactivated": "Deaktivováno",
    "admin.toast.correctionSaved": "Oprava uložena",
    "admin.toast.correctionSaveFailed": "Nepodařilo se uložit opravu",
    "admin.toast.feedbackIgnored": "Feedback vyřazen",
    "admin.toast.feedbackIgnoreFailed": "Nepodařilo se vyřadit feedback",
    "admin.toast.knowledgeAdded": "Znalost přidána",
    "admin.toast.knowledgeSaved": "Znalost uložena",
    "admin.toast.aiCategorized": "AI navrhla kategorii a název",
    "admin.toast.serviceCreated": "Služba vytvořena",
    "admin.toast.createServiceFailed": "Nepodařilo se vytvořit službu",
    "admin.toast.serviceActivated": "Služba aktivována",
    "admin.toast.serviceRejected": "Služba odmítnuta",
    "admin.toast.movedToQueue": "Přesunuto do fronty",
    "admin.toast.movedToCandidate": "Přesunuto na kandidáta",
    "admin.toast.serviceAssigned": "Služba přiřazena",
    "admin.toast.newServiceProposed": "Nová služba navržena a přiřazena",
    "admin.toast.aiNoMatch": "AI nenašla vhodnou službu",
    "admin.toast.detectionFixed": "Detekce opravena + learning vytvořen",
    "admin.toast.detectionFixFailed": "Nepodařilo se opravit detekci",
    "admin.toast.detectionRejected": "Detekce zamítnuta",
    "admin.toast.detectionRejectFailed": "Nepodařilo se zamítnout",
    "admin.toast.overrideSaved": "Override uložen",
    "admin.toast.overrideSaveFailed": "Nepodařilo se uložit override",
    "admin.toast.planChangeFailed": "Nepodařilo se změnit plán",
    "admin.toast.featureToggleFailed": "Nepodařilo se změnit stav feature",
    "admin.toast.fillTopicContent": "Vyplň téma a obsah",
    "admin.toast.fillNameSlug": "Vyplňte název a slug",
    "admin.toast.invalidSlug": "Slug může obsahovat jen malá písmena, čísla a pomlčky",
    "admin.toast.learningAdded": "Učení přidáno – AI ho začne používat okamžitě",
    "admin.toast.addFailed": "Nepodařilo se přidat",
    "admin.toast.inquiryDeleted": "Poptávka smazána",
    "admin.toast.writeCorrectedAnswer": "Napište opravenou odpověď",
    "admin.toast.serviceSaved": "Služba uložena",
    "admin.toast.generateFailed": "Generování selhalo",
    "admin.toast.formApproved": "Formulář schválen — dodavatelé ho uvidí",
    "admin.toast.approveFailed": "Nepodařilo se schválit",
    "admin.toast.formToDraft": "Formulář přepnut do editace",
    "admin.toast.operationFailed": "Nepodařilo se",
    "admin.toast.keywordApproved": "Keyword schválen",
    "admin.toast.keywordRejected": "Keyword odmítnut",
    "admin.toast.keywordAdded": "Keyword přidán",
    "admin.toast.keywordDeleted": "Keyword smazán",
    "admin.status.active": "Aktivní",
    "admin.status.inactive": "Neaktivní",
    "admin.status.candidate": "Kandidát",
    "admin.status.queued": "Ve frontě",
    "admin.status.rejected": "Odmítnuto",
    "admin.status.positive": "Pozitivní",
    "admin.status.negative": "Negativní",
    "admin.empty.inquiries": "Žádné poptávky",
    "admin.empty.suppliers": "Žádní dodavatelé",
    "admin.empty.customers": "Žádní zákazníci",
    "admin.empty.feedback": "Zatím žádný feedback od uživatelů",
    "admin.confirm.deleteInquiry": "Opravdu smazat tuto poptávku?",
    "admin.confirm.deleteLearning": "Opravdu smazat toto učení?",
    "admin.btn.approve": "Schválit",
    "admin.btn.reject": "Odmítnout",
    "admin.btn.addItem": "Přidat položku",
    "admin.btn.saveDraft": "Uložit draft",
    "admin.btn.approveAndDeploy": "Schválit a nasadit",
    "admin.btn.regenerate": "Přegenerovat",
    "admin.btn.editForm": "Upravit formulář",
    "admin.btn.collapse": "Sbalit",
    "admin.btn.showAll": "Zobrazit vše",
    "admin.btn.ignore": "Vyřadit",
    "admin.btn.save": "Uložit",
    "admin.btn.cancel": "Zrušit",
    "admin.btn.addKnowledge": "Přidat znalost pro",
    "admin.btn.restoreCandidate": "Obnovit jako kandidáta",
    "admin.btn.removeOverride": "Odstranit výjimku",
    "admin.btn.disable": "Zakázat",
    "admin.btn.enable": "Povolit",
    "admin.section.quoteForm": "Nabídkový formulář pro dodavatele",
    "admin.section.noQuoteForm": "Zatím nemá nabídkový formulář. AI navrhne položky rozpočtu pro tuto službu.",
    "admin.section.costItems": "Nákladové položky",
    "admin.section.formPreview": "Náhled formuláře pro dodavatele",
    "admin.section.serviceStatus": "Stav služby",
    "admin.section.feedbackHelp": "Hodnocení AI odpovědí. Negativní feedback s opravou se okamžitě aplikuje jako AI learning. Podezřelá hodnocení vyřaď tlačítkem.",
    "admin.section.totalRatings": "Celkem hodnocení",
    "admin.section.aiKnowledge": "AI používá tyto znalosti při odpovídání zákazníkům a generování nabídek pro tuto službu.",
    "admin.placeholder.knowledgeName": "Název znalosti",
    "admin.placeholder.knowledgeContent": "Vlož text o zateplování, cenách, materiálech... AI sama navrhne název a kategorii.",
    "admin.placeholder.learningContent": "Obsah znalosti — co má AI vědět a jak se má chovat...",
    "admin.placeholder.itemName": "Název položky",
    "admin.placeholder.keyword": "Nové klíčové slovo nebo fráze...",
    "admin.placeholder.serviceDesc": "Krátký popis služby",
    "admin.placeholder.priceName": "např. Ceny EPS",
    "admin.placeholder.newServiceName": "Název nové služby:",
    "admin.label.totalPrice": "Celková cena",
    "admin.label.currency": "Kč",
    "admin.label.records": "záznamů",
    "admin.label.offer": "nabídka",
    "admin.label.offers": "nabídky",
    "admin.label.offersMany": "nabídek",
    "admin.label.inquirySent": "Poptávka ✓",
    "admin.label.inquiryNotSent": "Bez poptávky",
    "admin.label.formDraft": "📝 Draft",
    "admin.label.formApproved": "✅ Formulář",
    "admin.label.user": "👤 Uživatel",
    "admin.label.ai": "🤖 AI",
    "admin.label.advancedCalc": "Pokročilá kalkulace",
    "admin.label.weight": "Váha",
    "admin.label.serviceNotFound": "Služba nenalezena",
    "admin.label.generating": "Generuji…",
    "admin.label.generateForm": "🤖 Generovat nabídkový formulář",
    "admin.label.analyzing": "Analyzuji...",
    "admin.label.aiCategorize": "🤖 AI zařadí",
    "admin.label.newServiceProposal": "Návrh nové služby",
    "admin.label.uncertainMatch": "Nejistý match",
    "admin.label.match": "Match",
    "admin.label.new": "Nová",
    "admin.label.uncertain": "Nejistý",
    "admin.filter.all": "Vše",
    "admin.filter.sent": "Poptávka odeslána",
    "admin.filter.notSent": "Neodeslána",
    "admin.module.walls": "Stěny",
    "admin.module.material": "Poptávka materiálu",
    "admin.module.quoteCalc": "Kalkulace nabídky",
    "admin.module.generalInquiry": "Obecná poptávka",
    "admin.module.simpleQuote": "Jednoduchá nabídka",
    "admin.material.epsWhite": "EPS bílý",
    "admin.material.epsGrey": "EPS šedý",
    "admin.material.mineralWool": "Minerální vata",
    "admin.material.ventilatedFacade": "Provětrávaná fasáda",
    "admin.section.basicInfo": "Základní info",
    "admin.section.keywordsDetection": "Klíčová slova (detection)",
    "admin.section.keywordsHelp": "Schválená klíčová slova se používají pro hybridní detekci služby. Navrhovaná čekají na review.",
    "admin.section.modules": "Moduly",
    "admin.section.knowledgeFor": "Znalosti AI pro",
    "admin.label.name": "Název",
    "admin.label.slugUrl": "Slug (URL)",
    "admin.label.keywordsComma": "Klíčová slova (čárkami, pro keyword matching)",
    "admin.label.descOptional": "Popis (volitelný)",
    "admin.label.approved": "Schválená",
    "admin.label.suggested": "Navrhovaná",
    "admin.label.rejectedKw": "Odmítnutá",
    "admin.label.profit": "Zisk",
    "admin.label.vat": "DPH",
    "admin.label.unitLabel": "Jednotka:",
    "admin.label.vatRate": "DPH sazba (12 % / 21 %)",
    "admin.label.query1": "dotaz",
    "admin.label.query2to4": "dotazy",
    "admin.label.query5plus": "dotazů",
    "admin.label.record1": "záznam",
    "admin.label.record2to4": "záznamy",
    "admin.label.learningActive": "✓ Aktivní",
    "admin.label.learningInactive": "○ Neaktivní",
    "admin.label.formApprovedBadge": "✅ Schváleno",
    "admin.label.formDraftBadge": "📝 Draft",
    "admin.label.facadeNote": "ℹ️ Fasádnické práce mají vestavěný formulář (materiál, lešení, práce, doprava). Pokud vygeneruješ nový, nahradí vestavěný.",
    "admin.label.fact": "📚 Fakt",
    "admin.label.correction": "🔧 Oprava",
    "admin.label.tip": "💡 Tip",
    "admin.btn.refresh": "Obnovit",
    "admin.btn.backToOverview": "← Zpět na přehled",
    "admin.btn.add": "Přidat",
    "admin.btn.activate": "Aktivovat",
    "admin.btn.toQueue": "Do fronty",
    "admin.placeholder.keywordsExample": "fasáda, zateplení, eps, polystyren",
    "admin.placeholder.learningTopic": "Téma (např. Cena SDK příčky)",
    "admin.toast.aiSuggestedForm": "AI navrhla formulář — zkontroluj a schval",
    "admin.toast.error": "Chyba",
    "admin.module.wallsDesc": "Zákazník zadává stěny s rozměry a otvory. Dodavatel vidí detailní rozpis ploch.",
    "admin.module.materialDesc": "Dodavatel poptává materiál od velkoobchodů přímo z poptávky.",
    "admin.module.quoteCalcDesc": "Položkový rozpočet s cenami za m². Generativní AI nabídka pro zákazníka.",
    "admin.module.generalInquiryDesc": "AI-generovaný popis poptávky z chatu.",
    "admin.module.simpleQuoteDesc": "Dodavatel zadá celkovou cenu + poznámky.",
  },

  uk: {
    "topbar.login": "Увійти",
    "topbar.register": "Зареєструватися безкоштовно",
    "topbar.comingSoon": "Скоро буде більше",
    "topbar.facades": "Фасадні роботи",
    "landing.title": "Скільки коштує ваше будівництво?",
    "landing.subtitle": "Оберіть тип будівельних робіт і дізнайтеся орієнтовну ціну за хвилину.",
    "landing.service.fasady": "Фасадні роботи",
    "landing.service.fasady.desc": "Орієнтовна ціна фасаду, утеплення та штукатурки",
    "service.fasadnicke-prace": "Фасадні роботи",
    "landing.service.strechy": "Дахи",
    "landing.service.okna": "Вікна та двері",
    "landing.startChat": "Дізнатися ціну",
    "landing.comingSoon": "Готуємо",
    "index.headline1": "Скільки коштує ваше будівництво?",
    "index.headline2": "Розрахуйте вартість будівельних робіт",
    "index.headline3": "Дізнайтеся орієнтовну ціну онлайн",
    "index.headline4": "Оцініть будівельні роботи онлайн",
    "index.headline5": "Орієнтовна ціна за хвилину",
    "index.disclaimer": "Надсилаючи повідомлення сервісу cenik.ai, чат-боту на основі штучного інтелекту, ви погоджуєтесь з нашими",
    "index.terms": "умовами використання",
    "index.privacyConfirm": "та підтверджуєте, що ознайомилися з нашою",
    "index.privacy": "політикою конфіденційності",
    "index.cookiesSee": "Див.",
    "index.cookies": "налаштування cookies",
    "cookie.message": "Цей сайт використовує файли cookie для забезпечення належної роботи та аналізу відвідуваності.",
    "cookie.accept": "Прийняти все",
    "cookie.reject": "Відхилити необов'язкові",
    "cookie.settings": "Налаштування",
    "index.sensitiveWarning": "Не діліться конфіденційними даними (ідентифікаційний номер, паролі, номери рахунків).",
    "index.nextQuestion": "Напишіть наступне запитання...",
    "index.mayMakeErrors": "cenik.ai може помилятися. Перевіряйте важливу інформацію.",
    "index.leadSaved": "Контактні дані надіслано – скоро зв'яжемося! 🏠",
    "sidebar.newChat": "Новий чат",
    "sidebar.searchChats": "Пошук чатів",
    "sidebar.projects": "Проєкти",
    "sidebar.howToUse": "Як це працює",
    "sidebar.howToUseQuery": "Як це працює?",
    "sidebar.howToUseResponse": "Ласкаво просимо до **cenik.ai** 👋\n\nЦе працює просто:\n\n1. **Напишіть, що потрібно** – тип будівельних робіт, розміри, матеріал.\n2. **AI розрахує орієнтовну ціну** – на основі актуальних ринкових даних.\n3. **Можете уточнювати** – матеріали, технології, або порівняти варіанти.\n4. **Якщо хочете безкоштовну пропозицію**, залиште контакт і ми з'єднаємо вас з перевіреними постачальниками.\n\n💡 **Порада:** Можете перейти на **калькулятор** і заповнити все самостійно.\n\nПочніть, описавши, що потрібно оцінити 🏠",
    "sidebar.yourChats": "Історія чатів",
    "sidebar.noChatsYet": "Чатів поки немає",
    "sidebar.loginForHistory": "Щоб переглянути історію чатів, увійдіть.",
    "sidebar.loginLink": "увійдіть",
    "sidebar.loginForFeature": "Щоб використовувати цю функцію, увійдіть.",
    "sidebar.loginForInquiries": "Щоб переглянути запити та пропозиції, увійдіть.",
    "sidebar.becomeSupplier": "Хочу власний чат-бот",
    "sidebar.supplierDashboard": "Панель постачальника",
    "sidebar.supplierCta": "Хочете отримувати безкоштовні запити на вашу електронну пошту?",
    "sidebar.login": "Увійти",
    "chat.placeholder1": "Що потрібно оцінити?",
    "chat.placeholder2": "Які розміри або площа?",
    "chat.placeholder3": "Який матеріал бажаєте?",
    "chat.placeholder4": "Де знаходиться будівництво?",
    "chat.placeholder5": "Маєте питання про будівельні роботи?",
    "chat.placeholderMobile1": "Що потрібно оцінити?",
    "chat.placeholderMobile2": "Які розміри?",
    "chat.placeholderMobile3": "Який матеріал?",
    "chat.placeholderMobile4": "Де будівництво?",
    "chat.placeholderMobile5": "Питання про будівництво?",
    "chat.takePhoto": "Сфотографувати",
    "chat.addPhotos": "Додати фото",
    "chat.uploadFiles": "Завантажити файли",
    "chat.fileTooLarge": "Файл \"{name}\" більший за 10 МБ.",
    "chat.assignToProject": "Додати до проєкту",
    "chat.unassign": "Видалити з проєкту",
    "chat.deleteChat": "Видалити чат",
    "chat.loginToSave": "Увійти та зберегти",
    "suggestion.facadePrice": "Розрахувати ціну фасаду",
    "suggestion.insulationCost": "Скільки коштують будівельні роботи?",
    "suggestion.bestPolystyrene": "Який матеріал найкращий?",
    "suggestion.contactInsulation": "Дізнатися орієнтовну ціну",
    "login.title": "Увійти",
    "login.email": "Email",
    "login.emailPlaceholder": "vash@email.ua",
    "login.password": "Пароль",
    "login.passwordPlaceholder": "••••••••",
    "login.submit": "Увійти",
    "login.or": "або",
    "login.google": "Продовжити через Google",
    "login.noAccount": "Немає акаунту?",
    "login.register": "Зареєструватися",
    "login.forgotPassword": "Забули пароль?",
    "login.subtitle": "Введіть email та пароль для входу.",
    "login.chatbotBoxTitle": "Маєте бізнес?",
    "login.chatbotBoxDesc": "Створіть власний AI чат-бот для ваших клієнтів — безкоштовно.",
    "login.becomeSupplier": "Хочу власний чат-бот",
    "forgotPassword.title": "Відновлення пароля",
    "forgotPassword.description": "Введіть свій email, і ми надішлемо посилання для відновлення пароля.",
    "forgotPassword.submit": "Надіслати посилання",
    "forgotPassword.sent": "Посилання надіслано!",
    "forgotPassword.sentDesc": "Перевірте свою поштову скриньку та натисніть на посилання для відновлення пароля.",
    "forgotPassword.backToLogin": "Повернутися до входу",
    "resetPassword.title": "Новий пароль",
    "resetPassword.password": "Новий пароль",
    "resetPassword.passwordPlaceholder": "Мінімум 8 символів",
    "resetPassword.submit": "Встановити пароль",
    "resetPassword.success": "Пароль змінено",
    "login.info": "Увійдіть за допомогою email та пароля або через Google. Після входу ви матимете доступ до історії чатів та збережених проектів.",
    "register.info": "Створіть безкоштовний акаунт для збереження чатів, управління проектами та будівництвами. Потрібен лише email і пароль або Google акаунт.",
    "forgotPassword.info": "Введіть email, з яким ви реєструвалися. Ми надішлемо посилання для встановлення нового пароля.",
    "resetPassword.info": "Введіть новий пароль (мінімум 8 символів). Після збереження вас буде перенаправлено на сторінку входу.",
    "supplier.loginInfo": "Зареєструйтесь як постачальник і отримуйте безкоштовні запити з вашого регіону. Достатньо ввести email – без пароля, без оплати.",
    "supplier.registerInfo": "Створіть акаунт постачальника для управління цінами та отримання запитів. Введіть назву компанії, email та пароль.",
    "auth.continue": "Продовжити",
    "auth.invalidCredentials": "Неправильний email або пароль",
    "auth.alreadyRegistered": "Цей email вже зареєстровано. Увійдіть.",
    "auth.switchToRegister": "Немає акаунту? Зареєструйтесь",
    "auth.switchToLogin": "Вже є акаунт? Увійдіть",
    "auth.forgotPassword": "Забули пароль?",
    "auth.backToLogin": "← Повернутися до входу",
    "register.title": "Створити безкоштовний акаунт",
    "register.subtitle": "Створіть новий акаунт — потрібні лише email та пароль.",
    "register.email": "Email",
    "register.emailPlaceholder": "vash@email.ua",
    "register.password": "Пароль",
    "register.passwordPlaceholder": "Мінімум 8 символів",
    "register.submit": "Зареєструватися",
    "register.or": "або",
    "register.google": "Продовжити через Google",
    "register.hasAccount": "Вже є акаунт?",
    "register.login": "Увійти",
    "dashboard.pricing": "Ціни",
    "dashboard.emptyTitle": "Чим можу допомогти?",
    "dashboard.emptySubtitle": "Напишіть ваш запит або питання",
    "dashboard.inputPlaceholder": "Що вас цікавить сьогодні?",
    "pricing.title": "Простий прайс-лист",
    "pricing.subtitle": "Оберіть план, який вам підходить",
    "pricing.free": "Free",
    "pricing.pro": "Pro",
    "pricing.agency": "Agency",
    "pricing.forever": "назавжди",
    "pricing.perMonth": "/місяць",
    "pricing.startFree": "Почати безкоштовно",
    "pricing.choosePro": "Обрати Pro",
    "pricing.contactUs": "Зв'язатися з нами",
    "pricing.feature.requests5": "5 запитів/місяць",
    "pricing.feature.basicAI": "Базові AI відповіді",
    "pricing.feature.emailSupport": "Email підтримка",
    "pricing.feature.unlimitedRequests": "Необмежені запити",
    "pricing.feature.advancedAI": "Просунутий AI агент",
    "pricing.feature.prioritySupport": "Пріоритетна підтримка",
    "pricing.feature.pdfExport": "Експорт у PDF",
    "pricing.feature.allPro": "Все з Pro",
    "pricing.feature.teamAccounts": "Командні акаунти (5 користувачів)",
    "pricing.feature.apiAccess": "API доступ",
    "pricing.feature.customBranding": "Власний брендинг",
    "pricing.feature.dedicatedManager": "Виділений менеджер",
    "pricing.login": "Увійти",

    "supplier.headline": "Хочете безкоштовно отримувати запити, які вас цікавлять?",
    "supplier.description": "Просто введіть e-mail – ми одразу почнемо надсилати вам релевантні запити. Без пароля, без оплати.",
    "supplier.chatbotHeadline": "Хочу власний чат-бот",
    "supplier.chatbotDescription": "Зареєструйтесь та створіть AI чат-бот, який відповідатиме вашим клієнтам.",
    "supplier.benefit1": "Запити з регіонів, де ви працюєте",
    "supplier.benefit2": "Фільтруйте за типом матеріалу та обсягом",
    "supplier.benefit3": "Реагуйте лише на замовлення, які вам підходять",
    "supplier.emailPlaceholder": "vash@firma.ua",
    "supplier.submitEmail": "Отримувати запити безкоштовно",
    "supplier.noSpam": "Жодного спаму. Відписатися можна будь-коли.",
    "supplier.consent": "Надсилаючи, ви погоджуєтеся на отримання запитів на вашу електронну пошту та з умовами сервісу. Відписатися можна будь-коли.",
    "supplier.registerHint": "Хочете обрати, які запити отримувати, та оцінювати замовлення? Зареєструйтеся безкоштовно.",
    "supplier.preferencesTitle": "Які роботи вас цікавлять?",
    "supplier.preferencesSubtitle": "Оберіть типи замовлень, які хочете отримувати. Можна змінити будь-коли.",
    "supplier.savePreferences": "Завершити реєстрацію",
    "supplier.magicLinkSent": "E-mail зареєстровано!",
    "supplier.checkInbox": "Ми будемо надсилати вам відповідні запити від клієнтів на вашу пошту.",
    "supplier.detailsTitle": "Розкажіть про себе",
    "supplier.detailsSubtitle": "Ці дані необов'язкові, але допоможуть нам краще підбирати запити.",
    "supplier.namePlaceholder": "Ваше ім'я та прізвище",
    "supplier.phonePlaceholder": "Телефон, напр. +380 67 123 4567",
    "supplier.saveDetails": "Зберегти дані",
    "supplier.skipStep": "Пропустити",
    "supplier.loginTitle": "Вхід постачальника",
    "supplier.loginSubtitle": "Отримуйте безкоштовні запити на пошту.",
    "supplier.noAccount": "Немає облікового запису постачальника?",
    "supplier.register": "Зареєструватися",
    "supplier.backToApp": "Назад на cenik.ai",
    "supplier.registerTitle": "Реєстрація постачальника",
    "supplier.registerSubtitle": "Створіть обліковий запис і почніть оцінювати",
    "supplier.companyName": "Назва компанії",
    "supplier.companyPlaceholder": "Ваша компанія",
    "supplier.registerBtn": "Зареєструватися",
    "supplier.hasAccount": "Вже є обліковий запис?",
    "supplier.loginLink": "Увійти",
    "supplier.passwordMin": "Пароль повинен містити мінімум 8 символів",
    "supplier.checkEmail": "Перевірте свій e-mail для активації",
    "supplier.icoPlaceholder": "Код ЄДРПОУ (необов'язково)",
    "supplier.addressPlaceholder": "Місцезнаходження, напр. Київ",
    "supplier.fillCompany": "Вкажіть назву компанії",
    "supplier.becomePartnerTitle": "Стати партнером",
    "supplier.becomePartnerSubtitle": "Заповніть основні дані компанії.",
    "supplier.continueToPreferences": "Створити постачальника",
    "supplier.registrationDone": "Реєстрацію завершено! Ласкаво просимо.",
    "supplier.settingsTitle": "Налаштування",
    "supplier.tabCompany": "Компанія",
    "supplier.tabChatbot": "Чат-бот",
    "supplier.tabPricing": "Ціни",
    "supplier.incompleteProfile": "Будь ласка, заповніть інформацію про себе в налаштуваннях, щоб ваш чат-бот працював належним чином.",
    "supplier.goToSettings": "Перейти до налаштувань",
    "supplier.passwordPlaceholder": "Пароль (мін. 8 символів)",
    "supplier.dashboard": "Адмін постачальника",
    "supplier.logout": "Вийти",
    "supplier.productsTitle": "Мої продукти та ціни",
    "supplier.productsSubtitle": "Додайте продукти та встановіть ціну за м²",
    "supplier.addProduct": "Додати продукт",
    "supplier.editProduct": "Редагувати продукт",
    "supplier.newProduct": "Новий продукт",
    "supplier.productName": "Назва продукту",
    "supplier.productNamePlaceholder": "напр. Утеплення EPS 100",
    "supplier.pricePerM2": "Ціна за м² (Kč)",
    "supplier.productDescription": "Опис",
    "supplier.descriptionPlaceholder": "Короткий опис продукту...",
    "supplier.cancel": "Скасувати",
    "supplier.save": "Зберегти",
    "supplier.noProducts": "У вас ще немає продуктів",
    "supplier.addFirstProduct": "Натисніть 'Додати продукт' для початку",
    "supplier.productAdded": "Продукт додано",
    "supplier.productUpdated": "Продукт оновлено",
    "supplier.productDeleted": "Продукт видалено",
    "supplier.fillRequired": "Заповніть назву та ціну",
    "supplier.inquiriesTab": "Запити",
    "supplier.productsTab": "Продукти",
    "supplier.noInquiries": "Поки що запитів немає",
    "supplier.noInquiriesHint": "Нові запити з'являться автоматично",
    "supplier.alreadyPaid": "Пропозицію вже надіслано",
    "supplier.paymentError": "Помилка оплати, спробуйте ще раз",
    "supplier.loginRequired": "Для оплати потрібно увійти",
    "supplier.submitQuoteTitle": "Ваша цінова пропозиція",
    "supplier.submitQuoteHint": "Вкажіть орієнтовну ціну без ПДВ. Після надсилання та оплати 15 Kč пропозицію отримає замовник.",
    "supplier.quoteTotal": "Пропонована ціна (без ПДВ)",
    "supplier.quoteNotes": "Примітка",
    "supplier.quoteNotesPlaceholder": "Що включено в ціну, термін реалізації...",
    "supplier.submitQuoteBtn": "Надіслати пропозицію і оплатити 15 Kč",
    "supplier.quoteSaved": "Пропозицію збережено!",
    "supplier.quoteAlreadySent": "Пропозицію вже надіслано",
    "supplier.fillQuotePrice": "Вкажіть пропоновану ціну",
    "supplier.quoteSent": "Надіслано",
    "supplier.quoteSentConfirm": "Пропозицію надіслано замовнику",
    "supplier.quoteSentSuccess": "Пропозицію успішно надіслано!",
    "supplier.waitingCustomerApproval": "Чекаємо на схвалення замовником. Коли він проявить інтерес, вам відобразяться його контактні дані.",
    "supplier.customerApproved": "Замовник схвалив надання контакту",
    "supplier.contactVisibleNow": "Контактні дані замовника тепер видні вище.",
    "supplier.share": "Поділитися",
    "supplier.linkCopied": "Посилання скопійовано",
    "supplier.shareFailed": "Помилка при поділі",
    "supplier.aboutQuery": "Розкажіть мені більше про вашу компанію.",
    "supplier.aboutCompany": "Про компанію",
    "supplier.slugSaved": "Slug збережено",
    "supplier.slugTakenOrInvalid": "Slug вже зайнятий або недійсний",
    "supplier.connectionError": "Помилка з'єднання",
    "supplier.paymentNotVerified": "Оплата не підтверджена. Спробуйте оновити сторінку.",
    "supplier.paymentVerificationError": "Помилка при перевірці оплати.",
    "supplier.savingError": "Помилка при збереженні",
    "supplier.dataSaved": "Дані збережено",
    "supplier.edit": "редагувати",
    "supplier.onboardingTitle": "Як тут працюють запити для вашої компанії?",
    "supplier.onboardingStep1": "Налаштуйте прайс-лист та регіони, де хочете отримувати замовлення.",
    "supplier.onboardingStep2": "На основі цього ми будемо показувати вам нові запити.",
    "supplier.onboardingStep3": "Для обраних запитів надішліть розрахунок, і замовник побачить деталі вашої пропозиції.",
    "supplier.aiChatCalculator": "Ваш AI чат-калькулятор",
    "supplier.freeTestVersion": "Безкоштовно · тестова версія",
    "supplier.checking": "Перевіряю...",
    "supplier.slugAvailableLabel": "✓ Вільний",
    "supplier.slugTakenLabel": "✗ Зайнятий",
    "supplier.savingLabel": "Зберігаю...",
    "supplier.saveLabel": "Зберегти",
    "supplier.cancel": "Скасувати",
    "supplier.copyLink": "Копіювати посилання",
    "supplier.customDomainTag": "· власний домен",
    "supplier.customDomainLabel": "Власний домен: {domain}",
    "supplier.connectCustomDomain": "Підключити власний домен",
    "supplier.enterDomainLabel": "Введіть свій домен (напр. {example}):",
    "supplier.connectButton": "Підключити",
    "supplier.verifiedLabel": "Перевірено ✓",
    "supplier.awaitingDns": "Очікування DNS",
    "supplier.dnsStep1Title": "1. Налаштуйте DNS-записи у вашого реєстратора",
    "supplier.dnsStep1Instruction": "Увійдіть до управління доменом і додайте ці записи:",
    "supplier.mainDomainLabel": "Для основного домену (напр. {example}):",
    "supplier.dnsTypeHeader": "Тип",
    "supplier.dnsNameHeader": "Назва",
    "supplier.dnsValueHeader": "Значення",
    "supplier.subdomainLabel": "Для субдомену (напр. {example}):",
    "supplier.subdomainHint": "У субдомені замініть {sub} вашою назвою (напр. {ex1}, {ex2}). CNAME-запис рекомендований для субдоменів.",
    "supplier.dnsStep2Title": "2. Додайте верифікаційний запис",
    "supplier.dnsStep2Instruction": "Цей запис підтверджує, що домен належить вам:",
    "supplier.dnsSettingsTitle": "Де знайти налаштування DNS?",
    "supplier.dnsForpsi": "Адміністрація → Домени → ваш домен → DNS-записи",
    "supplier.dnsActive24": "Деталі домену → Управління DNS → Додати запис",
    "supplier.dnsWedos": "Управління DNS → Додати запис (тип A або TXT)",
    "supplier.dnsCloudflare": "DNS → Records → Add record",
    "supplier.dnsPropagationHint": "Зміна DNS набуде чинності протягом 5 хвилин – 24 годин. Після налаштування натисніть «Перевірити DNS».",
    "supplier.verifyDns": "Перевірити DNS",
    "supplier.disconnectDomain": "Відключити домен",
    "supplier.chatbotSettingsLabel": "Налаштування чат-бота та прайс-листів",
    "supplier.noPriceItemsWarning": "Без налаштованого прайс-листа ваша компанія не відображається в пропозиціях.",
    "supplier.setPriceList": "Налаштувати прайс-лист →",
    "supplier.readiness.setupCta": "Налаштування чат-бота та прайс-листа",
    "supplier.readiness.companyInfo": "Інформація про компанію",
    "supplier.readiness.pricing": "Прайс-лист",
    "supplier.readiness.done": "Готово",
    "supplier.readiness.missing": "Відсутнє",
    "supplier.readiness.warningText": "Щоб ваш чат-бот був публічно доступний, необхідно заповнити інформацію про компанію та налаштувати прайс-лист.",
    "supplier.readiness.readyText": "Ваш чат-бот готовий до публічного показу.",
    "supplier.readiness.fallbackTitle": "Чат-бот поки що недоступний",
    "supplier.readiness.fallbackDescription": "Цей чат-бот зараз готується. Спробуйте пізніше.",
    "supplier.ownChatbot": "Власний чат-бот",
    "supplier.marketplaceInquiries": "Запити з cenik.ai",
    "supplier.filterNew": "Нові",
    "supplier.filterQuoteSent": "Пропозицію надіслано",
    "supplier.filterHidden": "Приховані",
    "supplier.chatbotBadge": "Чат-бот",
    "supplier.marketplaceBadge": "cenik.ai",
    "supplier.requestMaterial": "Запит матеріалу",
    "supplier.quoteAction": "Розрахувати",
    "supplier.restore": "Відновити",
    "supplier.hideAction": "Приховати",
    "supplier.marketplaceUpsellTitle": "Запити з cenik.ai marketplace",
    "supplier.marketplaceUpsellDescription": "Отримайте доступ до запитів з усього marketplace з Premium-планом",
    "supplier.premiumLabel": "Premium",
    "supplier.approved": "Схвалено",
    "customer.allowContact": "Хочу, щоб постачальник зв'язався зі мною",
    "customer.approved": "Контакт надано",
    "customer.contactShared": "Постачальник тепер бачить ваші контактні дані",
    "customer.supplierContact": "Контакт постачальника:",

    "filter.title": "Фільтр запитів",
    "filter.workType": "Тип роботи",
    "filter.allTypes": "Усі типи",
    "filter.location": "Місце будівництва",
    "filter.locationPlaceholder": "напр. Київ, Львів...",
    "filter.priceFrom": "Ціна від (грн)",
    "filter.priceTo": "Ціна до (грн)",
    "filter.deadline": "Термін виконання",
    "filter.deadlinePlaceholder": "напр. травень 2026, терміново...",
    "filter.lock": "Зафіксувати фільтр",
    "filter.apply": "Показати результати",
    "filter.save": "Зберегти й отримувати сповіщення",
    "filter.unlock": "Розблокувати фільтр",
    "filter.locked": "Зафіксовано",
    "filter.wholeCountry": "Вся Чеська Республіка",
    "filter.reset": "Скинути",
    "filter.lockedHint": "Фільтр зафіксовано – нові запити, що відповідають вашим критеріям, надходитимуть у сповіщення та на e-mail. Для зміни спочатку розблокуйте.",
    "filter.noResults": "Жодних запитів за фільтром",
    "filter.noResultsHint": "Спробуйте змінити або скинути фільтр",
    "filter.searchCity": "Шукати місто...",
    "filter.radius": "Радіус",
    "filter.addWholeCZ": "+ Вся Чеська Республіка",
    "filter.wholeCZ": "Вся Чеська Республіка",
    "filter.noLocationsSet": "Локації не встановлені",
    "filter.searching": "Шукаю",
    "filter.locationAreas": "Області діяльності",
    "filter.locationSaved": "Області збережені",

    "register.checkEmail": "Перевірте e-mail для активації",
    "register.emailSent": "E-mail надіслано!",
    "register.checkEmailDesc": "Перевірте свою поштову скриньку та натисніть посилання для підтвердження.",
    "register.goToLogin": "Перейти до входу",
    "profile.title": "Налаштування профілю",
    "profile.personalInfo": "Особисті дані",
    "profile.fullName": "Повне ім'я",
    "profile.fullNamePlaceholder": "Іван Петренко",
    "profile.phone": "Телефон",
    "profile.phonePlaceholder": "+380 12 345 6789",
    "profile.save": "Зберегти",
    "profile.saved": "Профіль збережено",
    "profile.supplierPanel": "Панель постачальника",
    "profile.sitesTitle": "Проєкти",
    "profile.sitesSubtitle": "Керуйте своїми проєктами та будівництвами",
    "profile.siteNamePlaceholder": "Назва проєкту",
    "profile.siteAddressPlaceholder": "Адреса проєкту",
    "profile.addSite": "Зберегти проєкт",
    "profile.siteAdded": "Проєкт додано",
    "profile.siteDeleted": "Проєкт видалено",
    "profile.siteSaved": "Проєкт збережено",
    "sidebar.settings": "Налаштування",
    "sidebar.logout": "Вийти",
    "sidebar.aboutUs": "Про нас",
    "sidebar.assignTo": "Призначити до проєкту",
    "sidebar.unassign": "Видалити з проєкту",
    "sidebar.deleteChat": "Видалити чат",
    "sidebar.chatDeleted": "Чат видалено",
    "sidebar.chatAssigned": "Чат призначено",
    "sidebar.noProjectsYet": "Проєктів поки немає",
    "sidebar.noSearchResults": "Нічого не знайдено",
    "sidebar.history": "Історія",
    "sidebar.inquiriesAndQuotes": "Запити та пропозиції",
    "sidebar.noInquiriesYet": "Запитів поки немає",
    "sidebar.quote": "пропозиція",
    "sidebar.quotes": "пропозиції",
    "sidebar.backToChat": "Назад до чату",
    "sidebar.aboutUsResponse": "## Про cenik.ai 👋\n\nМи тут, щоб **змінити спосіб ціноутворення та запитів у будівництві**.\n\nМи віримо, що отримати справедливу ціну за фасад, утеплення чи іншу будівельну роботу має бути просто – як для тих, **хто замовляє**, так і для тих, **хто виконує роботу**.\n\nЗа проектом стоїть **Adam Černohous, IČO: 76535398**, із місцем реєстрації Třebovská 65, Ústí nad Orlicí 56203.\n\n📧 Контакт: **info@cenik.ai**",
    "dashboard.newProject": "Новий проєкт",
    "notifications.title": "Сповіщення",
    "notifications.empty": "Немає сповіщень",
    "notifications.newInquiry": "Новий запит",
    "notifications.loginPrompt": "Тут будуть відображатися ваші пропозиції від постачальників. Увійдіть для перегляду.",

    // Customer dashboard
    "customerDashboard.title": "Мої запити",
    "customerDashboard.newInquiry": "Новий запит",
    "customerDashboard.onboardingTitle": "Як працює cenik.ai для вас?",
    "customerDashboard.onboardingStep1": "Запитайте в чаті оцінку вартості утеплення або фасаду.",
    "customerDashboard.onboardingStep2": "Чат створить запит з ваших відповідей.",
    "customerDashboard.onboardingStep3": "Запит надішлемо компаніям з вашого регіону, і їхні пропозиції з'являться тут.",
    "customerDashboard.emptyTitle": "У вас поки немає запитів",
    "customerDashboard.emptyDesc": "Відкрийте чат на головній сторінці та отримайте першу оцінку.",
    "customerDashboard.statusOpen": "Відкритий",
    "customerDashboard.statusInProgress": "Комунікація",
    "customerDashboard.statusClosed": "Закритий",
    "customerDashboard.statusCompleted": "Реалізовано",
    "customerDashboard.quote": "пропозиція",
    "customerDashboard.quotes": "пропозиції",
    "customerDashboard.quotesMany": "пропозицій",

    // Supplier chat
    "supplierChat.newChat": "Новий чат",
    "supplierChat.inquiry": "Запит",
    "supplierChat.message": "Повідомлення",
    "supplierChat.upsellTitle": "Хочете власний чат-бот?",
    "supplierChat.upsellDesc": "Отримайте AI асистента для вашої компанії з власними цінами та брендингом.",
    "supplierChat.upsellLink": "Дізнатися більше →",
    "supplierChat.aboutUs": "Про нас",
    "supplierChat.msgSavedNotifyFailed": "Повідомлення збережено, але сповіщення компанії не вдалося надіслати.",
    "supplierChat.msgDeliveryFailed": "Не вдалося доставити повідомлення компанії. Спробуйте ще раз.",
    "supplierChat.inquiryDeliveryFailed": "Не вдалося доставити запит компанії. Спробуйте ще раз.",
    "supplierChat.error": "Помилка: {error}",
    "supplierChat.chatNotFound": "AI чат не знайдено",
    "supplierChat.domainNotLinked": "Цей домен поки не пов'язаний з жодним прайс-листом.",
    "supplierChat.slugNotFound": "не існує.",
    "supplierChat.backToHome": "← Назад на головну",
    "supplierChat.askAboutCompany": "Розкажіть мені більше про вашу компанію.",
    "supplierChat.inputPlaceholder": "Напишіть запитання або параметри…",
    "supplierChat.aboutCompany": "Про компанію {name}",
    "supplierChat.specialization": "Спеціалізація",
    "supplierChat.serviceArea": "Область діяльності",
    "supplierChat.aiAssistantHelp": "Що допоможе дізнатися AI асистент?",
    "supplierChat.aiHelp1": "Орієнтовну вартість будівельних робіт",
    "supplierChat.aiHelp2": "Доступність та типи матеріалів",
    "supplierChat.aiHelp3": "Відповіді на фахові питання",
    "supplierChat.aiHelp4": "Попередній розрахунок на основі ваших параметрів",
    "supplierChat.faq": "Часті питання",
    "supplierChat.contact": "Контакт",

    // Sidebar extras
    "sidebar.currentChat": "Поточний чат",
    "sidebar.messageCount": "повідомлень",

    // Supplier settings
    "supplierSettings.companyName": "Назва компанії *",
    "supplierSettings.ico": "Код ЄДРПОУ",
    "supplierSettings.contactEmail": "Контактний e-mail",
    "supplierSettings.contactPhone": "Контактний телефон",
    "supplierSettings.region": "Регіон",
    "supplierSettings.regionPlaceholder": "Напр. Київ, Львівська область",
    "supplierSettings.description": "Опис компанії",
    "supplierSettings.descriptionPlaceholder": "Короткий опис вашої компанії...",
    "supplierSettings.saving": "Зберігаю...",
    "supplierSettings.saveCompany": "Зберегти дані компанії",
    "supplierSettings.savedCompany": "Дані компанії збережено",
    "supplierSettings.saveError": "Помилка збереження",
    "supplierSettings.saveFailed": "Не вдалося зберегти",
    "supplierSettings.chatbotDesc": "Налаштуйте, як ваш чат-бот представлятиметься клієнтам. У безкоштовній версії бот не надає контактні дані.",
    "supplierSettings.botName": "Ім'я чат-бота",
    "supplierSettings.botNameHint": "Якщо не заповните, бот матиме назву вашої компанії.",
    "supplierSettings.botIntro": "Як бот представляється",
    "supplierSettings.botIntroPlaceholder": "Я AI асистент компанії XY, допоможу вам з питаннями про фасади та утеплення.",
    "supplierSettings.aboutCompany": "Про компанію",
    "supplierSettings.aboutCompanyPlaceholder": "Коротка інформація про компанію, спеціалізацію, досвід, регіон діяльності...",
    "supplierSettings.faq": "FAQ – часті питання та відповіді",
    "supplierSettings.faqPlaceholder": "Питання: Які матеріали використовуєте?\nВідповідь: EPS, мінеральна вата, ETICS системи...\n\nПитання: Яка ваша ціна за м²?\nВідповідь: Ціна залежить від обсягу робіт, зазвичай...",
    "supplierSettings.saveBot": "Зберегти налаштування",
    "supplierSettings.savedBot": "Налаштування чат-бота збережено.",
    "supplierSettings.saveBotFailed": "Не вдалося зберегти налаштування.",
    "supplierSettings.tabServices": "Послуги та ціни",
    "supplierSettings.tabPublicPage": "Публічна сторінка",
    "supplierSettings.publicPageDesc": "Налаштуйте, як ваша публічна AI сторінка відображатиметься в пошукових системах та соціальних мережах.",
    "supplierSettings.tagline": "Короткий слоган",
    "supplierSettings.taglinePlaceholder": "Напр. Фасадні роботи під ключ у Києві",
    "supplierSettings.taglineHint": "Відображається під назвою компанії на публічній сторінці.",
    "supplierSettings.businessType": "Галузь / тип послуги",
    "supplierSettings.businessTypePlaceholder": "Фасадні роботи",
    "supplierSettings.serviceArea": "Область діяльності",
    "supplierSettings.serviceAreaPlaceholder": "Київ та Київська область",
    "supplierSettings.seoTitle": "SEO заголовок сторінки",
    "supplierSettings.seoTitleHint": "Відображається у вкладці браузера та в результатах пошуку.",
    "supplierSettings.seoDesc": "SEO опис сторінки",
    "supplierSettings.seoDescHint": "Відображається під заголовком у результатах пошуку.",
    "supplierSettings.ogImage": "OG image URL (необов'язково)",
    "supplierSettings.ogImageHint": "Зображення при поширенні в соціальних мережах. Ідеально 1200×630 px.",
    "supplierSettings.searchPreview": "Попередній перегляд у пошуку",
    "supplierSettings.savePublicPage": "Зберегти налаштування сторінки",
    "supplierSettings.savedPublicPage": "Налаштування публічної сторінки збережено.",
    "supplierSettings.servicesHeading": "Які послуги ви пропонуєте?",
    "supplierSettings.servicesDesc": "Оберіть послуги, які надає ваша компанія. Для кожної можна додати опис та ціни.",
    "supplierSettings.addCustomService": "Додати власну послугу…",
    "supplierSettings.addButton": "Додати",
    "supplierSettings.serviceAlreadyAdded": "Ця послуга вже додана",
    "supplierSettings.serviceAdded": "Послугу «{name}» додано",
    "supplierSettings.selectServiceFirst": "Оберіть хоча б одну послугу вище, потім додайте опис та ціни.",
    "supplierSettings.customerMessages": "Повідомлення від клієнтів",
    "supplierSettings.translate": "Перекласти",
    "supplierSettings.showOriginal": "Показати оригінал",

    // Admin
    "admin.title": "Панель адміністратора",
    "admin.loginPrompt": "Увійдіть адміністраторським обліковим записом",
    "admin.loginButton": "Увійти",
    "admin.email": "Електронна пошта",
    "admin.password": "Пароль",
    "admin.googleLogin": "Увійти через Google",
    "admin.orPassword": "або паролем",
    "admin.noPermission": "Цей обліковий запис не має адміністраторських прав",
    "admin.loginFailed": "Вхід не вдався",
    "admin.googleLoginFailed": "Вхід через Google не вдався",
    "admin.sessionExpired": "Сесія закінчилася, увійдіть знову",
    "admin.registrationFailed": "Реєстрація не вдалась",
    "admin.accountCreated": "Адмін-акаунт створено та виконано вхід",
    "admin.loginExpired": "Вхід закінчився, увійдіть знову",
    "admin.tab.chats": "Чати",
    "admin.tab.inquiries": "Запити",
    "admin.tab.users": "Користувачі",
    "admin.tab.prices": "Ціни",
    "admin.tab.services": "Послуги",
    "admin.tab.subscriptions": "Підписки",
    "admin.tab.feedback": "Відгуки",
    "admin.tab.knowledge": "Знання",
    "admin.toast.saved": "Збережено",
    "admin.toast.deleted": "Видалено",
    "admin.toast.saveFailed": "Не вдалося зберегти",
    "admin.toast.deleteFailed": "Не вдалося видалити",
    "admin.toast.loadFailed": "Не вдалося завантажити дані",
    "admin.toast.statusUpdated": "Статус оновлено",
    "admin.toast.activated": "Активовано",
    "admin.toast.deactivated": "Деактивовано",
    "admin.toast.correctionSaved": "Виправлення збережено",
    "admin.toast.correctionSaveFailed": "Не вдалося зберегти виправлення",
    "admin.toast.feedbackIgnored": "Відгук відхилено",
    "admin.toast.feedbackIgnoreFailed": "Не вдалося відхилити відгук",
    "admin.toast.knowledgeAdded": "Знання додано",
    "admin.toast.knowledgeSaved": "Знання збережено",
    "admin.toast.aiCategorized": "AI запропонував категорію та назву",
    "admin.toast.serviceCreated": "Послугу створено",
    "admin.toast.createServiceFailed": "Не вдалося створити послугу",
    "admin.toast.serviceActivated": "Послугу активовано",
    "admin.toast.serviceRejected": "Послугу відхилено",
    "admin.toast.movedToQueue": "Переміщено до черги",
    "admin.toast.movedToCandidate": "Переміщено до кандидатів",
    "admin.toast.serviceAssigned": "Послугу призначено",
    "admin.toast.newServiceProposed": "Нову послугу запропоновано та призначено",
    "admin.toast.aiNoMatch": "AI не знайшов відповідну послугу",
    "admin.toast.detectionFixed": "Виявлення виправлено + навчання створено",
    "admin.toast.detectionFixFailed": "Не вдалося виправити виявлення",
    "admin.toast.detectionRejected": "Виявлення відхилено",
    "admin.toast.detectionRejectFailed": "Не вдалося відхилити",
    "admin.toast.overrideSaved": "Перевизначення збережено",
    "admin.toast.overrideSaveFailed": "Не вдалося зберегти перевизначення",
    "admin.toast.planChangeFailed": "Не вдалося змінити план",
    "admin.toast.featureToggleFailed": "Не вдалося змінити стан функції",
    "admin.toast.fillTopicContent": "Заповніть тему та вміст",
    "admin.toast.fillNameSlug": "Заповніть назву та slug",
    "admin.toast.invalidSlug": "Slug може містити лише малі літери, цифри та дефіси",
    "admin.toast.learningAdded": "Навчання додано – AI почне його використовувати негайно",
    "admin.toast.addFailed": "Не вдалося додати",
    "admin.toast.inquiryDeleted": "Запит видалено",
    "admin.toast.writeCorrectedAnswer": "Напишіть виправлену відповідь",
    "admin.toast.serviceSaved": "Послугу збережено",
    "admin.toast.generateFailed": "Генерування не вдалося",
    "admin.toast.formApproved": "Форму схвалено — постачальники її побачать",
    "admin.toast.approveFailed": "Не вдалося схвалити",
    "admin.toast.formToDraft": "Форму переведено у чернетку",
    "admin.toast.operationFailed": "Не вдалося",
    "admin.toast.keywordApproved": "Ключове слово схвалено",
    "admin.toast.keywordRejected": "Ключове слово відхилено",
    "admin.toast.keywordAdded": "Ключове слово додано",
    "admin.toast.keywordDeleted": "Ключове слово видалено",
    "admin.status.active": "Активний",
    "admin.status.inactive": "Неактивний",
    "admin.status.candidate": "Кандидат",
    "admin.status.queued": "У черзі",
    "admin.status.rejected": "Відхилено",
    "admin.status.positive": "Позитивний",
    "admin.status.negative": "Негативний",
    "admin.empty.inquiries": "Немає запитів",
    "admin.empty.suppliers": "Немає постачальників",
    "admin.empty.customers": "Немає клієнтів",
    "admin.empty.feedback": "Поки що немає відгуків від користувачів",
    "admin.confirm.deleteInquiry": "Дійсно видалити цей запит?",
    "admin.confirm.deleteLearning": "Дійсно видалити це навчання?",
    "admin.btn.approve": "Схвалити",
    "admin.btn.reject": "Відхилити",
    "admin.btn.addItem": "Додати елемент",
    "admin.btn.saveDraft": "Зберегти чернетку",
    "admin.btn.approveAndDeploy": "Схвалити та розгорнути",
    "admin.btn.regenerate": "Перегенерувати",
    "admin.btn.editForm": "Редагувати форму",
    "admin.btn.collapse": "Згорнути",
    "admin.btn.showAll": "Показати все",
    "admin.btn.ignore": "Відхилити",
    "admin.btn.save": "Зберегти",
    "admin.btn.cancel": "Скасувати",
    "admin.btn.addKnowledge": "Додати знання для",
    "admin.btn.restoreCandidate": "Відновити як кандидата",
    "admin.btn.removeOverride": "Видалити перевизначення",
    "admin.btn.disable": "Вимкнути",
    "admin.btn.enable": "Увімкнути",
    "admin.section.quoteForm": "Форма пропозиції для постачальників",
    "admin.section.noQuoteForm": "Поки немає форми пропозиції. AI запропонує бюджетні позиції для цієї послуги.",
    "admin.section.costItems": "Статті витрат",
    "admin.section.formPreview": "Попередній перегляд форми для постачальників",
    "admin.section.serviceStatus": "Статус послуги",
    "admin.section.feedbackHelp": "Оцінки відповідей AI. Негативний відгук з виправленням негайно застосовується як навчання AI. Відхиліть підозрілі оцінки.",
    "admin.section.totalRatings": "Усього оцінок",
    "admin.section.aiKnowledge": "AI використовує ці знання при відповідях клієнтам та генеруванні пропозицій для цієї послуги.",
    "admin.placeholder.knowledgeName": "Назва знання",
    "admin.placeholder.knowledgeContent": "Вставте текст про утеплення, ціни, матеріали... AI сам запропонує назву та категорію.",
    "admin.placeholder.learningContent": "Вміст знання — що має знати AI та як поводитися...",
    "admin.placeholder.itemName": "Назва елемента",
    "admin.placeholder.keyword": "Нове ключове слово або фраза...",
    "admin.placeholder.serviceDesc": "Короткий опис послуги",
    "admin.placeholder.priceName": "напр. Ціни EPS",
    "admin.placeholder.newServiceName": "Назва нової послуги:",
    "admin.label.totalPrice": "Загальна ціна",
    "admin.label.currency": "Kč",
    "admin.label.records": "записів",
    "admin.label.offer": "пропозиція",
    "admin.label.offers": "пропозиції",
    "admin.label.offersMany": "пропозицій",
    "admin.label.inquirySent": "Запит ✓",
    "admin.label.inquiryNotSent": "Без запиту",
    "admin.label.formDraft": "📝 Чернетка",
    "admin.label.formApproved": "✅ Форма",
    "admin.label.user": "👤 Користувач",
    "admin.label.ai": "🤖 AI",
    "admin.label.advancedCalc": "Розширений розрахунок",
    "admin.label.weight": "Вага",
    "admin.label.serviceNotFound": "Послугу не знайдено",
    "admin.label.generating": "Генерую…",
    "admin.label.generateForm": "🤖 Згенерувати форму пропозиції",
    "admin.label.analyzing": "Аналізую...",
    "admin.label.aiCategorize": "🤖 AI категоризує",
    "admin.label.newServiceProposal": "Пропозиція нової послуги",
    "admin.label.uncertainMatch": "Непевний збіг",
    "admin.label.match": "Збіг",
    "admin.label.new": "Новий",
    "admin.label.uncertain": "Непевний",
    "admin.filter.all": "Усі",
    "admin.filter.sent": "Запит надіслано",
    "admin.filter.notSent": "Не надіслано",
    "admin.module.walls": "Стіни",
    "admin.module.material": "Запит матеріалу",
    "admin.module.quoteCalc": "Розрахунок пропозиції",
    "admin.module.generalInquiry": "Загальний запит",
    "admin.module.simpleQuote": "Проста пропозиція",
    "admin.material.epsWhite": "EPS білий",
    "admin.material.epsGrey": "EPS сірий",
    "admin.material.mineralWool": "Мінеральна вата",
    "admin.material.ventilatedFacade": "Вентильована фасада",
    "admin.section.basicInfo": "Основна інформація",
    "admin.section.keywordsDetection": "Ключові слова (виявлення)",
    "admin.section.keywordsHelp": "Схвалені ключові слова використовуються для гібридного виявлення послуг. Запропоновані чекають на перевірку.",
    "admin.section.modules": "Модулі",
    "admin.section.knowledgeFor": "Знання AI для",
    "admin.label.name": "Назва",
    "admin.label.slugUrl": "Slug (URL)",
    "admin.label.keywordsComma": "Ключові слова (через кому, для пошуку)",
    "admin.label.descOptional": "Опис (необов'язковий)",
    "admin.label.approved": "Схвалені",
    "admin.label.suggested": "Запропоновані",
    "admin.label.rejectedKw": "Відхилені",
    "admin.label.profit": "Прибуток",
    "admin.label.vat": "ПДВ",
    "admin.label.unitLabel": "Одиниця:",
    "admin.label.vatRate": "Ставка ПДВ (12 % / 21 %)",
    "admin.label.query1": "запит",
    "admin.label.query2to4": "запити",
    "admin.label.query5plus": "запитів",
    "admin.label.record1": "запис",
    "admin.label.record2to4": "записи",
    "admin.label.learningActive": "✓ Активне",
    "admin.label.learningInactive": "○ Неактивне",
    "admin.label.formApprovedBadge": "✅ Схвалено",
    "admin.label.formDraftBadge": "📝 Чернетка",
    "admin.label.facadeNote": "ℹ️ Фасадні роботи мають вбудовану форму (матеріал, риштування, робота, доставка). Якщо згенеруєте нову, вона замінить вбудовану.",
    "admin.label.fact": "📚 Факт",
    "admin.label.correction": "🔧 Виправлення",
    "admin.label.tip": "💡 Порада",
    "admin.btn.refresh": "Оновити",
    "admin.btn.backToOverview": "← Назад до огляду",
    "admin.btn.add": "Додати",
    "admin.btn.activate": "Активувати",
    "admin.btn.toQueue": "До черги",
    "admin.placeholder.keywordsExample": "фасад, утеплення, eps, полістирол",
    "admin.placeholder.learningTopic": "Тема (напр. Ціна SDK перегородки)",
    "admin.toast.aiSuggestedForm": "AI запропонувала форму — перевірте та схваліть",
    "admin.toast.error": "Помилка",
    "admin.module.wallsDesc": "Клієнт задає стіни з розмірами та отворами. Постачальник бачить детальний розбір площ.",
    "admin.module.materialDesc": "Постачальник запитує матеріал у оптовиків прямо із запиту.",
    "admin.module.quoteCalcDesc": "Постатейний бюджет з цінами за м². Генеративна AI пропозиція для клієнта.",
    "admin.module.generalInquiryDesc": "AI-генерований опис запиту з чату.",
    "admin.module.simpleQuoteDesc": "Постачальник вводить загальну ціну + примітки.",
  },

  sk: {
    "topbar.login": "Prihlásiť sa",
    "topbar.register": "Zaregistruj sa zadarmo",
    "topbar.comingSoon": "Ďalšie pripravujeme",
    "topbar.facades": "Fasádnické práce",
    "landing.title": "Koľko stojí vaša stavba?",
    "landing.subtitle": "Vyberte typ stavebnej práce a zistite orientačnú cenu za minútu.",
    "landing.service.fasady": "Fasádnické práce",
    "landing.service.fasady.desc": "Orientačná cena fasády, zateplenia a omietok",
    "service.fasadnicke-prace": "Fasádnické práce",
    "landing.service.strechy": "Strechy",
    "landing.service.okna": "Okná a dvere",
    "landing.startChat": "Zistiť cenu",
    "landing.comingSoon": "Pripravujeme",
    "index.headline1": "Koľko stojí vaša stavba?",
    "index.headline2": "Spočítajte si cenu stavebných prác",
    "index.headline3": "Zistite orientačnú cenu online",
    "index.headline4": "Naceňte si stavebné práce",
    "index.headline5": "Orientačná cena za minútu",
    "index.disclaimer": "Ak pošleš správu službe cenik.ai, chatbotovi založenému na umelej inteligencii, vyjadruješ súhlas s našimi",
    "index.terms": "zmluvnými podmienkami",
    "index.privacyConfirm": "a potvrdzuješ, že máš prečítané naše",
    "index.privacy": "zásady ochrany súkromia",
    "index.cookiesSee": "Viď",
    "index.cookies": "predvoľby cookies",
    "cookie.message": "Tento web používa cookies na zabezpečenie správneho fungovania a analýzu návštevnosti.",
    "cookie.accept": "Prijať všetko",
    "cookie.reject": "Odmietnuť voliteľné",
    "cookie.settings": "Nastavenia",
    "index.sensitiveWarning": "Nezdieľaj citlivé osobné údaje (rodné číslo, heslá, čísla účtov).",
    "index.nextQuestion": "Napíšte ďalšiu otázku...",
    "index.mayMakeErrors": "cenik.ai môže robiť chyby. Overte si dôležité informácie.",
    "index.leadSaved": "Kontaktné údaje odoslané – čoskoro sa ozveme! 🏠",
    "sidebar.newChat": "Nový chat",
    "sidebar.searchChats": "Vyhľadať chat",
    "sidebar.projects": "Projekty",
    "sidebar.howToUse": "Ako to funguje",
    "sidebar.howToUseQuery": "Ako to funguje?",
    "sidebar.howToUseResponse": "Vitajte v **cenik.ai** 👋\n\nFunguje to jednoducho:\n\n1. **Napíšete mi, čo potrebujete** – typ stavebných prác, rozmery, materiál.\n2. **AI vám spočíta orientačnú cenu** – na základe aktuálnych trhových dát.\n3. **Môžete sa dopytovať** – na materiály, technológie, alebo porovnať varianty.\n4. **Ak chcete nezáväznú ponuku**, zanecháte kontakt a spojíme vás s overenými dodávateľmi.\n\n💡 **Tip:** Môžete prejsť na **kalkulačku** a vyplniť si všetko sám.\n\nZačnite tým, že popíšete, čo potrebujete naceniť 🏠",
    "sidebar.yourChats": "História chatov",
    "sidebar.noChatsYet": "Zatiaľ žiadne chaty",
    "sidebar.loginForHistory": "Pre zobrazenie histórie chatov sa prihláste.",
    "sidebar.loginLink": "prihláste",
    "sidebar.loginForFeature": "Pre použitie tejto funkcie sa prihláste.",
    "sidebar.loginForInquiries": "Pre zobrazenie dopytov a ponúk sa prihláste.",
    "sidebar.becomeSupplier": "Chcem vlastný chatbot",
    "sidebar.supplierDashboard": "Panel dodávateľa",
    "sidebar.supplierCta": "Chcete dostávať zadarmo dopyty do Vášho e-mailu?",
    "sidebar.login": "Prihlásiť sa",
    "chat.placeholder1": "Čo potrebujete naceniť?",
    "chat.placeholder2": "Aké sú rozmery alebo plocha?",
    "chat.placeholder3": "Aký materiál preferujete?",
    "chat.placeholder4": "Kde sa stavba nachádza?",
    "chat.placeholder5": "Máte otázky k stavebným prácam?",
    "chat.placeholderMobile1": "Čo potrebujete naceniť?",
    "chat.placeholderMobile2": "Aké sú rozmery?",
    "chat.placeholderMobile3": "Aký materiál?",
    "chat.placeholderMobile4": "Kde je stavba?",
    "chat.placeholderMobile5": "Otázky k stavbe?",
    "chat.takePhoto": "Odfotiť",
    "chat.addPhotos": "Pridať fotografie",
    "chat.uploadFiles": "Nahrať súbory",
    "chat.fileTooLarge": "Súbor \"{name}\" je väčší ako 10 MB.",
    "chat.assignToProject": "Zaradiť do projektu",
    "chat.unassign": "Odobrať z projektu",
    "chat.deleteChat": "Zmazať chat",
    "chat.loginToSave": "Prihlásiť sa a uložiť",
    "suggestion.facadePrice": "Spočítaj cenu fasády",
    "suggestion.insulationCost": "Koľko stoja stavebné práce?",
    "suggestion.bestPolystyrene": "Aký materiál je najlepší?",
    "suggestion.contactInsulation": "Zistiť orientačnú cenu",
    "login.title": "Prihlásiť sa",
    "login.email": "Email",
    "login.emailPlaceholder": "vas@email.sk",
    "login.password": "Heslo",
    "login.passwordPlaceholder": "••••••••",
    "login.submit": "Prihlásiť sa",
    "login.or": "alebo",
    "login.google": "Pokračovať cez Google",
    "login.noAccount": "Nemáte účet?",
    "login.register": "Zaregistrujte sa",
    "login.forgotPassword": "Zabudli ste heslo?",
    "login.subtitle": "Zadajte e-mail a heslo pre prihlásenie.",
    "login.chatbotBoxTitle": "Máte firmu?",
    "login.chatbotBoxDesc": "Vytvorte si vlastný AI chatbot pre vašich zákazníkov — zadarmo.",
    "login.becomeSupplier": "Chcem vlastný chatbot",
    "forgotPassword.title": "Obnovenie hesla",
    "forgotPassword.description": "Zadajte svoj e-mail a my vám pošleme odkaz na obnovenie hesla.",
    "forgotPassword.submit": "Odoslať odkaz",
    "forgotPassword.sent": "Odkaz odoslaný!",
    "forgotPassword.sentDesc": "Skontrolujte svoju e-mailovú schránku a kliknite na odkaz na obnovenie hesla.",
    "forgotPassword.backToLogin": "Späť na prihlásenie",
    "resetPassword.title": "Nové heslo",
    "resetPassword.password": "Nové heslo",
    "resetPassword.passwordPlaceholder": "Minimálne 8 znakov",
    "resetPassword.submit": "Nastaviť heslo",
    "resetPassword.success": "Heslo bolo zmenené",
    "login.info": "Prihláste sa pomocou e-mailu a hesla alebo cez Google. Po prihlásení budete mať prístup k histórii chatov a uloženým projektom.",
    "register.info": "Vytvorte si bezplatný účet pre ukladanie chatov, správu projektov a stavieb. Stačí e-mail a heslo alebo Google účet.",
    "forgotPassword.info": "Zadajte e-mail, na ktorý ste sa registrovali. Pošleme vám odkaz na nastavenie nového hesla.",
    "resetPassword.info": "Zadajte nové heslo (minimálne 8 znakov). Po uložení budete presmerovaní na prihlásenie.",
    "supplier.loginInfo": "Zaregistrujte sa ako dodávateľ a dostávajte zadarmo dopyty z vášho regiónu. Stačí zadať e-mail – žiadne heslo, žiadne poplatky.",
    "supplier.registerInfo": "Vytvorte si dodávateľský účet pre správu cenníka a prijímanie dopytov. Zadajte názov firmy, e-mail a heslo.",
    "auth.continue": "Pokračovať",
    "auth.invalidCredentials": "Nesprávny e-mail alebo heslo",
    "auth.alreadyRegistered": "Tento e-mail je už registrovaný. Prihláste sa.",
    "auth.switchToRegister": "Nemáte účet? Zaregistrujte sa",
    "auth.switchToLogin": "Už máte účet? Prihláste sa",
    "auth.forgotPassword": "Zabudli ste heslo?",
    "auth.backToLogin": "← Späť na prihlásenie",
    "register.title": "Vytvoriť účet zadarmo",
    "register.subtitle": "Vytvorte si nový účet — stačí e-mail a heslo.",
    "register.email": "Email",
    "register.emailPlaceholder": "vas@email.sk",
    "register.password": "Heslo",
    "register.passwordPlaceholder": "Minimálne 8 znakov",
    "register.submit": "Zaregistrovať sa",
    "register.or": "alebo",
    "register.google": "Pokračovať cez Google",
    "register.hasAccount": "Už máte účet?",
    "register.login": "Prihláste sa",
    "dashboard.pricing": "Cenník",
    "dashboard.emptyTitle": "S čím ti môžem pomôcť?",
    "dashboard.emptySubtitle": "Napíš svoju požiadavku alebo otázku",
    "dashboard.inputPlaceholder": "Čo dopytúješ dnes?",
    "pricing.title": "Jednoduchý cenník",
    "pricing.subtitle": "Vyberte plán, ktorý vám vyhovuje",
    "pricing.free": "Free",
    "pricing.pro": "Pro",
    "pricing.agency": "Agency",
    "pricing.forever": "navždy",
    "pricing.perMonth": "/mesiac",
    "pricing.startFree": "Začať zadarmo",
    "pricing.choosePro": "Vybrať Pro",
    "pricing.contactUs": "Kontaktovať nás",
    "pricing.feature.requests5": "5 dopytov/mesiac",
    "pricing.feature.basicAI": "Základné AI odpovede",
    "pricing.feature.emailSupport": "Email podpora",
    "pricing.feature.unlimitedRequests": "Neobmedzené dopyty",
    "pricing.feature.advancedAI": "Pokročilý AI agent",
    "pricing.feature.prioritySupport": "Prioritná podpora",
    "pricing.feature.pdfExport": "Export do PDF",
    "pricing.feature.allPro": "Všetko z Pro",
    "pricing.feature.teamAccounts": "Tímové účty (5 používateľov)",
    "pricing.feature.apiAccess": "API prístup",
    "pricing.feature.customBranding": "Vlastný branding",
    "pricing.feature.dedicatedManager": "Dedikovaný manažér",
    "pricing.login": "Prihlásiť sa",

    "supplier.headline": "Chcete zadarmo dostávať dopyty, o ktoré máte záujem?",
    "supplier.description": "Stačí zadať e-mail – začneme vám ihneď posielať relevantné dopyty. Žiadne heslo, žiadne poplatky.",
    "supplier.chatbotHeadline": "Chcem vlastný chatbot",
    "supplier.chatbotDescription": "Zaregistrujte sa a vytvorte si AI chatbot, ktorý bude odpovedať vašim zákazníkom.",
    "supplier.benefit1": "Dopyty z regiónov, kde pôsobíte",
    "supplier.benefit2": "Filtrujte podľa typu materiálu a objemu",
    "supplier.benefit3": "Reagujte len na zákazky, ktoré vám dávajú zmysel",
    "supplier.emailPlaceholder": "vas@firma.sk",
    "supplier.submitEmail": "Odoberať dopyty zadarmo",
    "supplier.noSpam": "Žiadny spam. Odhlásiť sa môžete kedykoľvek.",
    "supplier.consent": "Odoslaním súhlasíte so zasielaním dopytov na váš e-mail a s podmienkami služby. Z odberu sa môžete kedykoľvek odhlásiť.",
    "supplier.registerHint": "Chcete si vybrať, aké dopyty dostávať, a naceniť zákazky? Zaregistrujte sa zadarmo.",
    "supplier.preferencesTitle": "Aké práce vás zaujímajú?",
    "supplier.preferencesSubtitle": "Vyberte typy zákaziek, ktoré chcete dostávať. Môžete zmeniť kedykoľvek.",
    "supplier.savePreferences": "Dokončiť registráciu",
    "supplier.magicLinkSent": "E-mail zaregistrovaný!",
    "supplier.checkInbox": "Budeme vám zasielať relevantné dopyty od zákazníkov priamo na váš e-mail.",
    "supplier.detailsTitle": "Povedzte nám o sebe",
    "supplier.detailsSubtitle": "Tieto údaje sú nepovinné, ale pomôžu nám lepšie cieliť dopyty.",
    "supplier.namePlaceholder": "Vaše meno a priezvisko",
    "supplier.phonePlaceholder": "Telefón, napr. +421 900 123 456",
    "supplier.saveDetails": "Uložiť údaje",
    "supplier.skipStep": "Preskočiť",
    "supplier.loginTitle": "Prihlásenie dodávateľa",
    "supplier.loginSubtitle": "Dostávajte zadarmo dopyty priamo do e-mailu.",
    "supplier.noAccount": "Nemáte účet dodávateľa?",
    "supplier.register": "Zaregistrujte sa",
    "supplier.backToApp": "Späť na cenik.ai",
    "supplier.registerTitle": "Registrácia dodávateľa",
    "supplier.registerSubtitle": "Vytvorte si účet a začnite oceňovať",
    "supplier.companyName": "Názov firmy",
    "supplier.companyPlaceholder": "Vaša firma s.r.o.",
    "supplier.registerBtn": "Zaregistrovať sa",
    "supplier.hasAccount": "Už máte účet?",
    "supplier.loginLink": "Prihláste sa",
    "supplier.passwordMin": "Heslo musí mať aspoň 8 znakov",
    "supplier.checkEmail": "Overte svoj e-mail pre aktiváciu účtu",
    "supplier.icoPlaceholder": "IČO, napr. 12345678 (nepovinné)",
    "supplier.addressPlaceholder": "Sídlo firmy, napr. Bratislava",
    "supplier.fillCompany": "Vyplňte názov firmy",
    "supplier.becomePartnerTitle": "Staňte sa partnerom",
    "supplier.becomePartnerSubtitle": "Vyplňte základné údaje o firme.",
    "supplier.continueToPreferences": "Vytvoriť dodávateľa",
    "supplier.registrationDone": "Registrácia dokončená! Vitajte v paneli dodávateľa.",
    "supplier.settingsTitle": "Nastavenia",
    "supplier.tabCompany": "Firma",
    "supplier.tabChatbot": "Chatbot",
    "supplier.tabPricing": "Cenníky",
    "supplier.incompleteProfile": "Vyplňte informácie o sebe v nastaveniach, aby váš chatbot fungoval správne.",
    "supplier.goToSettings": "Prejsť do nastavení",
    "supplier.passwordPlaceholder": "Heslo (min. 8 znakov)",
    "supplier.dashboard": "Admin dodávateľa",
    "supplier.logout": "Odhlásiť",
    "supplier.productsTitle": "Moje produkty a ceny",
    "supplier.productsSubtitle": "Pridajte produkty a nastavte cenu za m²",
    "supplier.addProduct": "Pridať produkt",
    "supplier.editProduct": "Upraviť produkt",
    "supplier.newProduct": "Nový produkt",
    "supplier.productName": "Názov produktu",
    "supplier.productNamePlaceholder": "napr. Zateplenie EPS 100",
    "supplier.pricePerM2": "Cena za m² (Kč)",
    "supplier.productDescription": "Popis",
    "supplier.descriptionPlaceholder": "Krátky popis produktu...",
    "supplier.cancel": "Zrušiť",
    "supplier.save": "Uložiť",
    "supplier.noProducts": "Zatiaľ nemáte žiadne produkty",
    "supplier.addFirstProduct": "Kliknite na 'Pridať produkt' pre začiatok",
    "supplier.productAdded": "Produkt pridaný",
    "supplier.productUpdated": "Produkt aktualizovaný",
    "supplier.productDeleted": "Produkt vymazaný",
    "supplier.fillRequired": "Vyplňte názov a cenu",
    "supplier.inquiriesTab": "Dopyty",
    "supplier.productsTab": "Produkty",
    "supplier.noInquiries": "Zatiaľ žiadne dopyty",
    "supplier.noInquiriesHint": "Nové dopyty sa zobrazia automaticky",
    "supplier.alreadyPaid": "Ponuka už bola odoslaná",
    "supplier.paymentError": "Chyba pri platbe, skúste to znova",
    "supplier.loginRequired": "Pre platbu sa musíte prihlásiť",
    "supplier.submitQuoteTitle": "Vaša cenová ponuka",
    "supplier.submitQuoteHint": "Zadajte orientačnú cenu bez DPH. Po odoslaní a zaplatení 15 Kč bude ponuka doručená zákazníkovi.",
    "supplier.quoteTotal": "Ponúkaná cena (Kč bez DPH)",
    "supplier.quoteNotes": "Poznámka",
    "supplier.quoteNotesPlaceholder": "Čo je zahrnuté v cene, termín realizácie...",
    "supplier.submitQuoteBtn": "Odoslať ponuku a zaplatiť 15 Kč",
    "supplier.quoteSaved": "Ponuka uložená!",
    "supplier.quoteAlreadySent": "Ponuka už bola odoslaná",
    "supplier.fillQuotePrice": "Vyplňte ponúkanú cenu",
    "supplier.quoteSent": "Odoslané",
    "supplier.quoteSentConfirm": "Ponuka bola odoslaná zákazníkovi",
    "supplier.quoteSentSuccess": "Ponuka úspešne odoslaná!",
    "supplier.waitingCustomerApproval": "Čakáme na schválenie zákazníkom. Keď prejaví záujem, zobrazia sa vám jeho kontaktné údaje.",
    "supplier.customerApproved": "Zákazník schválil zdieľanie kontaktu",
    "supplier.contactVisibleNow": "Kontaktné údaje zákazníka sú teraz viditeľné vyššie.",
    "supplier.share": "Zdieľať",
    "supplier.linkCopied": "Odkaz skopírovaný",
    "supplier.shareFailed": "Zdieľanie zlyhalo",
    "supplier.aboutQuery": "Povedzte mi viac o vašej firme.",
    "supplier.aboutCompany": "O firme",
    "supplier.slugSaved": "Slug uložený",
    "supplier.slugTakenOrInvalid": "Slug je už obsadený alebo neplatný",
    "supplier.connectionError": "Chyba pripojenia",
    "supplier.paymentNotVerified": "Platba nebola overená. Skúste obnoviť stránku.",
    "supplier.paymentVerificationError": "Chyba pri overovaní platby.",
    "supplier.savingError": "Chyba pri ukladaní",
    "supplier.dataSaved": "Údaje uložené",
    "supplier.edit": "upraviť",
    "supplier.onboardingTitle": "Ako tu fungujú dopyty pre vašu firmu?",
    "supplier.onboardingStep1": "Nastavíte cenník a oblasti, kde chcete zákazky.",
    "supplier.onboardingStep2": "Podľa toho vám budeme zobrazovať nové dopyty.",
    "supplier.onboardingStep3": "Pri vybraných dopytoch odošlete nacenenie a zákazníkovi sa zobrazí detail vašej ponuky.",
    "supplier.aiChatCalculator": "Váš AI chat kalkulačka",
    "supplier.freeTestVersion": "Zadarmo · testovacia verzia",
    "supplier.checking": "Kontrolujem...",
    "supplier.slugAvailableLabel": "✓ Voľný",
    "supplier.slugTakenLabel": "✗ Obsadený",
    "supplier.savingLabel": "Ukladám...",
    "supplier.saveLabel": "Uložiť",
    "supplier.cancel": "Zrušiť",
    "supplier.copyLink": "Kopírovať odkaz",
    "supplier.customDomainTag": "· vlastná doména",
    "supplier.customDomainLabel": "Vlastná doména: {domain}",
    "supplier.connectCustomDomain": "Pripojiť vlastnú doménu",
    "supplier.enterDomainLabel": "Zadajte svoju doménu (napr. {example}):",
    "supplier.connectButton": "Pripojiť",
    "supplier.verifiedLabel": "Overené ✓",
    "supplier.awaitingDns": "Čaká na DNS",
    "supplier.dnsStep1Title": "1. Nastavte DNS záznamy u vášho registrátora",
    "supplier.dnsStep1Instruction": "Prihláste sa do správy domény (Forpsi, Active24, WEDOS, Cloudflare…) a pridajte tieto záznamy:",
    "supplier.mainDomainLabel": "Pre hlavnú doménu (napr. {example}):",
    "supplier.dnsTypeHeader": "Typ",
    "supplier.dnsNameHeader": "Názov",
    "supplier.dnsValueHeader": "Hodnota",
    "supplier.subdomainLabel": "Pre subdoménu (napr. {example}):",
    "supplier.subdomainHint": "Pri subdoméne nahraďte {sub} vaším názvom (napr. {ex1}, {ex2}). CNAME záznam je odporúčaný pre subdomény.",
    "supplier.dnsStep2Title": "2. Pridajte overovací záznam",
    "supplier.dnsStep2Instruction": "Tento záznam slúži na overenie, že doména patrí vám:",
    "supplier.dnsSettingsTitle": "Kde nájdem nastavenie DNS?",
    "supplier.dnsForpsi": "Administrácia → Domény → vaša doména → DNS záznamy",
    "supplier.dnsActive24": "Detail domény → DNS správa → Pridať záznam",
    "supplier.dnsWedos": "DNS správa → Pridať záznam (typ A alebo TXT)",
    "supplier.dnsCloudflare": "DNS → Records → Add record",
    "supplier.dnsPropagationHint": "Zmena DNS sa prejaví do 5 minút – 24 hodín. Po nastavení kliknite 'Overiť DNS'.",
    "supplier.verifyDns": "Overiť DNS",
    "supplier.disconnectDomain": "Odpojiť doménu",
    "supplier.chatbotSettingsLabel": "Nastavenie chatbota a cenníkov",
    "supplier.noPriceItemsWarning": "Bez nastaveného cenníka sa vaša firma nezobrazuje v ponukách.",
    "supplier.setPriceList": "Nastaviť cenník →",
    "supplier.readiness.setupCta": "Nastavenie chatbota a cenníkov",
    "supplier.readiness.companyInfo": "Informácie o firme",
    "supplier.readiness.pricing": "Cenník",
    "supplier.readiness.done": "Hotovo",
    "supplier.readiness.missing": "Chýba",
    "supplier.readiness.warningText": "Aby sa váš chatbot verejne zobrazil, musíte vyplniť informácie o firme a nastaviť cenník.",
    "supplier.readiness.readyText": "Váš chatbot je pripravený na verejné zobrazenie.",
    "supplier.readiness.fallbackTitle": "Chatbot zatiaľ nie je dostupný",
    "supplier.readiness.fallbackDescription": "Tento chatbot sa práve pripravuje. Skúste to prosím neskôr.",
    "supplier.ownChatbot": "Vlastný chatbot",
    "supplier.marketplaceInquiries": "Dopyty z cenik.ai",
    "supplier.filterNew": "Nové",
    "supplier.filterQuoteSent": "Ponuka odoslaná",
    "supplier.filterHidden": "Skryté",
    "supplier.chatbotBadge": "Chatbot",
    "supplier.marketplaceBadge": "cenik.ai",
    "supplier.requestMaterial": "Dopyt materiálu",
    "supplier.quoteAction": "Naceniť",
    "supplier.restore": "Obnoviť",
    "supplier.hideAction": "Skryť",
    "supplier.marketplaceUpsellTitle": "Dopyty z cenik.ai marketplace",
    "supplier.marketplaceUpsellDescription": "Získajte prístup k dopytom z celého marketplace s Premium plánom",
    "supplier.premiumLabel": "Premium",
    "supplier.approved": "Schválené",
    "customer.allowContact": "Chcem, aby ma dodávateľ kontaktoval",
    "customer.approved": "Kontakt zdieľaný",
    "customer.contactShared": "Dodávateľ teraz vidí vaše kontaktné údaje",
    "customer.supplierContact": "Kontakt na dodávateľa:",

    "filter.title": "Filter dopytov",
    "filter.workType": "Typ práce",
    "filter.allTypes": "Všetky typy",
    "filter.location": "Miesto stavby",
    "filter.locationPlaceholder": "napr. Bratislava, Košice...",
    "filter.priceFrom": "Cena od (€)",
    "filter.priceTo": "Cena do (€)",
    "filter.deadline": "Termín realizácie",
    "filter.deadlinePlaceholder": "napr. máj 2026, ihneď...",
    "filter.lock": "Uzamknúť filter",
    "filter.apply": "Zobraziť výsledky",
    "filter.save": "Uložiť a dostávať oznámenia",
    "filter.unlock": "Odomknúť filter",
    "filter.locked": "Uzamknuté",
    "filter.wholeCountry": "Celá Česká republika",
    "filter.reset": "Resetovať",
    "filter.lockedHint": "Filter je uzamknutý – nové dopyty zodpovedajúce vašim kritériám vám budú zasielané do notifikácií aj na e-mail. Pre zmenu filtrov ich najprv odomknite.",
    "filter.noResults": "Žiadne dopyty nezodpovedajú filtru",
    "filter.noResultsHint": "Skúste upraviť alebo resetovať filter",
    "filter.searchCity": "Hľadať mesto alebo obec...",
    "filter.radius": "Okruh",
    "filter.addWholeCZ": "+ Celá Česká republika",
    "filter.wholeCZ": "Celá Česká republika",
    "filter.noLocationsSet": "Žiadne lokácie nenastavené",
    "filter.searching": "Hľadám",
    "filter.locationAreas": "Oblasti pôsobnosti",
    "filter.locationSaved": "Oblasti uložené",

    "register.checkEmail": "Overte svoj e-mail pre aktiváciu",
    "register.emailSent": "E-mail odoslaný!",
    "register.checkEmailDesc": "Skontrolujte svoju schránku a kliknite na potvrdzovací odkaz.",
    "register.goToLogin": "Prejsť na prihlásenie",
    "profile.title": "Nastavenia profilu",
    "profile.personalInfo": "Osobné údaje",
    "profile.fullName": "Meno a priezvisko",
    "profile.fullNamePlaceholder": "Ján Novák",
    "profile.phone": "Telefón",
    "profile.phonePlaceholder": "+421 123 456 789",
    "profile.save": "Uložiť",
    "profile.saved": "Profil uložený",
    "profile.supplierPanel": "Panel dodávateľa",
    "profile.sitesTitle": "Projekty",
    "profile.sitesSubtitle": "Spravujte svoje projekty a stavby",
    "profile.siteNamePlaceholder": "Názov projektu",
    "profile.siteAddressPlaceholder": "Adresa projektu",
    "profile.addSite": "Uložiť projekt",
    "profile.siteAdded": "Projekt pridaný",
    "profile.siteDeleted": "Projekt odstránený",
    "profile.siteSaved": "Projekt uložený",
    "sidebar.settings": "Nastavenia",
    "sidebar.logout": "Odhlásiť sa",
    "sidebar.aboutUs": "O nás",
    "sidebar.assignTo": "Priradiť k projektu",
    "sidebar.unassign": "Odobrať z projektu",
    "sidebar.deleteChat": "Zmazať chat",
    "sidebar.chatDeleted": "Chat zmazaný",
    "sidebar.chatAssigned": "Chat priradený",
    "sidebar.noProjectsYet": "Zatiaľ žiadne projekty",
    "sidebar.noSearchResults": "Žiadne výsledky",
    "sidebar.history": "História",
    "sidebar.inquiriesAndQuotes": "Dopyty a ponuky",
    "sidebar.noInquiriesYet": "Zatiaľ žiadne dopyty",
    "sidebar.quote": "ponuka",
    "sidebar.quotes": "ponuky",
    "sidebar.backToChat": "Späť na chat",
    "sidebar.aboutUsResponse": "## O cenik.ai 👋\n\nSme tu, aby sme **zmenili spôsob, akým funguje oceňovanie a dopyt v stavebníctve**.\n\nVeríme, že získať férovú cenu za fasádu, zateplenie alebo inú stavebnú prácu by malo byť jednoduché – ako pre tých, **kto dopytuje**, tak pre tých, **kto prácu realizuje**.\n\nZa projektom stojí **Adam Černohous, IČO: 76535398**, so sídlom Třebovská 65, Ústí nad Orlicí 56203.\n\n📧 Kontakt: **info@cenik.ai**",
    "dashboard.newProject": "Nový projekt",
    "notifications.title": "Upozornenia",
    "notifications.empty": "Žiadne upozornenia",
    "notifications.newInquiry": "Nový dopyt",
    "notifications.loginPrompt": "Tu sa budú zobrazovať vaše ponuky od dodávateľov. Prihláste sa pre zobrazenie.",

    // Customer dashboard
    "customerDashboard.title": "Moje dopyty",
    "customerDashboard.newInquiry": "Nový dopyt",
    "customerDashboard.onboardingTitle": "Ako funguje cenik.ai pre vás?",
    "customerDashboard.onboardingStep1": "Spýtajte sa v chate na odhad ceny zateplenia alebo fasády.",
    "customerDashboard.onboardingStep2": "Chat z vašich odpovedí vytvorí dopyt.",
    "customerDashboard.onboardingStep3": "Dopyt odošleme firmám z vášho okolia a ich ponuky uvidíte práve tu.",
    "customerDashboard.emptyTitle": "Zatiaľ nemáte žiadne dopyty",
    "customerDashboard.emptyDesc": "Otvorte chat na hlavnej stránke a nechajte si spočítať prvý dopyt.",
    "customerDashboard.statusOpen": "Otvorený",
    "customerDashboard.statusInProgress": "Prebieha komunikácia",
    "customerDashboard.statusClosed": "Uzavretý",
    "customerDashboard.statusCompleted": "Realizované",
    "customerDashboard.quote": "ponuka",
    "customerDashboard.quotes": "ponuky",
    "customerDashboard.quotesMany": "ponúk",

    // Supplier chat
    "supplierChat.newChat": "Nový chat",
    "supplierChat.inquiry": "Dopyt",
    "supplierChat.message": "Správa",
    "supplierChat.upsellTitle": "Chcete vlastný chatbot?",
    "supplierChat.upsellDesc": "Získajte AI asistenta pre vašu firmu s vlastným cenníkom a brandingom.",
    "supplierChat.upsellLink": "Zistiť viac →",
    "supplierChat.aboutUs": "O nás",
    "supplierChat.msgSavedNotifyFailed": "Správa sa uložila, ale upozornenie firme sa nepodarilo odoslať.",
    "supplierChat.msgDeliveryFailed": "Správu sa nepodarilo doručiť firme. Skúste to prosím znova.",
    "supplierChat.inquiryDeliveryFailed": "Dopyt sa nepodarilo doručiť firme. Skúste to prosím znova.",
    "supplierChat.error": "Chyba: {error}",
    "supplierChat.chatNotFound": "AI chat nenájdený",
    "supplierChat.domainNotLinked": "Táto doména zatiaľ nie je prepojená so žiadnym cenníkom.",
    "supplierChat.slugNotFound": "neexistuje.",
    "supplierChat.backToHome": "← Späť na hlavnú stránku",
    "supplierChat.askAboutCompany": "Povedzte mi viac o vašej firme.",
    "supplierChat.inputPlaceholder": "Napíšte dotaz alebo parametre…",
    "supplierChat.aboutCompany": "O firme {name}",
    "supplierChat.specialization": "Špecializácia",
    "supplierChat.serviceArea": "Oblasť pôsobnosti",
    "supplierChat.aiAssistantHelp": "Čo vám AI asistent pomôže zistiť?",
    "supplierChat.aiHelp1": "Orientačnú cenu stavebných prác",
    "supplierChat.aiHelp2": "Dostupnosť a typy materiálov",
    "supplierChat.aiHelp3": "Odpovede na odborné otázky",
    "supplierChat.aiHelp4": "Predbežnú kalkuláciu na základe vašich parametrov",
    "supplierChat.faq": "Časté otázky",
    "supplierChat.contact": "Kontakt",

    // Sidebar extras
    "sidebar.currentChat": "Aktuálny chat",
    "sidebar.messageCount": "správ",

    // Supplier settings
    "supplierSettings.companyName": "Názov firmy *",
    "supplierSettings.ico": "IČO",
    "supplierSettings.contactEmail": "Kontaktný e-mail",
    "supplierSettings.contactPhone": "Kontaktný telefón",
    "supplierSettings.region": "Región",
    "supplierSettings.regionPlaceholder": "Napr. Bratislava, Košický kraj",
    "supplierSettings.description": "Popis firmy",
    "supplierSettings.descriptionPlaceholder": "Krátky popis vašej firmy...",
    "supplierSettings.saving": "Ukladám...",
    "supplierSettings.saveCompany": "Uložiť údaje firmy",
    "supplierSettings.savedCompany": "Údaje firmy uložené",
    "supplierSettings.saveError": "Chyba pri ukladaní",
    "supplierSettings.saveFailed": "Nepodarilo sa uložiť",
    "supplierSettings.chatbotDesc": "Prispôsobte, ako sa váš chatbot predstaví zákazníkom. Vo free verzii bot nezdieľa kontaktné údaje.",
    "supplierSettings.botName": "Meno chatbota",
    "supplierSettings.botNameHint": "Ak nevyplníte, bot bude mať meno podľa vašej firmy.",
    "supplierSettings.botIntro": "Ako sa bot predstaví",
    "supplierSettings.botIntroPlaceholder": "Som AI asistent spoločnosti XY, pomôžem vám s otázkami o fasádach a zateplení.",
    "supplierSettings.aboutCompany": "O firme",
    "supplierSettings.aboutCompanyPlaceholder": "Stručné info o firme, špecializácii, skúsenostiach, oblasti pôsobenia...",
    "supplierSettings.faq": "FAQ – časté otázky a odpovede",
    "supplierSettings.faqPlaceholder": "Otázka: Aké materiály používate?\nOdpoveď: EPS, minerálna vlna, ETICS systémy...\n\nOtázka: Aká je vaša cena za m²?\nOdpoveď: Cena závisí od rozsahu prác, obvykle...",
    "supplierSettings.saveBot": "Uložiť nastavenia",
    "supplierSettings.savedBot": "Nastavenia chatbota uložené.",
    "supplierSettings.saveBotFailed": "Nepodarilo sa uložiť nastavenia.",
    "supplierSettings.tabServices": "Služby a cenníky",
    "supplierSettings.tabPublicPage": "Verejná stránka",
    "supplierSettings.publicPageDesc": "Nastavte, ako sa vaša verejná AI stránka zobrazí vo vyhľadávačoch a na sociálnych sieťach.",
    "supplierSettings.tagline": "Krátky slogan",
    "supplierSettings.taglinePlaceholder": "Napr. Fasádnické práce na kľúč v Bratislave",
    "supplierSettings.taglineHint": "Zobrazí sa pod názvom firmy na verejnej stránke.",
    "supplierSettings.businessType": "Odbor / typ služby",
    "supplierSettings.businessTypePlaceholder": "Fasádnické práce",
    "supplierSettings.serviceArea": "Oblasť pôsobnosti",
    "supplierSettings.serviceAreaPlaceholder": "Bratislava a okolie",
    "supplierSettings.seoTitle": "SEO titulok stránky",
    "supplierSettings.seoTitleHint": "Zobrazí sa v záložke prehliadača a vo výsledkoch vyhľadávania.",
    "supplierSettings.seoDesc": "SEO popis stránky",
    "supplierSettings.seoDescHint": "Zobrazí sa pod titulkom vo výsledkoch vyhľadávania.",
    "supplierSettings.ogImage": "OG image URL (voliteľné)",
    "supplierSettings.ogImageHint": "Obrázok zobrazený pri zdieľaní na sociálnych sieťach. Ideálne 1200×630 px.",
    "supplierSettings.searchPreview": "Náhľad vo vyhľadávači",
    "supplierSettings.savePublicPage": "Uložiť nastavenia stránky",
    "supplierSettings.savedPublicPage": "Nastavenia verejnej stránky uložené.",
    "supplierSettings.servicesHeading": "Aké služby ponúkate?",
    "supplierSettings.servicesDesc": "Vyberte služby, ktoré vaša firma poskytuje. Pre každú môžete pridať popis a cenník.",
    "supplierSettings.addCustomService": "Pridať vlastnú službu…",
    "supplierSettings.addButton": "Pridať",
    "supplierSettings.serviceAlreadyAdded": "Táto služba je už pridaná",
    "supplierSettings.serviceAdded": 'Služba „{name}" pridaná',
    "supplierSettings.selectServiceFirst": "Vyberte aspoň jednu službu vyššie a potom pridajte popis a cenník.",
    "supplierSettings.customerMessages": "Správy od zákazníkov",
    "supplierSettings.translate": "Preložiť",
    "supplierSettings.showOriginal": "Zobraziť originál",

    // Admin
    "admin.title": "Admin Panel",
    "admin.loginPrompt": "Prihláste sa administrátorským účtom",
    "admin.loginButton": "Prihlásiť sa",
    "admin.email": "E-mail",
    "admin.password": "Heslo",
    "admin.googleLogin": "Prihlásiť sa cez Google",
    "admin.orPassword": "alebo heslom",
    "admin.noPermission": "Tento účet nemá administrátorské oprávnenia",
    "admin.loginFailed": "Prihlásenie sa nepodarilo",
    "admin.googleLoginFailed": "Google prihlásenie zlyhalo",
    "admin.sessionExpired": "Relácia vypršala, prihláste sa znova",
    "admin.registrationFailed": "Registrácia sa nepodarila",
    "admin.accountCreated": "Admin účet vytvorený a prihlásený",
    "admin.loginExpired": "Prihlásenie vypršalo, prihláste sa znova",
    "admin.tab.chats": "Chaty",
    "admin.tab.inquiries": "Dopyty",
    "admin.tab.users": "Používatelia",
    "admin.tab.prices": "Ceny",
    "admin.tab.services": "Služby",
    "admin.tab.subscriptions": "Predplatné",
    "admin.tab.feedback": "Feedback",
    "admin.tab.knowledge": "Znalosti",
    "admin.toast.saved": "Uložené",
    "admin.toast.deleted": "Zmazané",
    "admin.toast.saveFailed": "Nepodarilo sa uložiť",
    "admin.toast.deleteFailed": "Nepodarilo sa zmazať",
    "admin.toast.loadFailed": "Nepodarilo sa načítať dáta",
    "admin.toast.statusUpdated": "Stav aktualizovaný",
    "admin.toast.activated": "Aktivované",
    "admin.toast.deactivated": "Deaktivované",
    "admin.toast.correctionSaved": "Oprava uložená",
    "admin.toast.correctionSaveFailed": "Nepodarilo sa uložiť opravu",
    "admin.toast.feedbackIgnored": "Feedback vyradený",
    "admin.toast.feedbackIgnoreFailed": "Nepodarilo sa vyradiť feedback",
    "admin.toast.knowledgeAdded": "Znalosť pridaná",
    "admin.toast.knowledgeSaved": "Znalosť uložená",
    "admin.toast.aiCategorized": "AI navrhla kategóriu a názov",
    "admin.toast.serviceCreated": "Služba vytvorená",
    "admin.toast.createServiceFailed": "Nepodarilo sa vytvoriť službu",
    "admin.toast.serviceActivated": "Služba aktivovaná",
    "admin.toast.serviceRejected": "Služba odmietnutá",
    "admin.toast.movedToQueue": "Presunuté do fronty",
    "admin.toast.movedToCandidate": "Presunuté na kandidáta",
    "admin.toast.serviceAssigned": "Služba priradená",
    "admin.toast.newServiceProposed": "Nová služba navrhnutá a priradená",
    "admin.toast.aiNoMatch": "AI nenašla vhodnú službu",
    "admin.toast.detectionFixed": "Detekcia opravená + learning vytvorený",
    "admin.toast.detectionFixFailed": "Nepodarilo sa opraviť detekciu",
    "admin.toast.detectionRejected": "Detekcia zamietnutá",
    "admin.toast.detectionRejectFailed": "Nepodarilo sa zamietnuť",
    "admin.toast.overrideSaved": "Override uložený",
    "admin.toast.overrideSaveFailed": "Nepodarilo sa uložiť override",
    "admin.toast.planChangeFailed": "Nepodarilo sa zmeniť plán",
    "admin.toast.featureToggleFailed": "Nepodarilo sa zmeniť stav feature",
    "admin.toast.fillTopicContent": "Vyplňte tému a obsah",
    "admin.toast.fillNameSlug": "Vyplňte názov a slug",
    "admin.toast.invalidSlug": "Slug môže obsahovať len malé písmená, čísla a pomlčky",
    "admin.toast.learningAdded": "Učenie pridané – AI ho začne používať okamžite",
    "admin.toast.addFailed": "Nepodarilo sa pridať",
    "admin.toast.inquiryDeleted": "Dopyt zmazaný",
    "admin.toast.writeCorrectedAnswer": "Napíšte opravenú odpoveď",
    "admin.toast.serviceSaved": "Služba uložená",
    "admin.toast.generateFailed": "Generovanie zlyhalo",
    "admin.toast.formApproved": "Formulár schválený — dodávatelia ho uvidia",
    "admin.toast.approveFailed": "Nepodarilo sa schváliť",
    "admin.toast.formToDraft": "Formulár prepnutý do editácie",
    "admin.toast.operationFailed": "Nepodarilo sa",
    "admin.toast.keywordApproved": "Keyword schválený",
    "admin.toast.keywordRejected": "Keyword odmietnutý",
    "admin.toast.keywordAdded": "Keyword pridaný",
    "admin.toast.keywordDeleted": "Keyword zmazaný",
    "admin.status.active": "Aktívny",
    "admin.status.inactive": "Neaktívny",
    "admin.status.candidate": "Kandidát",
    "admin.status.queued": "Vo fronte",
    "admin.status.rejected": "Odmietnuté",
    "admin.status.positive": "Pozitívny",
    "admin.status.negative": "Negatívny",
    "admin.empty.inquiries": "Žiadne dopyty",
    "admin.empty.suppliers": "Žiadni dodávatelia",
    "admin.empty.customers": "Žiadni zákazníci",
    "admin.empty.feedback": "Zatiaľ žiadny feedback od používateľov",
    "admin.confirm.deleteInquiry": "Naozaj zmazať tento dopyt?",
    "admin.confirm.deleteLearning": "Naozaj zmazať toto učenie?",
    "admin.btn.approve": "Schváliť",
    "admin.btn.reject": "Odmietnuť",
    "admin.btn.addItem": "Pridať položku",
    "admin.btn.saveDraft": "Uložiť draft",
    "admin.btn.approveAndDeploy": "Schváliť a nasadiť",
    "admin.btn.regenerate": "Pregenerovať",
    "admin.btn.editForm": "Upraviť formulár",
    "admin.btn.collapse": "Zbaliť",
    "admin.btn.showAll": "Zobraziť všetko",
    "admin.btn.ignore": "Vyradiť",
    "admin.btn.save": "Uložiť",
    "admin.btn.cancel": "Zrušiť",
    "admin.btn.addKnowledge": "Pridať znalosť pre",
    "admin.btn.restoreCandidate": "Obnoviť ako kandidáta",
    "admin.btn.removeOverride": "Odstrániť výnimku",
    "admin.btn.disable": "Zakázať",
    "admin.btn.enable": "Povoliť",
    "admin.section.quoteForm": "Formulár ponuky pre dodávateľov",
    "admin.section.noQuoteForm": "Zatiaľ nemá formulár ponuky. AI navrhne položky rozpočtu pre túto službu.",
    "admin.section.costItems": "Nákladové položky",
    "admin.section.formPreview": "Náhľad formulára pre dodávateľov",
    "admin.section.serviceStatus": "Stav služby",
    "admin.section.feedbackHelp": "Hodnotenia AI odpovedí. Negatívny feedback s opravou sa okamžite aplikuje ako AI learning. Podozrivé hodnotenia vyraďte tlačidlom.",
    "admin.section.totalRatings": "Celkom hodnotení",
    "admin.section.aiKnowledge": "AI používa tieto znalosti pri odpovedaní zákazníkom a generovaní ponúk pre túto službu.",
    "admin.placeholder.knowledgeName": "Názov znalosti",
    "admin.placeholder.knowledgeContent": "Vlož text o zatepľovaní, cenách, materiáloch... AI sama navrhne názov a kategóriu.",
    "admin.placeholder.learningContent": "Obsah znalosti — čo má AI vedieť a ako sa má správať...",
    "admin.placeholder.itemName": "Názov položky",
    "admin.placeholder.keyword": "Nové kľúčové slovo alebo fráza...",
    "admin.placeholder.serviceDesc": "Krátky popis služby",
    "admin.placeholder.priceName": "napr. Ceny EPS",
    "admin.placeholder.newServiceName": "Názov novej služby:",
    "admin.label.totalPrice": "Celková cena",
    "admin.label.currency": "Kč",
    "admin.label.records": "záznamov",
    "admin.label.offer": "ponuka",
    "admin.label.offers": "ponuky",
    "admin.label.offersMany": "ponúk",
    "admin.label.inquirySent": "Dopyt ✓",
    "admin.label.inquiryNotSent": "Bez dopytu",
    "admin.label.formDraft": "📝 Draft",
    "admin.label.formApproved": "✅ Formulár",
    "admin.label.user": "👤 Používateľ",
    "admin.label.ai": "🤖 AI",
    "admin.label.advancedCalc": "Pokročilá kalkulácia",
    "admin.label.weight": "Váha",
    "admin.label.serviceNotFound": "Služba nenájdená",
    "admin.label.generating": "Generujem…",
    "admin.label.generateForm": "🤖 Generovať formulár ponuky",
    "admin.label.analyzing": "Analyzujem...",
    "admin.label.aiCategorize": "🤖 AI zaradí",
    "admin.label.newServiceProposal": "Návrh novej služby",
    "admin.label.uncertainMatch": "Neistý match",
    "admin.label.match": "Match",
    "admin.label.new": "Nová",
    "admin.label.uncertain": "Neistý",
    "admin.filter.all": "Všetko",
    "admin.filter.sent": "Dopyt odoslaný",
    "admin.filter.notSent": "Neodoslaný",
    "admin.module.walls": "Steny",
    "admin.module.material": "Dopyt materiálu",
    "admin.module.quoteCalc": "Kalkulácia ponuky",
    "admin.module.generalInquiry": "Všeobecný dopyt",
    "admin.module.simpleQuote": "Jednoduchá ponuka",
    "admin.material.epsWhite": "EPS biely",
    "admin.material.epsGrey": "EPS šedý",
    "admin.material.mineralWool": "Minerálna vata",
    "admin.material.ventilatedFacade": "Odvetrávaná fasáda",
    "admin.section.basicInfo": "Základné info",
    "admin.section.keywordsDetection": "Kľúčové slová (detekcia)",
    "admin.section.keywordsHelp": "Schválené kľúčové slová sa používajú na hybridnú detekciu služby. Navrhované čakajú na kontrolu.",
    "admin.section.modules": "Moduly",
    "admin.section.knowledgeFor": "Znalosti AI pre",
    "admin.label.name": "Názov",
    "admin.label.slugUrl": "Slug (URL)",
    "admin.label.keywordsComma": "Kľúčové slová (čiarkami, pre keyword matching)",
    "admin.label.descOptional": "Popis (voliteľný)",
    "admin.label.approved": "Schválené",
    "admin.label.suggested": "Navrhované",
    "admin.label.rejectedKw": "Odmietnuté",
    "admin.label.profit": "Zisk",
    "admin.label.vat": "DPH",
    "admin.label.unitLabel": "Jednotka:",
    "admin.label.vatRate": "DPH sadzba (12 % / 21 %)",
    "admin.label.query1": "dopyt",
    "admin.label.query2to4": "dopyty",
    "admin.label.query5plus": "dopytov",
    "admin.label.record1": "záznam",
    "admin.label.record2to4": "záznamy",
    "admin.label.learningActive": "✓ Aktívne",
    "admin.label.learningInactive": "○ Neaktívne",
    "admin.label.formApprovedBadge": "✅ Schválené",
    "admin.label.formDraftBadge": "📝 Draft",
    "admin.label.facadeNote": "ℹ️ Fasádnické práce majú vstavaný formulár (materiál, lešenie, práca, doprava). Ak vygenerujete nový, nahradí vstavaný.",
    "admin.label.fact": "📚 Fakt",
    "admin.label.correction": "🔧 Oprava",
    "admin.label.tip": "💡 Tip",
    "admin.btn.refresh": "Obnoviť",
    "admin.btn.backToOverview": "← Späť na prehľad",
    "admin.btn.add": "Pridať",
    "admin.btn.activate": "Aktivovať",
    "admin.btn.toQueue": "Do fronty",
    "admin.placeholder.keywordsExample": "fasáda, zateplenie, eps, polystyrén",
    "admin.placeholder.learningTopic": "Téma (napr. Cena SDK priečky)",
    "admin.toast.aiSuggestedForm": "AI navrhla formulár — skontrolujte a schváľte",
    "admin.toast.error": "Chyba",
    "admin.module.wallsDesc": "Zákazník zadáva steny s rozmermi a otvormi. Dodávateľ vidí detailný rozpis plôch.",
    "admin.module.materialDesc": "Dodávateľ dopytuje materiál od veľkoobchodov priamo z dopytu.",
    "admin.module.quoteCalcDesc": "Položkový rozpočet s cenami za m². Generatívna AI ponuka pre zákazníka.",
    "admin.module.generalInquiryDesc": "AI-generovaný popis dopytu z chatu.",
    "admin.module.simpleQuoteDesc": "Dodávateľ zadá celkovú cenu + poznámky.",
  },

  en: {
    "topbar.login": "Sign in",
    "topbar.register": "Sign up for free",
    "topbar.comingSoon": "More coming soon",
    "topbar.facades": "Facade works",
    "landing.title": "How much does your construction cost?",
    "landing.subtitle": "Choose a type of construction work and get an estimate in minutes.",
    "landing.service.fasady": "Facade works",
    "landing.service.fasady.desc": "Estimated cost of facades, insulation, and plaster",
    "service.fasadnicke-prace": "Facade works",
    "landing.service.strechy": "Roofing",
    "landing.service.okna": "Windows & doors",
    "landing.startChat": "Get estimate",
    "landing.comingSoon": "Coming soon",
    "index.headline1": "How much does your project cost?",
    "index.headline2": "Calculate your construction costs",
    "index.headline3": "Get an estimate online",
    "index.headline4": "Price your construction work",
    "index.headline5": "Cost estimate in a minute",
    "index.disclaimer": "By sending a message to cenik.ai, an AI-based chatbot, you agree to our",
    "index.terms": "terms and conditions",
    "index.privacyConfirm": "and confirm that you have read our",
    "index.privacy": "privacy policy",
    "index.cookiesSee": "See",
    "index.cookies": "cookie preferences",
    "cookie.message": "This website uses cookies to ensure proper functionality and analyze traffic.",
    "cookie.accept": "Accept all",
    "cookie.reject": "Reject optional",
    "cookie.settings": "Settings",
    "index.sensitiveWarning": "Do not share sensitive personal data (ID numbers, passwords, bank accounts).",
    "index.nextQuestion": "Type your next question...",
    "index.mayMakeErrors": "cenik.ai may make mistakes. Verify important information.",
    "index.leadSaved": "Contact details sent – we'll be in touch soon! 🏠",
    "sidebar.newChat": "New chat",
    "sidebar.searchChats": "Search chats",
    "sidebar.projects": "Projects",
    "sidebar.howToUse": "How it works",
    "sidebar.howToUseQuery": "How does it work?",
    "sidebar.howToUseResponse": "Welcome to **cenik.ai** 👋\n\nIt's simple:\n\n1. **Tell me what you need** – type of work, dimensions, material.\n2. **AI will estimate the price** – based on current market data.\n3. **Ask follow-up questions** – about materials, technologies, or compare options.\n4. **Want a non-binding quote?** Leave your contact and we'll connect you with verified suppliers.\n\n💡 **Tip:** You can also go directly to the **calculator** and fill in everything yourself.\n\nStart by describing what you need priced 🏠",
    "sidebar.yourChats": "Chat history",
    "sidebar.noChatsYet": "No chats yet",
    "sidebar.loginForHistory": "Sign in to view chat history.",
    "sidebar.loginLink": "Sign in",
    "sidebar.loginForFeature": "Sign in to use this feature.",
    "sidebar.loginForInquiries": "Sign in to view inquiries and quotes.",
    "sidebar.becomeSupplier": "I want my own chatbot",
    "sidebar.supplierDashboard": "Supplier dashboard",
    "sidebar.supplierCta": "Want to receive free inquiries to your email?",
    "sidebar.login": "Sign in",
    "chat.placeholder1": "What do you need priced?",
    "chat.placeholder2": "What are the dimensions or area?",
    "chat.placeholder3": "What material do you prefer?",
    "chat.placeholder4": "Where is the construction site?",
    "chat.placeholder5": "Questions about construction work?",
    "chat.placeholderMobile1": "What do you need priced?",
    "chat.placeholderMobile2": "What are the dimensions?",
    "chat.placeholderMobile3": "What material?",
    "chat.placeholderMobile4": "Where is the site?",
    "chat.placeholderMobile5": "Construction questions?",
    "chat.takePhoto": "Take photo",
    "chat.addPhotos": "Add photos",
    "chat.uploadFiles": "Upload files",
    "chat.fileTooLarge": "File \"{name}\" is larger than 10 MB.",
    "chat.assignToProject": "Assign to project",
    "chat.unassign": "Remove from project",
    "chat.deleteChat": "Delete chat",
    "chat.loginToSave": "Sign in to save",
    "suggestion.facadePrice": "Calculate facade price",
    "suggestion.insulationCost": "How much do construction works cost?",
    "suggestion.bestPolystyrene": "What material is the best?",
    "suggestion.contactInsulation": "Get an estimate",
    "login.title": "Sign in",
    "login.email": "Email",
    "login.emailPlaceholder": "your@email.com",
    "login.password": "Password",
    "login.passwordPlaceholder": "••••••••",
    "login.submit": "Sign in",
    "login.or": "or",
    "login.google": "Continue with Google",
    "login.noAccount": "Don't have an account?",
    "login.register": "Sign up",
    "login.forgotPassword": "Forgot your password?",
    "login.subtitle": "Enter your email and password to log in.",
    "login.chatbotBoxTitle": "Do you own a business?",
    "login.chatbotBoxDesc": "Create your own AI chatbot for your customers — for free.",
    "login.becomeSupplier": "I want my own chatbot",
    "forgotPassword.title": "Reset password",
    "forgotPassword.description": "Enter your email and we'll send you a link to reset your password.",
    "forgotPassword.submit": "Send link",
    "forgotPassword.sent": "Link sent!",
    "forgotPassword.sentDesc": "Check your email inbox and click the link to reset your password.",
    "forgotPassword.backToLogin": "Back to sign in",
    "resetPassword.title": "New password",
    "resetPassword.password": "New password",
    "resetPassword.passwordPlaceholder": "At least 8 characters",
    "resetPassword.submit": "Set password",
    "resetPassword.success": "Password changed",
    "login.info": "Sign in with your email and password or via Google. After signing in, you'll have access to your chat history and saved projects.",
    "register.info": "Create a free account to save chats, manage projects and construction sites. Just an email and password or Google account.",
    "forgotPassword.info": "Enter the email you registered with. We'll send you a link to set a new password.",
    "resetPassword.info": "Enter a new password (at least 8 characters). After saving, you'll be redirected to sign in.",
    "supplier.loginInfo": "Register as a supplier and receive free inquiries from your region. Just enter your email – no password, no fees.",
    "supplier.registerInfo": "Create a supplier account to manage your price list and receive inquiries. Enter your company name, email and password.",
    "auth.continue": "Continue",
    "auth.invalidCredentials": "Invalid email or password",
    "auth.alreadyRegistered": "This email is already registered. Please log in.",
    "auth.switchToRegister": "Don't have an account? Sign up",
    "auth.switchToLogin": "Already have an account? Log in",
    "auth.forgotPassword": "Forgot your password?",
    "auth.backToLogin": "← Back to sign in",
    "register.title": "Create a free account",
    "register.subtitle": "Create a new account — just an email and password.",
    "register.email": "Email",
    "register.emailPlaceholder": "your@email.com",
    "register.password": "Password",
    "register.passwordPlaceholder": "At least 8 characters",
    "register.submit": "Sign up",
    "register.or": "or",
    "register.google": "Continue with Google",
    "register.hasAccount": "Already have an account?",
    "register.login": "Sign in",
    "dashboard.pricing": "Pricing",
    "dashboard.emptyTitle": "How can I help you?",
    "dashboard.emptySubtitle": "Write your inquiry or question",
    "dashboard.inputPlaceholder": "What are you looking for today?",
    "pricing.title": "Simple pricing",
    "pricing.subtitle": "Choose the plan that suits you",
    "pricing.free": "Free",
    "pricing.pro": "Pro",
    "pricing.agency": "Agency",
    "pricing.forever": "forever",
    "pricing.perMonth": "/month",
    "pricing.startFree": "Start for free",
    "pricing.choosePro": "Choose Pro",
    "pricing.contactUs": "Contact us",
    "pricing.feature.requests5": "5 requests/month",
    "pricing.feature.basicAI": "Basic AI answers",
    "pricing.feature.emailSupport": "Email support",
    "pricing.feature.unlimitedRequests": "Unlimited requests",
    "pricing.feature.advancedAI": "Advanced AI agent",
    "pricing.feature.prioritySupport": "Priority support",
    "pricing.feature.pdfExport": "PDF export",
    "pricing.feature.allPro": "Everything in Pro",
    "pricing.feature.teamAccounts": "Team accounts (5 users)",
    "pricing.feature.apiAccess": "API access",
    "pricing.feature.customBranding": "Custom branding",
    "pricing.feature.dedicatedManager": "Dedicated manager",
    "pricing.login": "Sign in",

    "supplier.headline": "Want to receive free inquiries that match your business?",
    "supplier.description": "Just enter your email – we'll start sending you relevant inquiries right away. No password, no fees.",
    "supplier.chatbotHeadline": "I want my own chatbot",
    "supplier.chatbotDescription": "Sign up and create an AI chatbot that will answer your customers.",
    "supplier.benefit1": "Inquiries from regions where you operate",
    "supplier.benefit2": "Filter by material type and volume",
    "supplier.benefit3": "Respond only to jobs that make sense for you",
    "supplier.emailPlaceholder": "you@company.com",
    "supplier.submitEmail": "Subscribe to inquiries for free",
    "supplier.noSpam": "No spam. Unsubscribe anytime.",
    "supplier.consent": "By submitting, you agree to receive inquiries via email and to our terms of service. You can unsubscribe at any time.",
    "supplier.registerHint": "Want to choose which inquiries you receive and quote on jobs? Register for free.",
    "supplier.preferencesTitle": "What work interests you?",
    "supplier.preferencesSubtitle": "Select the types of jobs you'd like to receive. You can change this anytime.",
    "supplier.savePreferences": "Complete registration",
    "supplier.magicLinkSent": "Email registered!",
    "supplier.checkInbox": "We'll send you relevant customer inquiries straight to your inbox.",
    "supplier.detailsTitle": "Tell us about yourself",
    "supplier.detailsSubtitle": "These details are optional but help us better target inquiries for you.",
    "supplier.namePlaceholder": "Your full name",
    "supplier.phonePlaceholder": "Phone, e.g. +1 555 123 4567",
    "supplier.saveDetails": "Save details",
    "supplier.skipStep": "Skip",
    "supplier.loginTitle": "Supplier login",
    "supplier.loginSubtitle": "Receive free inquiries straight to your inbox.",
    "supplier.noAccount": "Don't have a supplier account?",
    "supplier.register": "Sign up",
    "supplier.backToApp": "Back to cenik.ai",
    "supplier.registerTitle": "Supplier registration",
    "supplier.registerSubtitle": "Create an account and start pricing",
    "supplier.companyName": "Company name",
    "supplier.companyPlaceholder": "Your Company Ltd.",
    "supplier.registerBtn": "Sign up",
    "supplier.hasAccount": "Already have an account?",
    "supplier.loginLink": "Sign in",
    "supplier.passwordMin": "Password must be at least 8 characters",
    "supplier.checkEmail": "Check your email to activate your account",
    "supplier.icoPlaceholder": "Company ID, e.g. 12345678 (optional)",
    "supplier.addressPlaceholder": "Company address, e.g. London",
    "supplier.fillCompany": "Please fill in company name",
    "supplier.becomePartnerTitle": "Become a partner",
    "supplier.becomePartnerSubtitle": "Fill in your company details.",
    "supplier.continueToPreferences": "Create supplier",
    "supplier.registrationDone": "Registration complete! Welcome to the supplier dashboard.",
    "supplier.settingsTitle": "Settings",
    "supplier.tabCompany": "Company",
    "supplier.tabChatbot": "Chatbot",
    "supplier.tabPricing": "Pricing",
    "supplier.incompleteProfile": "Please fill in your information in the settings so your chatbot functions properly.",
    "supplier.goToSettings": "Go to settings",
    "supplier.passwordPlaceholder": "Password (min. 8 characters)",
    "supplier.dashboard": "Supplier admin",
    "supplier.logout": "Log out",
    "supplier.productsTitle": "My products & pricing",
    "supplier.productsSubtitle": "Add products and set price per m²",
    "supplier.addProduct": "Add product",
    "supplier.editProduct": "Edit product",
    "supplier.newProduct": "New product",
    "supplier.productName": "Product name",
    "supplier.productNamePlaceholder": "e.g. EPS 100 insulation",
    "supplier.pricePerM2": "Price per m² (CZK)",
    "supplier.productDescription": "Description",
    "supplier.descriptionPlaceholder": "Short product description...",
    "supplier.cancel": "Cancel",
    "supplier.save": "Save",
    "supplier.noProducts": "No products yet",
    "supplier.addFirstProduct": "Click 'Add product' to get started",
    "supplier.productAdded": "Product added",
    "supplier.productUpdated": "Product updated",
    "supplier.productDeleted": "Product deleted",
    "supplier.fillRequired": "Fill in name and price",
    "supplier.inquiriesTab": "Inquiries",
    "supplier.productsTab": "Products",
    "supplier.noInquiries": "No inquiries yet",
    "supplier.noInquiriesHint": "New inquiries will appear automatically",
    "supplier.alreadyPaid": "Quote already sent",
    "supplier.paymentError": "Payment error, please try again",
    "supplier.loginRequired": "You need to log in to pay",
    "supplier.submitQuoteTitle": "Your price quote",
    "supplier.submitQuoteHint": "Enter an estimated price excl. VAT. After submitting and paying 15 CZK, the quote will be delivered to the customer.",
    "supplier.quoteTotal": "Offered price (excl. VAT)",
    "supplier.quoteNotes": "Note",
    "supplier.quoteNotesPlaceholder": "What's included, timeline...",
    "supplier.submitQuoteBtn": "Submit quote & pay 15 CZK",
    "supplier.quoteSaved": "Quote saved!",
    "supplier.quoteAlreadySent": "Quote already submitted",
    "supplier.fillQuotePrice": "Please fill in the offered price",
    "supplier.quoteSent": "Sent",
    "supplier.quoteSentConfirm": "Quote has been sent to the customer",
    "supplier.quoteSentSuccess": "Quote sent successfully!",
    "supplier.waitingCustomerApproval": "Waiting for customer approval. Once they show interest, their contact details will appear.",
    "supplier.customerApproved": "Customer approved contact sharing",
    "supplier.contactVisibleNow": "Customer contact details are now visible above.",
    "supplier.share": "Share",
    "supplier.linkCopied": "Link copied",
    "supplier.shareFailed": "Sharing failed",
    "supplier.aboutQuery": "Tell me more about your company.",
    "supplier.aboutCompany": "About company",
    "supplier.slugSaved": "Slug saved",
    "supplier.slugTakenOrInvalid": "Slug is already taken or invalid",
    "supplier.connectionError": "Connection error",
    "supplier.paymentNotVerified": "Payment not verified. Try refreshing the page.",
    "supplier.paymentVerificationError": "Error verifying payment.",
    "supplier.savingError": "Error saving",
    "supplier.dataSaved": "Data saved",
    "supplier.edit": "edit",
    "supplier.onboardingTitle": "How do inquiries work for your company?",
    "supplier.onboardingStep1": "Set up your price list and areas where you want jobs.",
    "supplier.onboardingStep2": "Based on that, we'll show you new inquiries.",
    "supplier.onboardingStep3": "For selected inquiries, submit a quote and the customer will see your offer details.",
    "supplier.aiChatCalculator": "Your AI chat calculator",
    "supplier.freeTestVersion": "Free · test version",
    "supplier.checking": "Checking...",
    "supplier.slugAvailableLabel": "✓ Available",
    "supplier.slugTakenLabel": "✗ Taken",
    "supplier.savingLabel": "Saving...",
    "supplier.saveLabel": "Save",
    "supplier.cancel": "Cancel",
    "supplier.copyLink": "Copy link",
    "supplier.customDomainTag": "· custom domain",
    "supplier.customDomainLabel": "Custom domain: {domain}",
    "supplier.connectCustomDomain": "Connect custom domain",
    "supplier.enterDomainLabel": "Enter your domain (e.g. {example}):",
    "supplier.connectButton": "Connect",
    "supplier.verifiedLabel": "Verified ✓",
    "supplier.awaitingDns": "Awaiting DNS",
    "supplier.dnsStep1Title": "1. Set up DNS records at your registrar",
    "supplier.dnsStep1Instruction": "Log in to your domain management and add these records:",
    "supplier.mainDomainLabel": "For the main domain (e.g. {example}):",
    "supplier.dnsTypeHeader": "Type",
    "supplier.dnsNameHeader": "Name",
    "supplier.dnsValueHeader": "Value",
    "supplier.subdomainLabel": "For subdomain (e.g. {example}):",
    "supplier.subdomainHint": "For subdomain, replace {sub} with your name (e.g. {ex1}, {ex2}). CNAME record is recommended for subdomains.",
    "supplier.dnsStep2Title": "2. Add verification record",
    "supplier.dnsStep2Instruction": "This record verifies that the domain belongs to you:",
    "supplier.dnsSettingsTitle": "Where can I find DNS settings?",
    "supplier.dnsForpsi": "Administration → Domains → your domain → DNS records",
    "supplier.dnsActive24": "Domain detail → DNS management → Add record",
    "supplier.dnsWedos": "DNS management → Add record (type A or TXT)",
    "supplier.dnsCloudflare": "DNS → Records → Add record",
    "supplier.dnsPropagationHint": "DNS changes take effect within 5 minutes – 24 hours. After setup, click \"Verify DNS\".",
    "supplier.verifyDns": "Verify DNS",
    "supplier.disconnectDomain": "Disconnect domain",
    "supplier.chatbotSettingsLabel": "Chatbot and price list settings",
    "supplier.noPriceItemsWarning": "Without a price list, your company won't appear in offers.",
    "supplier.setPriceList": "Set up price list →",
    "supplier.readiness.setupCta": "Chatbot & pricing settings",
    "supplier.readiness.companyInfo": "Company info",
    "supplier.readiness.pricing": "Price list",
    "supplier.readiness.done": "Done",
    "supplier.readiness.missing": "Missing",
    "supplier.readiness.warningText": "To make your chatbot publicly available, you need to complete your company info and set up a price list.",
    "supplier.readiness.readyText": "Your chatbot is ready for public display.",
    "supplier.readiness.fallbackTitle": "Chatbot not yet available",
    "supplier.readiness.fallbackDescription": "This chatbot is currently being set up. Please try again later.",
    "supplier.ownChatbot": "Own chatbot",
    "supplier.marketplaceInquiries": "Inquiries from cenik.ai",
    "supplier.filterNew": "New",
    "supplier.filterQuoteSent": "Quote sent",
    "supplier.filterHidden": "Hidden",
    "supplier.chatbotBadge": "Chatbot",
    "supplier.marketplaceBadge": "cenik.ai",
    "supplier.requestMaterial": "Material request",
    "supplier.quoteAction": "Quote",
    "supplier.restore": "Restore",
    "supplier.hideAction": "Hide",
    "supplier.marketplaceUpsellTitle": "Inquiries from cenik.ai marketplace",
    "supplier.marketplaceUpsellDescription": "Get access to marketplace inquiries with a Premium plan",
    "supplier.premiumLabel": "Premium",
    "supplier.approved": "Approved",
    "customer.allowContact": "I want this supplier to contact me",
    "customer.approved": "Contact shared",
    "customer.contactShared": "The supplier can now see your contact details",
    "customer.supplierContact": "Supplier contact:",

    "filter.title": "Inquiry filter",
    "filter.workType": "Work type",
    "filter.allTypes": "All types",
    "filter.location": "Construction site",
    "filter.locationPlaceholder": "e.g. London, Manchester...",
    "filter.priceFrom": "Price from",
    "filter.priceTo": "Price to",
    "filter.deadline": "Deadline",
    "filter.deadlinePlaceholder": "e.g. May 2026, ASAP...",
    "filter.lock": "Lock filter",
    "filter.apply": "Show results",
    "filter.save": "Save & get notifications",
    "filter.unlock": "Unlock filter",
    "filter.locked": "Locked",
    "filter.wholeCountry": "Whole Czech Republic",
    "filter.reset": "Reset",
    "filter.lockedHint": "Filter is locked – new inquiries matching your criteria will be sent to your notifications and email. Unlock to make changes.",
    "filter.noResults": "No inquiries match the filter",
    "filter.noResultsHint": "Try adjusting or resetting the filter",
    "filter.searchCity": "Search city or town...",
    "filter.radius": "Radius",
    "filter.addWholeCZ": "+ Entire Czech Republic",
    "filter.wholeCZ": "Entire Czech Republic",
    "filter.noLocationsSet": "No locations set",
    "filter.searching": "Searching",
    "filter.locationAreas": "Service areas",
    "filter.locationSaved": "Areas saved",

    "register.checkEmail": "Check your email to activate",
    "register.emailSent": "Email sent!",
    "register.checkEmailDesc": "Check your inbox and click the confirmation link. Then you can sign in.",
    "register.goToLogin": "Go to sign in",
    "profile.title": "Profile settings",
    "profile.personalInfo": "Personal information",
    "profile.fullName": "Full name",
    "profile.fullNamePlaceholder": "John Doe",
    "profile.phone": "Phone",
    "profile.phonePlaceholder": "+1 234 567 890",
    "profile.save": "Save",
    "profile.saved": "Profile saved",
    "profile.supplierPanel": "Supplier Panel",
    "profile.sitesTitle": "Projects",
    "profile.sitesSubtitle": "Manage your projects and construction sites",
    "profile.siteNamePlaceholder": "Project name (e.g. Family house)",
    "profile.siteAddressPlaceholder": "Project address",
    "profile.addSite": "Save project",
    "profile.siteAdded": "Project added",
    "profile.siteDeleted": "Project removed",
    "profile.siteSaved": "Project saved",
    "sidebar.settings": "Settings",
    "sidebar.logout": "Sign out",
    "sidebar.aboutUs": "About us",
    "sidebar.assignTo": "Assign to project",
    "sidebar.unassign": "Remove from project",
    "sidebar.deleteChat": "Delete chat",
    "sidebar.chatDeleted": "Chat deleted",
    "sidebar.chatAssigned": "Chat assigned",
    "sidebar.noProjectsYet": "No projects yet",
    "sidebar.noSearchResults": "No results",
    "sidebar.history": "History",
    "sidebar.inquiriesAndQuotes": "Inquiries & Quotes",
    "sidebar.noInquiriesYet": "No inquiries yet",
    "sidebar.quote": "quote",
    "sidebar.quotes": "quotes",
    "sidebar.backToChat": "Back to chat",
    "sidebar.aboutUsResponse": "## About cenik.ai 👋\n\nWe're here to **change how pricing and inquiries work in the construction industry**.\n\nWe believe getting a fair price for a facade, insulation, or any construction work should be simple – both for those **who request quotes** and those **who do the work**.\n\nThe project is run by **Adam Černohous, IČO: 76535398**, registered at Třebovská 65, Ústí nad Orlicí 56203.\n\n📧 Contact: **info@cenik.ai**",
    "dashboard.newProject": "New project",
    "notifications.title": "Notifications",
    "notifications.empty": "No notifications",
    "notifications.newInquiry": "New inquiry",
    "notifications.loginPrompt": "Your supplier offers will appear here. Log in to view them.",

    // Customer dashboard
    "customerDashboard.title": "My inquiries",
    "customerDashboard.newInquiry": "New inquiry",
    "customerDashboard.onboardingTitle": "How does cenik.ai work for you?",
    "customerDashboard.onboardingStep1": "Ask the chat for a price estimate for insulation or facade.",
    "customerDashboard.onboardingStep2": "The chat creates an inquiry from your answers.",
    "customerDashboard.onboardingStep3": "We send the inquiry to companies in your area and their quotes will appear right here.",
    "customerDashboard.emptyTitle": "You don't have any inquiries yet",
    "customerDashboard.emptyDesc": "Open the chat on the main page and get your first estimate.",
    "customerDashboard.statusOpen": "Open",
    "customerDashboard.statusInProgress": "In progress",
    "customerDashboard.statusClosed": "Closed",
    "customerDashboard.statusCompleted": "Completed",
    "customerDashboard.quote": "quote",
    "customerDashboard.quotes": "quotes",
    "customerDashboard.quotesMany": "quotes",

    // Supplier chat
    "supplierChat.newChat": "New chat",
    "supplierChat.inquiry": "Inquiry",
    "supplierChat.message": "Message",
    "supplierChat.upsellTitle": "Want your own chatbot?",
    "supplierChat.upsellDesc": "Get an AI assistant for your company with your own pricing and branding.",
    "supplierChat.upsellLink": "Learn more →",
    "supplierChat.aboutUs": "About us",
    "supplierChat.msgSavedNotifyFailed": "Message saved, but notification to the company failed to send.",
    "supplierChat.msgDeliveryFailed": "Failed to deliver the message to the company. Please try again.",
    "supplierChat.inquiryDeliveryFailed": "Failed to deliver the inquiry to the company. Please try again.",
    "supplierChat.error": "Error: {error}",
    "supplierChat.chatNotFound": "AI chat not found",
    "supplierChat.domainNotLinked": "This domain is not yet linked to any pricing.",
    "supplierChat.slugNotFound": "does not exist.",
    "supplierChat.backToHome": "← Back to main page",
    "supplierChat.askAboutCompany": "Tell me more about your company.",
    "supplierChat.inputPlaceholder": "Type your question or parameters…",
    "supplierChat.aboutCompany": "About {name}",
    "supplierChat.specialization": "Specialization",
    "supplierChat.serviceArea": "Service area",
    "supplierChat.aiAssistantHelp": "What can the AI assistant help you with?",
    "supplierChat.aiHelp1": "Estimated cost of construction work",
    "supplierChat.aiHelp2": "Availability and types of materials",
    "supplierChat.aiHelp3": "Answers to expert questions",
    "supplierChat.aiHelp4": "Preliminary calculation based on your parameters",
    "supplierChat.faq": "FAQ",
    "supplierChat.contact": "Contact",

    // Sidebar extras
    "sidebar.currentChat": "Current chat",
    "sidebar.messageCount": "messages",

    // Supplier settings
    "supplierSettings.companyName": "Company name *",
    "supplierSettings.ico": "Business ID",
    "supplierSettings.contactEmail": "Contact email",
    "supplierSettings.contactPhone": "Contact phone",
    "supplierSettings.region": "Region",
    "supplierSettings.regionPlaceholder": "e.g. Prague, South Moravia",
    "supplierSettings.description": "Company description",
    "supplierSettings.descriptionPlaceholder": "Short description of your company...",
    "supplierSettings.saving": "Saving...",
    "supplierSettings.saveCompany": "Save company details",
    "supplierSettings.savedCompany": "Company details saved",
    "supplierSettings.saveError": "Error saving",
    "supplierSettings.saveFailed": "Failed to save",
    "supplierSettings.chatbotDesc": "Customize how your chatbot introduces itself to customers. In the free version, the bot does not share contact details.",
    "supplierSettings.botName": "Chatbot name",
    "supplierSettings.botNameHint": "If left empty, the bot will use your company name.",
    "supplierSettings.botIntro": "Bot introduction",
    "supplierSettings.botIntroPlaceholder": "I am the AI assistant of company XY, I can help you with questions about facades and insulation.",
    "supplierSettings.aboutCompany": "About company",
    "supplierSettings.aboutCompanyPlaceholder": "Brief info about the company, specialization, experience, service area...",
    "supplierSettings.faq": "FAQ – frequently asked questions",
    "supplierSettings.faqPlaceholder": "Question: What materials do you use?\nAnswer: EPS, mineral wool, ETICS systems...\n\nQuestion: What is your price per m²?\nAnswer: The price depends on the scope of work, usually...",
    "supplierSettings.saveBot": "Save settings",
    "supplierSettings.savedBot": "Chatbot settings saved.",
    "supplierSettings.saveBotFailed": "Failed to save settings.",
    "supplierSettings.tabServices": "Services & pricing",
    "supplierSettings.tabPublicPage": "Public page",
    "supplierSettings.publicPageDesc": "Set up how your public AI page appears in search engines and social media.",
    "supplierSettings.tagline": "Short tagline",
    "supplierSettings.taglinePlaceholder": "e.g. Facade works turnkey in Prague",
    "supplierSettings.taglineHint": "Displayed below the company name on your public page.",
    "supplierSettings.businessType": "Industry / service type",
    "supplierSettings.businessTypePlaceholder": "Facade works",
    "supplierSettings.serviceArea": "Service area",
    "supplierSettings.serviceAreaPlaceholder": "Prague and Central Bohemia",
    "supplierSettings.seoTitle": "SEO page title",
    "supplierSettings.seoTitleHint": "Shown in the browser tab and search results.",
    "supplierSettings.seoDesc": "SEO page description",
    "supplierSettings.seoDescHint": "Shown below the title in search results.",
    "supplierSettings.ogImage": "OG image URL (optional)",
    "supplierSettings.ogImageHint": "Image shown when sharing on social media. Ideally 1200×630 px.",
    "supplierSettings.searchPreview": "Search engine preview",
    "supplierSettings.savePublicPage": "Save page settings",
    "supplierSettings.savedPublicPage": "Public page settings saved.",
    "supplierSettings.servicesHeading": "What services do you offer?",
    "supplierSettings.servicesDesc": "Select the services your company provides. You can add descriptions and pricing for each.",
    "supplierSettings.addCustomService": "Add custom service…",
    "supplierSettings.addButton": "Add",
    "supplierSettings.serviceAlreadyAdded": "This service is already added",
    "supplierSettings.serviceAdded": "Service \"{name}\" added",
    "supplierSettings.selectServiceFirst": "Select at least one service above, then add a description and pricing.",
    "supplierSettings.customerMessages": "Customer messages",
    "supplierSettings.translate": "Translate",
    "supplierSettings.showOriginal": "Show original",

    // Admin
    "admin.title": "Admin Panel",
    "admin.loginPrompt": "Sign in with an admin account",
    "admin.loginButton": "Sign in",
    "admin.email": "Email",
    "admin.password": "Password",
    "admin.googleLogin": "Sign in with Google",
    "admin.orPassword": "or with password",
    "admin.noPermission": "This account doesn't have admin permissions",
    "admin.loginFailed": "Login failed",
    "admin.googleLoginFailed": "Google login failed",
    "admin.sessionExpired": "Session expired, please sign in again",
    "admin.registrationFailed": "Registration failed",
    "admin.accountCreated": "Admin account created and signed in",
    "admin.loginExpired": "Login expired, please sign in again",
    "admin.tab.chats": "Chats",
    "admin.tab.inquiries": "Inquiries",
    "admin.tab.users": "Users",
    "admin.tab.prices": "Prices",
    "admin.tab.services": "Services",
    "admin.tab.subscriptions": "Subscriptions",
    "admin.tab.feedback": "Feedback",
    "admin.tab.knowledge": "Knowledge",
    "admin.toast.saved": "Saved",
    "admin.toast.deleted": "Deleted",
    "admin.toast.saveFailed": "Failed to save",
    "admin.toast.deleteFailed": "Failed to delete",
    "admin.toast.loadFailed": "Failed to load data",
    "admin.toast.statusUpdated": "Status updated",
    "admin.toast.activated": "Activated",
    "admin.toast.deactivated": "Deactivated",
    "admin.toast.correctionSaved": "Correction saved",
    "admin.toast.correctionSaveFailed": "Failed to save correction",
    "admin.toast.feedbackIgnored": "Feedback dismissed",
    "admin.toast.feedbackIgnoreFailed": "Failed to dismiss feedback",
    "admin.toast.knowledgeAdded": "Knowledge added",
    "admin.toast.knowledgeSaved": "Knowledge saved",
    "admin.toast.aiCategorized": "AI suggested category and name",
    "admin.toast.serviceCreated": "Service created",
    "admin.toast.createServiceFailed": "Failed to create service",
    "admin.toast.serviceActivated": "Service activated",
    "admin.toast.serviceRejected": "Service rejected",
    "admin.toast.movedToQueue": "Moved to queue",
    "admin.toast.movedToCandidate": "Moved to candidate",
    "admin.toast.serviceAssigned": "Service assigned",
    "admin.toast.newServiceProposed": "New service proposed and assigned",
    "admin.toast.aiNoMatch": "AI found no matching service",
    "admin.toast.detectionFixed": "Detection fixed + learning created",
    "admin.toast.detectionFixFailed": "Failed to fix detection",
    "admin.toast.detectionRejected": "Detection rejected",
    "admin.toast.detectionRejectFailed": "Failed to reject",
    "admin.toast.overrideSaved": "Override saved",
    "admin.toast.overrideSaveFailed": "Failed to save override",
    "admin.toast.planChangeFailed": "Failed to change plan",
    "admin.toast.featureToggleFailed": "Failed to toggle feature",
    "admin.toast.fillTopicContent": "Fill in topic and content",
    "admin.toast.fillNameSlug": "Fill in name and slug",
    "admin.toast.invalidSlug": "Slug can only contain lowercase letters, numbers and hyphens",
    "admin.toast.learningAdded": "Learning added – AI will start using it immediately",
    "admin.toast.addFailed": "Failed to add",
    "admin.toast.inquiryDeleted": "Inquiry deleted",
    "admin.toast.writeCorrectedAnswer": "Write the corrected answer",
    "admin.toast.serviceSaved": "Service saved",
    "admin.toast.generateFailed": "Generation failed",
    "admin.toast.formApproved": "Form approved — suppliers will see it",
    "admin.toast.approveFailed": "Failed to approve",
    "admin.toast.formToDraft": "Form switched to draft",
    "admin.toast.operationFailed": "Operation failed",
    "admin.toast.keywordApproved": "Keyword approved",
    "admin.toast.keywordRejected": "Keyword rejected",
    "admin.toast.keywordAdded": "Keyword added",
    "admin.toast.keywordDeleted": "Keyword deleted",
    "admin.status.active": "Active",
    "admin.status.inactive": "Inactive",
    "admin.status.candidate": "Candidate",
    "admin.status.queued": "Queued",
    "admin.status.rejected": "Rejected",
    "admin.status.positive": "Positive",
    "admin.status.negative": "Negative",
    "admin.empty.inquiries": "No inquiries",
    "admin.empty.suppliers": "No suppliers",
    "admin.empty.customers": "No customers",
    "admin.empty.feedback": "No user feedback yet",
    "admin.confirm.deleteInquiry": "Really delete this inquiry?",
    "admin.confirm.deleteLearning": "Really delete this learning?",
    "admin.btn.approve": "Approve",
    "admin.btn.reject": "Reject",
    "admin.btn.addItem": "Add item",
    "admin.btn.saveDraft": "Save draft",
    "admin.btn.approveAndDeploy": "Approve and deploy",
    "admin.btn.regenerate": "Regenerate",
    "admin.btn.editForm": "Edit form",
    "admin.btn.collapse": "Collapse",
    "admin.btn.showAll": "Show all",
    "admin.btn.ignore": "Dismiss",
    "admin.btn.save": "Save",
    "admin.btn.cancel": "Cancel",
    "admin.btn.addKnowledge": "Add knowledge for",
    "admin.btn.restoreCandidate": "Restore as candidate",
    "admin.btn.removeOverride": "Remove override",
    "admin.btn.disable": "Disable",
    "admin.btn.enable": "Enable",
    "admin.section.quoteForm": "Quote form for suppliers",
    "admin.section.noQuoteForm": "No quote form yet. AI will suggest budget items for this service.",
    "admin.section.costItems": "Cost items",
    "admin.section.formPreview": "Form preview for suppliers",
    "admin.section.serviceStatus": "Service status",
    "admin.section.feedbackHelp": "AI response ratings. Negative feedback with correction is immediately applied as AI learning. Dismiss suspicious ratings.",
    "admin.section.totalRatings": "Total ratings",
    "admin.section.aiKnowledge": "AI uses this knowledge when answering customers and generating quotes for this service.",
    "admin.placeholder.knowledgeName": "Knowledge name",
    "admin.placeholder.knowledgeContent": "Paste text about insulation, prices, materials... AI will suggest a name and category.",
    "admin.placeholder.learningContent": "Knowledge content — what AI should know and how to behave...",
    "admin.placeholder.itemName": "Item name",
    "admin.placeholder.keyword": "New keyword or phrase...",
    "admin.placeholder.serviceDesc": "Short service description",
    "admin.placeholder.priceName": "e.g. EPS Prices",
    "admin.placeholder.newServiceName": "New service name:",
    "admin.label.totalPrice": "Total price",
    "admin.label.currency": "CZK",
    "admin.label.records": "records",
    "admin.label.offer": "quote",
    "admin.label.offers": "quotes",
    "admin.label.offersMany": "quotes",
    "admin.label.inquirySent": "Inquiry ✓",
    "admin.label.inquiryNotSent": "No inquiry",
    "admin.label.formDraft": "📝 Draft",
    "admin.label.formApproved": "✅ Form",
    "admin.label.user": "👤 User",
    "admin.label.ai": "🤖 AI",
    "admin.label.advancedCalc": "Advanced calculation",
    "admin.label.weight": "Weight",
    "admin.label.serviceNotFound": "Service not found",
    "admin.label.generating": "Generating…",
    "admin.label.generateForm": "🤖 Generate quote form",
    "admin.label.analyzing": "Analyzing...",
    "admin.label.aiCategorize": "🤖 AI categorize",
    "admin.label.newServiceProposal": "New service proposal",
    "admin.label.uncertainMatch": "Uncertain match",
    "admin.label.match": "Match",
    "admin.label.new": "New",
    "admin.label.uncertain": "Uncertain",
    "admin.filter.all": "All",
    "admin.filter.sent": "Inquiry sent",
    "admin.filter.notSent": "Not sent",
    "admin.module.walls": "Walls",
    "admin.module.material": "Material inquiry",
    "admin.module.quoteCalc": "Quote calculation",
    "admin.module.generalInquiry": "General inquiry",
    "admin.module.simpleQuote": "Simple quote",
    "admin.material.epsWhite": "White EPS",
    "admin.material.epsGrey": "Grey EPS",
    "admin.material.mineralWool": "Mineral wool",
    "admin.material.ventilatedFacade": "Ventilated facade",
    "admin.section.basicInfo": "Basic info",
    "admin.section.keywordsDetection": "Keywords (detection)",
    "admin.section.keywordsHelp": "Approved keywords are used for hybrid service detection. Suggested keywords await review.",
    "admin.section.modules": "Modules",
    "admin.section.knowledgeFor": "AI knowledge for",
    "admin.label.name": "Name",
    "admin.label.slugUrl": "Slug (URL)",
    "admin.label.keywordsComma": "Keywords (comma-separated, for keyword matching)",
    "admin.label.descOptional": "Description (optional)",
    "admin.label.approved": "Approved",
    "admin.label.suggested": "Suggested",
    "admin.label.rejectedKw": "Rejected",
    "admin.label.profit": "Profit",
    "admin.label.vat": "VAT",
    "admin.label.unitLabel": "Unit:",
    "admin.label.vatRate": "VAT rate (12% / 21%)",
    "admin.label.query1": "query",
    "admin.label.query2to4": "queries",
    "admin.label.query5plus": "queries",
    "admin.label.record1": "record",
    "admin.label.record2to4": "records",
    "admin.label.learningActive": "✓ Active",
    "admin.label.learningInactive": "○ Inactive",
    "admin.label.formApprovedBadge": "✅ Approved",
    "admin.label.formDraftBadge": "📝 Draft",
    "admin.label.facadeNote": "ℹ️ Facade works have a built-in form (material, scaffolding, labor, transport). If you generate a new one, it will replace the built-in form.",
    "admin.label.fact": "📚 Fact",
    "admin.label.correction": "🔧 Correction",
    "admin.label.tip": "💡 Tip",
    "admin.btn.refresh": "Refresh",
    "admin.btn.backToOverview": "← Back to overview",
    "admin.btn.add": "Add",
    "admin.btn.activate": "Activate",
    "admin.btn.toQueue": "To queue",
    "admin.placeholder.keywordsExample": "facade, insulation, eps, polystyrene",
    "admin.placeholder.learningTopic": "Topic (e.g. SDK partition price)",
    "admin.toast.aiSuggestedForm": "AI suggested a form — review and approve",
    "admin.toast.error": "Error",
    "admin.module.wallsDesc": "Customer enters walls with dimensions and openings. Supplier sees detailed area breakdown.",
    "admin.module.materialDesc": "Supplier requests material from wholesalers directly from the inquiry.",
    "admin.module.quoteCalcDesc": "Itemized budget with prices per m². Generative AI quote for the customer.",
    "admin.module.generalInquiryDesc": "AI-generated inquiry description from chat.",
    "admin.module.simpleQuoteDesc": "Supplier enters total price + notes.",
  },

  de: {
    "topbar.login": "Anmelden",
    "topbar.register": "Kostenlos registrieren",
    "topbar.comingSoon": "Weitere in Vorbereitung",
    "topbar.facades": "Fassadenarbeiten",
    "landing.title": "Was kostet Ihr Bau?",
    "landing.subtitle": "Wählen Sie die Art der Bauarbeit und erhalten Sie in Minuten eine Schätzung.",
    "landing.service.fasady": "Fassadenarbeiten",
    "landing.service.fasady.desc": "Geschätzte Kosten für Fassade, Dämmung und Putz",
    "service.fasadnicke-prace": "Fassadenarbeiten",
    "landing.service.strechy": "Dächer",
    "landing.service.okna": "Fenster & Türen",
    "landing.startChat": "Preis erfahren",
    "landing.comingSoon": "In Vorbereitung",
    "index.headline1": "Was kostet Ihr Bauprojekt?",
    "index.headline2": "Berechnen Sie die Baukosten",
    "index.headline3": "Erfahren Sie den Richtpreis online",
    "index.headline4": "Bauarbeiten online kalkulieren",
    "index.headline5": "Kostenvoranschlag in einer Minute",
    "index.disclaimer": "Wenn Sie eine Nachricht an cenik.ai senden, einen KI-basierten Chatbot, stimmen Sie unseren",
    "index.terms": "Nutzungsbedingungen",
    "index.privacyConfirm": "zu und bestätigen, dass Sie unsere",
    "index.privacy": "Datenschutzrichtlinie",
    "index.cookiesSee": "Siehe",
    "index.cookies": "Cookie-Einstellungen",
    "cookie.message": "Diese Website verwendet Cookies, um die ordnungsgemäße Funktion sicherzustellen und den Datenverkehr zu analysieren.",
    "cookie.accept": "Alle akzeptieren",
    "cookie.reject": "Optionale ablehnen",
    "cookie.settings": "Einstellungen",
    "index.sensitiveWarning": "Teilen Sie keine sensiblen persönlichen Daten (Ausweisnummern, Passwörter, Kontonummern).",
    "index.nextQuestion": "Nächste Frage eingeben...",
    "index.mayMakeErrors": "cenik.ai kann Fehler machen. Überprüfen Sie wichtige Informationen.",
    "index.leadSaved": "Kontaktdaten gesendet – wir melden uns bald! 🏠",
    "sidebar.newChat": "Neuer Chat",
    "sidebar.searchChats": "Chats suchen",
    "sidebar.projects": "Projekte",
    "sidebar.howToUse": "So funktioniert's",
    "sidebar.howToUseQuery": "Wie funktioniert es?",
    "sidebar.howToUseResponse": "Willkommen bei **cenik.ai** 👋\n\nSo funktioniert es:\n\n1. **Beschreiben Sie, was Sie brauchen** – Art der Bauarbeit, Abmessungen, Material.\n2. **Die KI berechnet einen Richtpreis** – basierend auf aktuellen Marktdaten.\n3. **Stellen Sie Nachfragen** – zu Materialien, Technologien oder vergleichen Sie Varianten.\n4. **Möchten Sie ein unverbindliches Angebot?** Hinterlassen Sie Ihre Kontaktdaten und wir verbinden Sie mit geprüften Lieferanten.\n\n💡 **Tipp:** Sie können auch direkt zum **Kalkulator** gehen und alles selbst ausfüllen.\n\nBeginnen Sie damit, zu beschreiben, was Sie kalkulieren möchten 🏠",
    "sidebar.yourChats": "Chat-Verlauf",
    "sidebar.noChatsYet": "Noch keine Chats",
    "sidebar.loginForHistory": "Melden Sie sich an, um den Chat-Verlauf anzuzeigen.",
    "sidebar.loginLink": "Melden Sie sich an",
    "sidebar.loginForFeature": "Melden Sie sich an, um diese Funktion zu nutzen.",
    "sidebar.loginForInquiries": "Melden Sie sich an, um Anfragen und Angebote anzuzeigen.",
    "sidebar.becomeSupplier": "Ich möchte meinen eigenen Chatbot",
    "sidebar.supplierDashboard": "Lieferanten-Dashboard",
    "sidebar.supplierCta": "Möchten Sie kostenlose Anfragen per E-Mail erhalten?",
    "sidebar.login": "Anmelden",
    "chat.placeholder1": "Was möchten Sie kalkulieren?",
    "chat.placeholder2": "Welche Abmessungen oder Fläche?",
    "chat.placeholder3": "Welches Material bevorzugen Sie?",
    "chat.placeholder4": "Wo befindet sich das Bauvorhaben?",
    "chat.placeholder5": "Fragen zu Bauarbeiten?",
    "chat.placeholderMobile1": "Was kalkulieren?",
    "chat.placeholderMobile2": "Welche Abmessungen?",
    "chat.placeholderMobile3": "Welches Material?",
    "chat.placeholderMobile4": "Wo ist das Bauvorhaben?",
    "chat.placeholderMobile5": "Fragen zum Bau?",
    "chat.takePhoto": "Foto aufnehmen",
    "chat.addPhotos": "Fotos hinzufügen",
    "chat.uploadFiles": "Dateien hochladen",
    "chat.fileTooLarge": "Datei \"{name}\" ist größer als 10 MB.",
    "chat.assignToProject": "Zum Projekt zuordnen",
    "chat.unassign": "Aus Projekt entfernen",
    "chat.deleteChat": "Chat löschen",
    "chat.loginToSave": "Anmelden und speichern",
    "suggestion.facadePrice": "Fassadenpreis berechnen",
    "suggestion.insulationCost": "Was kosten Bauarbeiten?",
    "suggestion.bestPolystyrene": "Welches Material ist das beste?",
    "suggestion.contactInsulation": "Richtpreis erfahren",
    "login.title": "Anmelden",
    "login.email": "E-Mail",
    "login.emailPlaceholder": "ihre@email.de",
    "login.password": "Passwort",
    "login.passwordPlaceholder": "••••••••",
    "login.submit": "Anmelden",
    "login.or": "oder",
    "login.google": "Weiter mit Google",
    "login.noAccount": "Kein Konto?",
    "login.register": "Registrieren",
    "login.forgotPassword": "Passwort vergessen?",
    "login.subtitle": "Geben Sie Ihre E-Mail und Ihr Passwort ein, um sich anzumelden.",
    "login.chatbotBoxTitle": "Haben Sie ein Unternehmen?",
    "login.chatbotBoxDesc": "Erstellen Sie Ihren eigenen KI-Chatbot für Ihre Kunden — kostenlos.",
    "login.becomeSupplier": "Ich möchte meinen eigenen Chatbot",
    "forgotPassword.title": "Passwort zurücksetzen",
    "forgotPassword.description": "Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen.",
    "forgotPassword.submit": "Link senden",
    "forgotPassword.sent": "Link gesendet!",
    "forgotPassword.sentDesc": "Überprüfen Sie Ihr Postfach und klicken Sie auf den Link zum Zurücksetzen.",
    "forgotPassword.backToLogin": "Zurück zur Anmeldung",
    "resetPassword.title": "Neues Passwort",
    "resetPassword.password": "Neues Passwort",
    "resetPassword.passwordPlaceholder": "Mindestens 8 Zeichen",
    "resetPassword.submit": "Passwort setzen",
    "resetPassword.success": "Passwort geändert",
    "login.info": "Melden Sie sich mit E-Mail und Passwort oder über Google an. Nach der Anmeldung haben Sie Zugriff auf Ihren Chatverlauf und gespeicherte Projekte.",
    "register.info": "Erstellen Sie ein kostenloses Konto zum Speichern von Chats, Verwalten von Projekten und Baustellen. Nur eine E-Mail und ein Passwort oder Google-Konto nötig.",
    "forgotPassword.info": "Geben Sie die E-Mail-Adresse ein, mit der Sie sich registriert haben. Wir senden Ihnen einen Link zum Setzen eines neuen Passworts.",
    "resetPassword.info": "Geben Sie ein neues Passwort ein (mindestens 8 Zeichen). Nach dem Speichern werden Sie zur Anmeldung weitergeleitet.",
    "supplier.loginInfo": "Registrieren Sie sich als Lieferant und erhalten Sie kostenlose Anfragen aus Ihrer Region. Einfach E-Mail eingeben – kein Passwort, keine Gebühren.",
    "supplier.registerInfo": "Erstellen Sie ein Lieferantenkonto zur Verwaltung Ihrer Preisliste und zum Empfang von Anfragen. Geben Sie Firmenname, E-Mail und Passwort ein.",
    "auth.continue": "Weiter",
    "auth.invalidCredentials": "Falsche E-Mail oder Passwort",
    "auth.alreadyRegistered": "Diese E-Mail ist bereits registriert. Bitte melden Sie sich an.",
    "auth.switchToRegister": "Haben Sie kein Konto? Registrieren Sie sich",
    "auth.switchToLogin": "Haben Sie bereits ein Konto? Anmelden",
    "auth.forgotPassword": "Passwort vergessen?",
    "auth.backToLogin": "← Zurück zur Anmeldung",
    "register.title": "Kostenloses Konto erstellen",
    "register.subtitle": "Erstellen Sie ein neues Konto — nur E-Mail und Passwort erforderlich.",
    "register.email": "E-Mail",
    "register.emailPlaceholder": "ihre@email.de",
    "register.password": "Passwort",
    "register.passwordPlaceholder": "Mindestens 8 Zeichen",
    "register.submit": "Registrieren",
    "register.or": "oder",
    "register.google": "Weiter mit Google",
    "register.hasAccount": "Bereits ein Konto?",
    "register.login": "Anmelden",
    "dashboard.pricing": "Preise",
    "dashboard.emptyTitle": "Wie kann ich Ihnen helfen?",
    "dashboard.emptySubtitle": "Schreiben Sie Ihre Anfrage oder Frage",
    "dashboard.inputPlaceholder": "Was suchen Sie heute?",
    "pricing.title": "Einfache Preisgestaltung",
    "pricing.subtitle": "Wählen Sie den passenden Plan",
    "pricing.free": "Free",
    "pricing.pro": "Pro",
    "pricing.agency": "Agency",
    "pricing.forever": "für immer",
    "pricing.perMonth": "/Monat",
    "pricing.startFree": "Kostenlos starten",
    "pricing.choosePro": "Pro wählen",
    "pricing.contactUs": "Kontaktieren Sie uns",
    "pricing.feature.requests5": "5 Anfragen/Monat",
    "pricing.feature.basicAI": "Basis-KI-Antworten",
    "pricing.feature.emailSupport": "E-Mail-Support",
    "pricing.feature.unlimitedRequests": "Unbegrenzte Anfragen",
    "pricing.feature.advancedAI": "Erweiterter KI-Agent",
    "pricing.feature.prioritySupport": "Prioritäts-Support",
    "pricing.feature.pdfExport": "PDF-Export",
    "pricing.feature.allPro": "Alles aus Pro",
    "pricing.feature.teamAccounts": "Team-Konten (5 Benutzer)",
    "pricing.feature.apiAccess": "API-Zugang",
    "pricing.feature.customBranding": "Eigenes Branding",
    "pricing.feature.dedicatedManager": "Dedizierter Manager",
    "pricing.login": "Anmelden",

    "supplier.headline": "Möchten Sie kostenlos Anfragen erhalten, die Sie interessieren?",
    "supplier.description": "Geben Sie einfach Ihre E-Mail ein – wir senden Ihnen sofort relevante Anfragen. Kein Passwort, keine Gebühren.",
    "supplier.chatbotHeadline": "Ich möchte meinen eigenen Chatbot",
    "supplier.chatbotDescription": "Registrieren Sie sich und erstellen Sie einen KI-Chatbot, der Ihren Kunden antwortet.",
    "supplier.benefit1": "Anfragen aus Regionen, in denen Sie tätig sind",
    "supplier.benefit2": "Filtern nach Materialart und Volumen",
    "supplier.benefit3": "Reagieren Sie nur auf Aufträge, die für Sie sinnvoll sind",
    "supplier.emailPlaceholder": "sie@firma.de",
    "supplier.submitEmail": "Anfragen kostenlos abonnieren",
    "supplier.noSpam": "Kein Spam. Jederzeit abmelden.",
    "supplier.consent": "Mit dem Absenden stimmen Sie dem Erhalt von Anfragen per E-Mail und unseren Nutzungsbedingungen zu. Sie können sich jederzeit abmelden.",
    "supplier.registerHint": "Möchten Sie auswählen, welche Anfragen Sie erhalten, und Aufträge anbieten? Registrieren Sie sich kostenlos.",
    "supplier.preferencesTitle": "Welche Arbeiten interessieren Sie?",
    "supplier.preferencesSubtitle": "Wählen Sie die Auftragsarten, die Sie erhalten möchten. Jederzeit änderbar.",
    "supplier.savePreferences": "Registrierung abschließen",
    "supplier.magicLinkSent": "E-Mail registriert!",
    "supplier.checkInbox": "Wir senden Ihnen relevante Kundenanfragen direkt an Ihre E-Mail.",
    "supplier.detailsTitle": "Erzählen Sie uns von sich",
    "supplier.detailsSubtitle": "Diese Angaben sind optional, helfen uns aber, Anfragen besser auf Sie abzustimmen.",
    "supplier.namePlaceholder": "Ihr vollständiger Name",
    "supplier.phonePlaceholder": "Telefon, z.B. +49 170 1234567",
    "supplier.saveDetails": "Daten speichern",
    "supplier.skipStep": "Überspringen",
    "supplier.loginTitle": "Lieferanten-Login",
    "supplier.loginSubtitle": "Erhalten Sie kostenlose Anfragen direkt per E-Mail.",
    "supplier.noAccount": "Kein Lieferantenkonto?",
    "supplier.register": "Registrieren",
    "supplier.backToApp": "Zurück zu cenik.ai",
    "supplier.registerTitle": "Lieferanten-Registrierung",
    "supplier.registerSubtitle": "Erstellen Sie ein Konto und beginnen Sie mit der Preisgestaltung",
    "supplier.companyName": "Firmenname",
    "supplier.companyPlaceholder": "Ihre Firma GmbH",
    "supplier.registerBtn": "Registrieren",
    "supplier.hasAccount": "Bereits ein Konto?",
    "supplier.loginLink": "Anmelden",
    "supplier.passwordMin": "Passwort muss mindestens 8 Zeichen haben",
    "supplier.checkEmail": "Überprüfen Sie Ihre E-Mail zur Kontoaktivierung",
    "supplier.icoPlaceholder": "Firmennummer, z.B. 12345678 (optional)",
    "supplier.addressPlaceholder": "Firmensitz, z.B. Berlin",
    "supplier.fillCompany": "Bitte Firmennamen ausfüllen",
    "supplier.becomePartnerTitle": "Partner werden",
    "supplier.becomePartnerSubtitle": "Geben Sie Ihre Firmendaten ein.",
    "supplier.continueToPreferences": "Lieferant erstellen",
    "supplier.registrationDone": "Registrierung abgeschlossen! Willkommen im Lieferanten-Panel.",
    "supplier.settingsTitle": "Einstellungen",
    "supplier.tabCompany": "Unternehmen",
    "supplier.tabChatbot": "Chatbot",
    "supplier.tabPricing": "Preisgestaltung",
    "supplier.incompleteProfile": "Bitte ergänzen Sie Ihre Informationen in den Einstellungen, damit Ihr Chatbot richtig funktioniert.",
    "supplier.goToSettings": "Zu den Einstellungen",
    "supplier.passwordPlaceholder": "Passwort (mind. 8 Zeichen)",
    "supplier.dashboard": "Lieferanten-Admin",
    "supplier.logout": "Abmelden",
    "supplier.productsTitle": "Meine Produkte & Preise",
    "supplier.productsSubtitle": "Produkte hinzufügen und Preis pro m² festlegen",
    "supplier.addProduct": "Produkt hinzufügen",
    "supplier.editProduct": "Produkt bearbeiten",
    "supplier.newProduct": "Neues Produkt",
    "supplier.productName": "Produktname",
    "supplier.productNamePlaceholder": "z.B. EPS 100 Dämmung",
    "supplier.pricePerM2": "Preis pro m² (CZK)",
    "supplier.productDescription": "Beschreibung",
    "supplier.descriptionPlaceholder": "Kurze Produktbeschreibung...",
    "supplier.cancel": "Abbrechen",
    "supplier.save": "Speichern",
    "supplier.noProducts": "Noch keine Produkte",
    "supplier.addFirstProduct": "Klicken Sie auf 'Produkt hinzufügen'",
    "supplier.productAdded": "Produkt hinzugefügt",
    "supplier.productUpdated": "Produkt aktualisiert",
    "supplier.productDeleted": "Produkt gelöscht",
    "supplier.fillRequired": "Name und Preis ausfüllen",
    "supplier.inquiriesTab": "Anfragen",
    "supplier.productsTab": "Produkte",
    "supplier.noInquiries": "Noch keine Anfragen",
    "supplier.noInquiriesHint": "Neue Anfragen erscheinen automatisch",
    "supplier.alreadyPaid": "Angebot bereits gesendet",
    "supplier.paymentError": "Zahlungsfehler, bitte versuchen Sie es erneut",
    "supplier.loginRequired": "Zum Bezahlen müssen Sie sich anmelden",
    "supplier.submitQuoteTitle": "Ihr Preisangebot",
    "supplier.submitQuoteHint": "Geben Sie einen geschätzten Preis ohne MwSt. an. Nach Absendung und Zahlung von 15 CZK wird das Angebot dem Kunden zugestellt.",
    "supplier.quoteTotal": "Angebotspreis (netto)",
    "supplier.quoteNotes": "Anmerkung",
    "supplier.quoteNotesPlaceholder": "Was im Preis enthalten ist, Zeitrahmen...",
    "supplier.submitQuoteBtn": "Angebot senden & 15 CZK zahlen",
    "supplier.quoteSaved": "Angebot gespeichert!",
    "supplier.quoteAlreadySent": "Angebot bereits abgegeben",
    "supplier.fillQuotePrice": "Bitte Angebotspreis ausfüllen",
    "supplier.quoteSent": "Gesendet",
    "supplier.quoteSentConfirm": "Angebot wurde an den Kunden gesendet",
    "supplier.quoteSentSuccess": "Angebot erfolgreich gesendet!",
    "supplier.waitingCustomerApproval": "Warten auf Kundengenehmigung. Sobald Interesse besteht, werden Kontaktdaten angezeigt.",
    "supplier.customerApproved": "Kunde hat Kontaktfreigabe genehmigt",
    "supplier.contactVisibleNow": "Kundenkontaktdaten sind jetzt oben sichtbar.",
    "supplier.share": "Teilen",
    "supplier.linkCopied": "Link kopiert",
    "supplier.shareFailed": "Teilen fehlgeschlagen",
    "supplier.aboutQuery": "Erzählen Sie mir mehr über Ihr Unternehmen.",
    "supplier.aboutCompany": "Über das Unternehmen",
    "supplier.slugSaved": "Slug gespeichert",
    "supplier.slugTakenOrInvalid": "Slug ist bereits vergeben oder ungültig",
    "supplier.connectionError": "Verbindungsfehler",
    "supplier.paymentNotVerified": "Zahlung nicht verifiziert. Versuchen Sie, die Seite zu aktualisieren.",
    "supplier.paymentVerificationError": "Fehler bei der Zahlungsverifizierung.",
    "supplier.savingError": "Fehler beim Speichern",
    "supplier.dataSaved": "Daten gespeichert",
    "supplier.edit": "bearbeiten",
    "supplier.onboardingTitle": "Wie funktionieren Anfragen für Ihr Unternehmen?",
    "supplier.onboardingStep1": "Richten Sie Preisliste und Gebiete ein, in denen Sie Aufträge wünschen.",
    "supplier.onboardingStep2": "Darauf basierend zeigen wir Ihnen neue Anfragen.",
    "supplier.onboardingStep3": "Für ausgewählte Anfragen senden Sie ein Angebot, und der Kunde sieht Ihre Angebotsdetails.",
    "supplier.aiChatCalculator": "Ihr AI-Chat-Rechner",
    "supplier.freeTestVersion": "Kostenlos · Testversion",
    "supplier.checking": "Überprüfe...",
    "supplier.slugAvailableLabel": "✓ Verfügbar",
    "supplier.slugTakenLabel": "✗ Vergeben",
    "supplier.savingLabel": "Speichere...",
    "supplier.saveLabel": "Speichern",
    "supplier.cancel": "Abbrechen",
    "supplier.copyLink": "Link kopieren",
    "supplier.customDomainTag": "· eigene Domain",
    "supplier.customDomainLabel": "Eigene Domain: {domain}",
    "supplier.connectCustomDomain": "Eigene Domain verbinden",
    "supplier.enterDomainLabel": "Geben Sie Ihre Domain ein (z.B. {example}):",
    "supplier.connectButton": "Verbinden",
    "supplier.verifiedLabel": "Verifiziert ✓",
    "supplier.awaitingDns": "Warten auf DNS",
    "supplier.dnsStep1Title": "1. DNS-Einträge bei Ihrem Registrar einrichten",
    "supplier.dnsStep1Instruction": "Melden Sie sich bei Ihrer Domain-Verwaltung an und fügen Sie diese Einträge hinzu:",
    "supplier.mainDomainLabel": "Für die Hauptdomain (z.B. {example}):",
    "supplier.dnsTypeHeader": "Typ",
    "supplier.dnsNameHeader": "Name",
    "supplier.dnsValueHeader": "Wert",
    "supplier.subdomainLabel": "Für Subdomain (z.B. {example}):",
    "supplier.subdomainHint": "Bei der Subdomain ersetzen Sie {sub} durch Ihren Namen (z.B. {ex1}, {ex2}). CNAME-Eintrag wird für Subdomains empfohlen.",
    "supplier.dnsStep2Title": "2. Verifizierungseintrag hinzufügen",
    "supplier.dnsStep2Instruction": "Dieser Eintrag bestätigt, dass die Domain Ihnen gehört:",
    "supplier.dnsSettingsTitle": "Wo finde ich DNS-Einstellungen?",
    "supplier.dnsForpsi": "Administration → Domains → Ihre Domain → DNS-Einträge",
    "supplier.dnsActive24": "Domain-Detail → DNS-Verwaltung → Eintrag hinzufügen",
    "supplier.dnsWedos": "DNS-Verwaltung → Eintrag hinzufügen (Typ A oder TXT)",
    "supplier.dnsCloudflare": "DNS → Records → Add record",
    "supplier.dnsPropagationHint": "DNS-Änderungen werden innerhalb von 5 Minuten – 24 Stunden wirksam. Nach der Einrichtung klicken Sie 'DNS verifizieren'.",
    "supplier.verifyDns": "DNS verifizieren",
    "supplier.disconnectDomain": "Domain trennen",
    "supplier.chatbotSettingsLabel": "Chatbot- und Preislisteneinstellungen",
    "supplier.noPriceItemsWarning": "Ohne Preisliste wird Ihr Unternehmen nicht in Angeboten angezeigt.",
    "supplier.setPriceList": "Preisliste einrichten →",
    "supplier.readiness.setupCta": "Chatbot- & Preiseinstellungen",
    "supplier.readiness.companyInfo": "Unternehmensinformationen",
    "supplier.readiness.pricing": "Preisliste",
    "supplier.readiness.done": "Fertig",
    "supplier.readiness.missing": "Fehlt",
    "supplier.readiness.warningText": "Damit Ihr Chatbot öffentlich angezeigt wird, müssen Sie Unternehmensinformationen ausfüllen und eine Preisliste einrichten.",
    "supplier.readiness.readyText": "Ihr Chatbot ist bereit für die öffentliche Anzeige.",
    "supplier.readiness.fallbackTitle": "Chatbot noch nicht verfügbar",
    "supplier.readiness.fallbackDescription": "Dieser Chatbot wird gerade eingerichtet. Bitte versuchen Sie es später erneut.",
    "supplier.ownChatbot": "Eigener Chatbot",
    "supplier.marketplaceInquiries": "Anfragen von cenik.ai",
    "supplier.filterNew": "Neu",
    "supplier.filterQuoteSent": "Angebot gesendet",
    "supplier.filterHidden": "Versteckt",
    "supplier.chatbotBadge": "Chatbot",
    "supplier.marketplaceBadge": "cenik.ai",
    "supplier.requestMaterial": "Materialanfrage",
    "supplier.quoteAction": "Angebot erstellen",
    "supplier.restore": "Wiederherstellen",
    "supplier.hideAction": "Ausblenden",
    "supplier.marketplaceUpsellTitle": "Anfragen vom cenik.ai Marketplace",
    "supplier.marketplaceUpsellDescription": "Erhalten Sie Zugang zu Marketplace-Anfragen mit einem Premium-Plan",
    "supplier.premiumLabel": "Premium",
    "supplier.approved": "Genehmigt",
    "customer.allowContact": "Ich möchte, dass mich der Anbieter kontaktiert",
    "customer.approved": "Kontakt geteilt",
    "customer.contactShared": "Der Anbieter kann jetzt Ihre Kontaktdaten sehen",
    "customer.supplierContact": "Anbieterkontakt:",

    "filter.title": "Anfragefilter",
    "filter.workType": "Arbeitstyp",
    "filter.allTypes": "Alle Typen",
    "filter.location": "Baustelle",
    "filter.locationPlaceholder": "z.B. Berlin, München...",
    "filter.priceFrom": "Preis ab (€)",
    "filter.priceTo": "Preis bis (€)",
    "filter.deadline": "Ausführungsfrist",
    "filter.deadlinePlaceholder": "z.B. Mai 2026, sofort...",
    "filter.lock": "Filter sperren",
    "filter.apply": "Ergebnisse anzeigen",
    "filter.save": "Speichern & Benachrichtigungen erhalten",
    "filter.unlock": "Filter entsperren",
    "filter.locked": "Gesperrt",
    "filter.wholeCountry": "Ganze Tschechische Republik",
    "filter.reset": "Zurücksetzen",
    "filter.lockedHint": "Filter ist gesperrt – neue Anfragen, die Ihren Kriterien entsprechen, werden Ihnen als Benachrichtigung und per E-Mail zugestellt. Zum Ändern erst entsperren.",
    "filter.noResults": "Keine Anfragen entsprechen dem Filter",
    "filter.noResultsHint": "Versuchen Sie, den Filter anzupassen oder zurückzusetzen",
    "filter.searchCity": "Stadt oder Gemeinde suchen...",
    "filter.radius": "Umkreis",
    "filter.addWholeCZ": "+ Gesamte Tschechische Republik",
    "filter.wholeCZ": "Gesamte Tschechische Republik",
    "filter.noLocationsSet": "Keine Standorte festgelegt",
    "filter.searching": "Suche",
    "filter.locationAreas": "Einsatzgebiete",
    "filter.locationSaved": "Gebiete gespeichert",

    "register.checkEmail": "Überprüfen Sie Ihre E-Mail zur Aktivierung",
    "register.emailSent": "E-Mail gesendet!",
    "register.checkEmailDesc": "Überprüfen Sie Ihren Posteingang und klicken Sie auf den Bestätigungslink.",
    "register.goToLogin": "Zur Anmeldung",
    "profile.title": "Profileinstellungen",
    "profile.personalInfo": "Persönliche Daten",
    "profile.fullName": "Vollständiger Name",
    "profile.fullNamePlaceholder": "Max Mustermann",
    "profile.phone": "Telefon",
    "profile.phonePlaceholder": "+49 123 456 789",
    "profile.save": "Speichern",
    "profile.saved": "Profil gespeichert",
    "profile.supplierPanel": "Lieferanten-Panel",
    "profile.sitesTitle": "Projekte",
    "profile.sitesSubtitle": "Verwalten Sie Ihre Projekte und Baustellen",
    "profile.siteNamePlaceholder": "Projektname",
    "profile.siteAddressPlaceholder": "Projektadresse",
    "profile.addSite": "Projekt speichern",
    "profile.siteAdded": "Projekt hinzugefügt",
    "profile.siteDeleted": "Projekt entfernt",
    "profile.siteSaved": "Projekt gespeichert",
    "sidebar.settings": "Einstellungen",
    "sidebar.logout": "Abmelden",
    "sidebar.aboutUs": "Über uns",
    "sidebar.assignTo": "Projekt zuweisen",
    "sidebar.unassign": "Aus Projekt entfernen",
    "sidebar.deleteChat": "Chat löschen",
    "sidebar.chatDeleted": "Chat gelöscht",
    "sidebar.chatAssigned": "Chat zugewiesen",
    "sidebar.noProjectsYet": "Noch keine Projekte",
    "sidebar.noSearchResults": "Keine Ergebnisse",
    "sidebar.history": "Verlauf",
    "sidebar.inquiriesAndQuotes": "Anfragen & Angebote",
    "sidebar.noInquiriesYet": "Noch keine Anfragen",
    "sidebar.quote": "Angebot",
    "sidebar.quotes": "Angebote",
    "sidebar.backToChat": "Zurück zum Chat",
    "sidebar.aboutUsResponse": "## Über cenik.ai 👋\n\nWir sind hier, um **die Art und Weise zu ändern, wie Preisgestaltung und Anfragen im Bauwesen funktionieren**.\n\nWir glauben, dass es einfach sein sollte, einen fairen Preis für eine Fassade, Dämmung oder andere Bauarbeiten zu bekommen – sowohl für **Auftraggeber** als auch für **Handwerker**.\n\nHinter dem Projekt steht **Adam Černohous, IČO: 76535398**, mit Sitz Třebovská 65, Ústí nad Orlicí 56203.\n\n📧 Kontakt: **info@cenik.ai**",
    "dashboard.newProject": "Neues Projekt",
    "notifications.title": "Benachrichtigungen",
    "notifications.empty": "Keine Benachrichtigungen",
    "notifications.newInquiry": "Neue Anfrage",
    "notifications.loginPrompt": "Hier werden Ihre Angebote von Lieferanten angezeigt. Melden Sie sich an, um sie zu sehen.",

    // Customer dashboard
    "customerDashboard.title": "Meine Anfragen",
    "customerDashboard.newInquiry": "Neue Anfrage",
    "customerDashboard.onboardingTitle": "Wie funktioniert cenik.ai für Sie?",
    "customerDashboard.onboardingStep1": "Fragen Sie im Chat nach einer Preisschätzung für Dämmung oder Fassade.",
    "customerDashboard.onboardingStep2": "Der Chat erstellt eine Anfrage aus Ihren Antworten.",
    "customerDashboard.onboardingStep3": "Wir senden die Anfrage an Firmen in Ihrer Nähe und deren Angebote erscheinen hier.",
    "customerDashboard.emptyTitle": "Sie haben noch keine Anfragen",
    "customerDashboard.emptyDesc": "Öffnen Sie den Chat auf der Hauptseite und lassen Sie sich die erste Schätzung berechnen.",
    "customerDashboard.statusOpen": "Offen",
    "customerDashboard.statusInProgress": "Kommunikation läuft",
    "customerDashboard.statusClosed": "Geschlossen",
    "customerDashboard.statusCompleted": "Abgeschlossen",
    "customerDashboard.quote": "Angebot",
    "customerDashboard.quotes": "Angebote",
    "customerDashboard.quotesMany": "Angebote",

    // Supplier chat
    "supplierChat.newChat": "Neuer Chat",
    "supplierChat.inquiry": "Anfrage",
    "supplierChat.message": "Nachricht",
    "supplierChat.upsellTitle": "Möchten Sie einen eigenen Chatbot?",
    "supplierChat.upsellDesc": "Holen Sie sich einen KI-Assistenten für Ihre Firma mit eigenen Preisen und Branding.",
    "supplierChat.upsellLink": "Mehr erfahren →",
    "supplierChat.aboutUs": "Über uns",
    "supplierChat.msgSavedNotifyFailed": "Nachricht gespeichert, aber Benachrichtigung an die Firma konnte nicht gesendet werden.",
    "supplierChat.msgDeliveryFailed": "Nachricht konnte nicht an die Firma zugestellt werden. Bitte versuchen Sie es erneut.",
    "supplierChat.inquiryDeliveryFailed": "Anfrage konnte nicht an die Firma zugestellt werden. Bitte versuchen Sie es erneut.",
    "supplierChat.error": "Fehler: {error}",
    "supplierChat.chatNotFound": "AI-Chat nicht gefunden",
    "supplierChat.domainNotLinked": "Diese Domain ist noch nicht mit einer Preisliste verknüpft.",
    "supplierChat.slugNotFound": "existiert nicht.",
    "supplierChat.backToHome": "← Zurück zur Hauptseite",
    "supplierChat.askAboutCompany": "Erzählen Sie mir mehr über Ihre Firma.",
    "supplierChat.inputPlaceholder": "Schreiben Sie Ihre Frage oder Parameter…",
    "supplierChat.aboutCompany": "Über {name}",
    "supplierChat.specialization": "Spezialisierung",
    "supplierChat.serviceArea": "Servicegebiet",
    "supplierChat.aiAssistantHelp": "Wobei kann Ihnen der KI-Assistent helfen?",
    "supplierChat.aiHelp1": "Geschätzte Kosten für Bauarbeiten",
    "supplierChat.aiHelp2": "Verfügbarkeit und Arten von Materialien",
    "supplierChat.aiHelp3": "Antworten auf Fachfragen",
    "supplierChat.aiHelp4": "Vorläufige Kalkulation basierend auf Ihren Parametern",
    "supplierChat.faq": "Häufige Fragen",
    "supplierChat.contact": "Kontakt",

    // Sidebar extras
    "sidebar.currentChat": "Aktueller Chat",
    "sidebar.messageCount": "Nachrichten",

    // Supplier settings
    "supplierSettings.companyName": "Firmenname *",
    "supplierSettings.ico": "Handelsregisternummer",
    "supplierSettings.contactEmail": "Kontakt-E-Mail",
    "supplierSettings.contactPhone": "Kontakttelefon",
    "supplierSettings.region": "Region",
    "supplierSettings.regionPlaceholder": "z.B. Prag, Südmähren",
    "supplierSettings.description": "Firmenbeschreibung",
    "supplierSettings.descriptionPlaceholder": "Kurze Beschreibung Ihrer Firma...",
    "supplierSettings.saving": "Speichern...",
    "supplierSettings.saveCompany": "Firmendaten speichern",
    "supplierSettings.savedCompany": "Firmendaten gespeichert",
    "supplierSettings.saveError": "Fehler beim Speichern",
    "supplierSettings.saveFailed": "Speichern fehlgeschlagen",
    "supplierSettings.chatbotDesc": "Passen Sie an, wie sich Ihr Chatbot den Kunden vorstellt. In der kostenlosen Version teilt der Bot keine Kontaktdaten mit.",
    "supplierSettings.botName": "Chatbot-Name",
    "supplierSettings.botNameHint": "Wenn nicht ausgefüllt, verwendet der Bot Ihren Firmennamen.",
    "supplierSettings.botIntro": "Bot-Vorstellung",
    "supplierSettings.botIntroPlaceholder": "Ich bin der KI-Assistent der Firma XY, ich helfe Ihnen bei Fragen zu Fassaden und Dämmung.",
    "supplierSettings.aboutCompany": "Über die Firma",
    "supplierSettings.aboutCompanyPlaceholder": "Kurze Info über Firma, Spezialisierung, Erfahrung, Servicegebiet...",
    "supplierSettings.faq": "FAQ – häufig gestellte Fragen",
    "supplierSettings.faqPlaceholder": "Frage: Welche Materialien verwenden Sie?\nAntwort: EPS, Mineralwolle, WDVS-Systeme...\n\nFrage: Was ist Ihr Preis pro m²?\nAntwort: Der Preis hängt vom Umfang der Arbeiten ab, normalerweise...",
    "supplierSettings.saveBot": "Einstellungen speichern",
    "supplierSettings.savedBot": "Chatbot-Einstellungen gespeichert.",
    "supplierSettings.saveBotFailed": "Einstellungen konnten nicht gespeichert werden.",
    "supplierSettings.tabServices": "Dienste & Preise",
    "supplierSettings.tabPublicPage": "Öffentliche Seite",
    "supplierSettings.publicPageDesc": "Legen Sie fest, wie Ihre öffentliche KI-Seite in Suchmaschinen und sozialen Medien angezeigt wird.",
    "supplierSettings.tagline": "Kurzer Slogan",
    "supplierSettings.taglinePlaceholder": "z.B. Fassadenarbeiten schlüsselfertig in Prag",
    "supplierSettings.taglineHint": "Wird unter dem Firmennamen auf der öffentlichen Seite angezeigt.",
    "supplierSettings.businessType": "Branche / Dienstleistungstyp",
    "supplierSettings.businessTypePlaceholder": "Fassadenarbeiten",
    "supplierSettings.serviceArea": "Servicegebiet",
    "supplierSettings.serviceAreaPlaceholder": "Prag und Mittelböhmen",
    "supplierSettings.seoTitle": "SEO-Seitentitel",
    "supplierSettings.seoTitleHint": "Wird im Browser-Tab und in Suchergebnissen angezeigt.",
    "supplierSettings.seoDesc": "SEO-Seitenbeschreibung",
    "supplierSettings.seoDescHint": "Wird unter dem Titel in Suchergebnissen angezeigt.",
    "supplierSettings.ogImage": "OG-Bild-URL (optional)",
    "supplierSettings.ogImageHint": "Bild beim Teilen in sozialen Medien. Idealerweise 1200×630 px.",
    "supplierSettings.searchPreview": "Suchmaschinen-Vorschau",
    "supplierSettings.savePublicPage": "Seiteneinstellungen speichern",
    "supplierSettings.savedPublicPage": "Öffentliche Seiteneinstellungen gespeichert.",
    "supplierSettings.servicesHeading": "Welche Dienste bieten Sie an?",
    "supplierSettings.servicesDesc": "Wählen Sie die Dienste, die Ihre Firma anbietet. Für jeden können Sie Beschreibung und Preise hinzufügen.",
    "supplierSettings.addCustomService": "Eigenen Dienst hinzufügen…",
    "supplierSettings.addButton": "Hinzufügen",
    "supplierSettings.serviceAlreadyAdded": "Dieser Dienst wurde bereits hinzugefügt",
    "supplierSettings.serviceAdded": 'Dienst „{name}" hinzugefügt',
    "supplierSettings.selectServiceFirst": "Wählen Sie mindestens einen Dienst oben, dann fügen Sie Beschreibung und Preise hinzu.",
    "supplierSettings.customerMessages": "Kundennachrichten",
    "supplierSettings.translate": "Übersetzen",
    "supplierSettings.showOriginal": "Original anzeigen",

    // Admin
    "admin.title": "Admin Panel",
    "admin.loginPrompt": "Melden Sie sich mit einem Admin-Konto an",
    "admin.loginButton": "Anmelden",
    "admin.email": "E-Mail",
    "admin.password": "Passwort",
    "admin.googleLogin": "Mit Google anmelden",
    "admin.orPassword": "oder mit Passwort",
    "admin.noPermission": "Dieses Konto hat keine Admin-Berechtigungen",
    "admin.loginFailed": "Anmeldung fehlgeschlagen",
    "admin.googleLoginFailed": "Google-Anmeldung fehlgeschlagen",
    "admin.sessionExpired": "Sitzung abgelaufen, bitte erneut anmelden",
    "admin.registrationFailed": "Registrierung fehlgeschlagen",
    "admin.accountCreated": "Admin-Konto erstellt und angemeldet",
    "admin.loginExpired": "Anmeldung abgelaufen, bitte erneut anmelden",
    "admin.tab.chats": "Chats",
    "admin.tab.inquiries": "Anfragen",
    "admin.tab.users": "Benutzer",
    "admin.tab.prices": "Preise",
    "admin.tab.services": "Dienste",
    "admin.tab.subscriptions": "Abonnements",
    "admin.tab.feedback": "Feedback",
    "admin.tab.knowledge": "Wissen",
    "admin.toast.saved": "Gespeichert",
    "admin.toast.deleted": "Gelöscht",
    "admin.toast.saveFailed": "Speichern fehlgeschlagen",
    "admin.toast.deleteFailed": "Löschen fehlgeschlagen",
    "admin.toast.loadFailed": "Daten konnten nicht geladen werden",
    "admin.toast.statusUpdated": "Status aktualisiert",
    "admin.toast.activated": "Aktiviert",
    "admin.toast.deactivated": "Deaktiviert",
    "admin.toast.correctionSaved": "Korrektur gespeichert",
    "admin.toast.correctionSaveFailed": "Korrektur speichern fehlgeschlagen",
    "admin.toast.feedbackIgnored": "Feedback verworfen",
    "admin.toast.feedbackIgnoreFailed": "Feedback verwerfen fehlgeschlagen",
    "admin.toast.knowledgeAdded": "Wissen hinzugefügt",
    "admin.toast.knowledgeSaved": "Wissen gespeichert",
    "admin.toast.aiCategorized": "AI hat Kategorie und Name vorgeschlagen",
    "admin.toast.serviceCreated": "Dienst erstellt",
    "admin.toast.createServiceFailed": "Dienst konnte nicht erstellt werden",
    "admin.toast.serviceActivated": "Dienst aktiviert",
    "admin.toast.serviceRejected": "Dienst abgelehnt",
    "admin.toast.movedToQueue": "In Warteschlange verschoben",
    "admin.toast.movedToCandidate": "Zum Kandidaten verschoben",
    "admin.toast.serviceAssigned": "Dienst zugewiesen",
    "admin.toast.newServiceProposed": "Neuer Dienst vorgeschlagen und zugewiesen",
    "admin.toast.aiNoMatch": "AI hat keinen passenden Dienst gefunden",
    "admin.toast.detectionFixed": "Erkennung korrigiert + Lerndaten erstellt",
    "admin.toast.detectionFixFailed": "Erkennung konnte nicht korrigiert werden",
    "admin.toast.detectionRejected": "Erkennung abgelehnt",
    "admin.toast.detectionRejectFailed": "Ablehnung fehlgeschlagen",
    "admin.toast.overrideSaved": "Override gespeichert",
    "admin.toast.overrideSaveFailed": "Override speichern fehlgeschlagen",
    "admin.toast.planChangeFailed": "Plan konnte nicht geändert werden",
    "admin.toast.featureToggleFailed": "Feature konnte nicht umgeschaltet werden",
    "admin.toast.fillTopicContent": "Thema und Inhalt ausfüllen",
    "admin.toast.fillNameSlug": "Name und Slug ausfüllen",
    "admin.toast.invalidSlug": "Slug darf nur Kleinbuchstaben, Zahlen und Bindestriche enthalten",
    "admin.toast.learningAdded": "Lerndaten hinzugefügt – AI wird sie sofort verwenden",
    "admin.toast.addFailed": "Hinzufügen fehlgeschlagen",
    "admin.toast.inquiryDeleted": "Anfrage gelöscht",
    "admin.toast.writeCorrectedAnswer": "Schreiben Sie die korrigierte Antwort",
    "admin.toast.serviceSaved": "Dienst gespeichert",
    "admin.toast.generateFailed": "Generierung fehlgeschlagen",
    "admin.toast.formApproved": "Formular genehmigt — Lieferanten werden es sehen",
    "admin.toast.approveFailed": "Genehmigung fehlgeschlagen",
    "admin.toast.formToDraft": "Formular in Bearbeitung gesetzt",
    "admin.toast.operationFailed": "Fehlgeschlagen",
    "admin.toast.keywordApproved": "Keyword genehmigt",
    "admin.toast.keywordRejected": "Keyword abgelehnt",
    "admin.toast.keywordAdded": "Keyword hinzugefügt",
    "admin.toast.keywordDeleted": "Keyword gelöscht",
    "admin.status.active": "Aktiv",
    "admin.status.inactive": "Inaktiv",
    "admin.status.candidate": "Kandidat",
    "admin.status.queued": "In Warteschlange",
    "admin.status.rejected": "Abgelehnt",
    "admin.status.positive": "Positiv",
    "admin.status.negative": "Negativ",
    "admin.empty.inquiries": "Keine Anfragen",
    "admin.empty.suppliers": "Keine Lieferanten",
    "admin.empty.customers": "Keine Kunden",
    "admin.empty.feedback": "Noch kein Benutzer-Feedback",
    "admin.confirm.deleteInquiry": "Diese Anfrage wirklich löschen?",
    "admin.confirm.deleteLearning": "Dieses Lerndaten wirklich löschen?",
    "admin.btn.approve": "Genehmigen",
    "admin.btn.reject": "Ablehnen",
    "admin.btn.addItem": "Element hinzufügen",
    "admin.btn.saveDraft": "Entwurf speichern",
    "admin.btn.approveAndDeploy": "Genehmigen und bereitstellen",
    "admin.btn.regenerate": "Neu generieren",
    "admin.btn.editForm": "Formular bearbeiten",
    "admin.btn.collapse": "Einklappen",
    "admin.btn.showAll": "Alle anzeigen",
    "admin.btn.ignore": "Verwerfen",
    "admin.btn.save": "Speichern",
    "admin.btn.cancel": "Abbrechen",
    "admin.btn.addKnowledge": "Wissen hinzufügen für",
    "admin.btn.restoreCandidate": "Als Kandidat wiederherstellen",
    "admin.btn.removeOverride": "Override entfernen",
    "admin.btn.disable": "Deaktivieren",
    "admin.btn.enable": "Aktivieren",
    "admin.section.quoteForm": "Angebotsformular für Lieferanten",
    "admin.section.noQuoteForm": "Noch kein Angebotsformular. AI wird Budgetposten für diesen Dienst vorschlagen.",
    "admin.section.costItems": "Kostenpositionen",
    "admin.section.formPreview": "Formularvorschau für Lieferanten",
    "admin.section.serviceStatus": "Dienststatus",
    "admin.section.feedbackHelp": "AI-Antwortbewertungen. Negatives Feedback mit Korrektur wird sofort als AI-Lerndaten angewandt. Verdächtige Bewertungen verwerfen.",
    "admin.section.totalRatings": "Bewertungen gesamt",
    "admin.section.aiKnowledge": "AI nutzt dieses Wissen bei der Beantwortung von Kundenanfragen und der Erstellung von Angeboten für diesen Dienst.",
    "admin.placeholder.knowledgeName": "Wissensname",
    "admin.placeholder.knowledgeContent": "Text über Dämmung, Preise, Materialien einfügen... AI schlägt Namen und Kategorie vor.",
    "admin.placeholder.learningContent": "Wissensinhalt — was die AI wissen und wie sie sich verhalten soll...",
    "admin.placeholder.itemName": "Elementname",
    "admin.placeholder.keyword": "Neues Schlüsselwort oder Phrase...",
    "admin.placeholder.serviceDesc": "Kurze Dienstbeschreibung",
    "admin.placeholder.priceName": "z.B. EPS-Preise",
    "admin.placeholder.newServiceName": "Name des neuen Dienstes:",
    "admin.label.totalPrice": "Gesamtpreis",
    "admin.label.currency": "CZK",
    "admin.label.records": "Einträge",
    "admin.label.offer": "Angebot",
    "admin.label.offers": "Angebote",
    "admin.label.offersMany": "Angebote",
    "admin.label.inquirySent": "Anfrage ✓",
    "admin.label.inquiryNotSent": "Keine Anfrage",
    "admin.label.formDraft": "📝 Entwurf",
    "admin.label.formApproved": "✅ Formular",
    "admin.label.user": "👤 Benutzer",
    "admin.label.ai": "🤖 AI",
    "admin.label.advancedCalc": "Erweiterte Kalkulation",
    "admin.label.weight": "Gewicht",
    "admin.label.serviceNotFound": "Dienst nicht gefunden",
    "admin.label.generating": "Generiere…",
    "admin.label.generateForm": "🤖 Angebotsformular generieren",
    "admin.label.analyzing": "Analysiere...",
    "admin.label.aiCategorize": "🤖 AI kategorisieren",
    "admin.label.newServiceProposal": "Neuer Dienstvorschlag",
    "admin.label.uncertainMatch": "Unsichere Zuordnung",
    "admin.label.match": "Zuordnung",
    "admin.label.new": "Neu",
    "admin.label.uncertain": "Unsicher",
    "admin.filter.all": "Alle",
    "admin.filter.sent": "Anfrage gesendet",
    "admin.filter.notSent": "Nicht gesendet",
    "admin.module.walls": "Wände",
    "admin.module.material": "Materialanfrage",
    "admin.module.quoteCalc": "Angebotsberechnung",
    "admin.module.generalInquiry": "Allgemeine Anfrage",
    "admin.module.simpleQuote": "Einfaches Angebot",
    "admin.material.epsWhite": "EPS weiß",
    "admin.material.epsGrey": "EPS grau",
    "admin.material.mineralWool": "Mineralwolle",
    "admin.material.ventilatedFacade": "Hinterlüftete Fassade",
    "admin.section.basicInfo": "Grundinfo",
    "admin.section.keywordsDetection": "Schlüsselwörter (Erkennung)",
    "admin.section.keywordsHelp": "Genehmigte Schlüsselwörter werden für die hybride Diensterkennung verwendet. Vorgeschlagene warten auf Überprüfung.",
    "admin.section.modules": "Module",
    "admin.section.knowledgeFor": "AI-Wissen für",
    "admin.label.name": "Name",
    "admin.label.slugUrl": "Slug (URL)",
    "admin.label.keywordsComma": "Schlüsselwörter (kommagetrennt, für Keyword-Matching)",
    "admin.label.descOptional": "Beschreibung (optional)",
    "admin.label.approved": "Genehmigt",
    "admin.label.suggested": "Vorgeschlagen",
    "admin.label.rejectedKw": "Abgelehnt",
    "admin.label.profit": "Gewinn",
    "admin.label.vat": "MwSt",
    "admin.label.unitLabel": "Einheit:",
    "admin.label.vatRate": "MwSt-Satz (12 % / 21 %)",
    "admin.label.query1": "Anfrage",
    "admin.label.query2to4": "Anfragen",
    "admin.label.query5plus": "Anfragen",
    "admin.label.record1": "Eintrag",
    "admin.label.record2to4": "Einträge",
    "admin.label.learningActive": "✓ Aktiv",
    "admin.label.learningInactive": "○ Inaktiv",
    "admin.label.formApprovedBadge": "✅ Genehmigt",
    "admin.label.formDraftBadge": "📝 Entwurf",
    "admin.label.facadeNote": "ℹ️ Fassadenarbeiten haben ein eingebautes Formular (Material, Gerüst, Arbeit, Transport). Wenn Sie ein neues generieren, ersetzt es das eingebaute.",
    "admin.label.fact": "📚 Fakt",
    "admin.label.correction": "🔧 Korrektur",
    "admin.label.tip": "💡 Tipp",
    "admin.btn.refresh": "Aktualisieren",
    "admin.btn.backToOverview": "← Zurück zur Übersicht",
    "admin.btn.add": "Hinzufügen",
    "admin.btn.activate": "Aktivieren",
    "admin.btn.toQueue": "In die Warteschlange",
    "admin.placeholder.keywordsExample": "Fassade, Dämmung, EPS, Polystyrol",
    "admin.placeholder.learningTopic": "Thema (z.B. SDK-Trennwandpreis)",
    "admin.toast.aiSuggestedForm": "AI hat ein Formular vorgeschlagen — überprüfen und genehmigen",
    "admin.toast.error": "Fehler",
    "admin.module.wallsDesc": "Kunde gibt Wände mit Maßen und Öffnungen ein. Lieferant sieht detaillierte Flächenaufschlüsselung.",
    "admin.module.materialDesc": "Lieferant fragt Material bei Großhändlern direkt aus der Anfrage an.",
    "admin.module.quoteCalcDesc": "Positionsbudget mit Preisen pro m². Generatives AI-Angebot für den Kunden.",
    "admin.module.generalInquiryDesc": "AI-generierte Anfragebeschreibung aus dem Chat.",
    "admin.module.simpleQuoteDesc": "Lieferant gibt Gesamtpreis + Anmerkungen ein.",
  },
};

interface I18nContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

export const DEFAULT_LANG: Lang = "cs";
export const LANG_STORAGE_KEY = "lang";

export function isLang(value: unknown): value is Lang {
  return typeof value === "string" && LANGUAGES.some(({ code }) => code === value);
}

export function readStoredLang(
  storage: Pick<Storage, "getItem"> | null = typeof localStorage === "undefined" ? null : localStorage,
): Lang {
  const saved = storage?.getItem(LANG_STORAGE_KEY);
  return isLang(saved) ? saved : DEFAULT_LANG;
}

function writeStoredLang(
  nextLang: Lang,
  storage: Pick<Storage, "setItem"> | null = typeof localStorage === "undefined" ? null : localStorage,
) {
  storage?.setItem(LANG_STORAGE_KEY, nextLang);
}

const I18nContext = createContext<I18nContextType>({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key) => key,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => readStoredLang());
  const [accountUserId, setAccountUserId] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const hasSupabaseEnv = Boolean(
      import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    );
    if (!hasSupabaseEnv) return;

    let isActive = true;
    let unsubscribe: (() => void) | null = null;

    const initAuthLanguageSync = async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        if (!isActive) return;

        const syncFromProfile = async (userId: string) => {
          const { data, error } = await supabase
            .from("profiles")
            .select("preferred_language")
            .eq("user_id", userId)
            .maybeSingle();

          if (!isActive) return;

          if (error) {
            console.error("Failed to load preferred language:", error);
            return;
          }

          if (isLang(data?.preferred_language)) {
            setLangState(data.preferred_language);
            writeStoredLang(data.preferred_language);
          }
        };

        const { data: { session } } = await supabase.auth.getSession();
        if (!isActive) return;

        const userId = session?.user?.id ?? null;
        setAccountUserId(userId);

        if (userId) {
          await syncFromProfile(userId);
        } else {
          setLangState(readStoredLang());
        }

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
          const nextUserId = nextSession?.user?.id ?? null;
          setAccountUserId(nextUserId);

          if (!nextUserId) {
            setLangState(readStoredLang());
            return;
          }

          void syncFromProfile(nextUserId);
        });

        if (!isActive) {
          subscription.unsubscribe();
          return;
        }

        unsubscribe = () => subscription.unsubscribe();
      } catch (error) {
        if (isActive) {
          console.error("Failed to initialize language sync:", error);
        }
      }
    };

    void initAuthLanguageSync();

    return () => {
      isActive = false;
      unsubscribe?.();
    };
  }, []);

  const setLang = useCallback((nextLang: Lang) => {
    if (!isLang(nextLang)) return;

    setLangState(nextLang);
    writeStoredLang(nextLang);

    const hasSupabaseEnv = Boolean(
      import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    );
    if (!hasSupabaseEnv) return;

    void (async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");

        let userId = accountUserId;
        if (!userId) {
          const { data: { session } } = await supabase.auth.getSession();
          userId = session?.user?.id ?? null;
          if (userId) {
            setAccountUserId(userId);
          }
        }

        if (!userId) return;

        const { error } = await supabase
          .from("profiles")
          .upsert(
            {
              user_id: userId,
              preferred_language: nextLang,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "user_id" },
          );

        if (error) {
          console.error("Failed to save preferred language:", error);
        }
      } catch (error) {
        console.error("Failed to persist preferred language:", error);
      }
    })();
  }, [accountUserId]);

  const t = useCallback(
    (key: string) => translations[lang]?.[key] ?? translations[DEFAULT_LANG][key] ?? key,
    [lang]
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
